import { createContext } from "react";
import nodata from "@images/nodata.png";
import {
    IMainContext,
    ICampaignCreationContext,
    ILinks,
    BidLimit,
    ParentModule,
    SubModule,
    Actions,
    Modules,
    IGridUpdateConfigParams,
    ChannelName,
    ChannelConfig,
    Features,
    TBidCellValue,
    TRuleOptionConfig,
    ProgramType,
    TagsMap,
    TBudgetUpdate,
    TSubscribeState,
    TRemoveEntity,
    GoalDataLevels,
    GoalStatus,
    GoalStatusConfig,
    ColorConfig,
    IFixedReviewOptions,
    Platforms,
    RecordType,
    TReviewDrawerTitleState,
    IDeepDiveModalConfig,
    TPaModuleLoadingState,
    TCerbosResources,
    ProgramTypeText,
    PricingCharts,
    ProductDeepdiveModules,
    MyProductTrendsTypes,
    SummaryWidgetConfigKeys,
    TGeneratePlanConfig,
    TMonthOption,
    TPermissionConfig,
    TUsersFormInitialState,
    User,
    CustomGroupingFunctionType,
    AggregationField,
    VisibilityFilterOptionKeys,
    PageRankValues,
    KeywordTypeValues,
    SOVTypeValues,
    Channels,
    AvailabilityCharts,
    EAccountType,
    ICategoryOverviewConfig,
    CATEGORY_OVERVIEW_MS_OFFTAKE_RADIO,
    CATEGORY_OVERVIEW_SKU_FILTERS,
    CATEGORY_OVERVIEW_MS_TRENDS_OVERTIME_RADIO,
    TManageReportAccessInitialState,
    ReportsAccessTypes,
    GlobalTagRecordType,
    ITagMappingStats,
    CommonWidgetMetrics,
    IFormattedSubCategoryPerformance,
    IFormattedCategoryOverviewSummaryWidgetData,
    AvailabilityDrawerRadioOptions,
    PlacementKey,
    IDatePickerInputValidationInitialState,
    TCustomDatePickerInputState,
    TSubCatDrillDownConfig,
    CA_DD_MODULES,
    CatAnalyticsSubCatDrillDownContentViews,
    WidgetType,
    CubeQueryType,
    RevstackCASectionIds,
    PERIOD_OPTIONS,
    Measure,
    TRevstackFiltersSearchState,
    DDColGroupKeys,
    RevstackFrequencySlicerOptions,
    RevstackFilterKey,
    PlacementTypeNames,
    CAMPAIGN_SYNC_STATE,
    BCCSelectionLevel,
    BCCSelectionMode,
    MappingSyncStatus,
} from "./common-types";
import {
    setAdgroupsData,
    setBudgetRecommendationData,
    setCampaignsData,
    setKeywordsData,
    setPlacementsData,
    setPlacementRecommendationData,
    setPortfoliosData,
    setProductAnalyticsData,
    setProductMappingData,
    setProductsData,
} from "@/features/gridDataSlice";
import {
    PopOverBidCellRenderer,
    UpdateBidOrBudgetCellRenderer,
} from "@/components/cell-renderers/AllCellRenderers";
import { Rule } from "antd/lib/form";

export const mainContext = createContext<IMainContext | null>(null);
export const campaignCreationContext =
    createContext<ICampaignCreationContext | null>(null);
export const activeFilters = {};
export const currentDateFilter = {};
export const selectDateRange = "Last 7 Days";
export const CUSTOM_DATE_RANGE_LABEL = "Custom Range";
export const DEFAULT_DECIMAL_PLACE = 1;
export const MINIMUM_FILTERS_TO_DISPLAY = 4;
export const NUM_DAYS_SELECTABLE = 45;
export const REVIEW_FILTER_MAX_LIMIT = 10;
export const MIN_BULK_CAMPAIGN_BUGET_LIMIT = 500;

export const EXTERNAL_FILTERS = [
    "percentIncreasedBy",
    "percentDecreasedBy",
    "inSet",
    "set",
];

export const CHANNEL_TARGET_REGEX: Record<number, RegExp> = {
    1: new RegExp(/\b(b0[a-z0-9]{8})\b/, "i"),
};

export const NEGATIVE_PARAMETERS = [
    "acos",
    "cpa",
    "cpc",
    "rank",
    "bsr",
    "impression_share_rank",
    "placement_product_page_acos",
    "placement_top_acos",
    "placement_rest_acos",
    "total_acos",
];

export enum SessionStorageKey {
    ActiveFilters = "activeFilters",
}

export const TREND_ATTRIBUTES = [
    { name: "Sales", key: "sale" },
    { name: "Spends", key: "spend" },
    { name: "Clicks", key: "click" },
    { name: "Impressions", key: "impression" },
    { name: "Orders", key: "orders" },
    { name: "CTR", key: "ctr" },
    { name: "CR", key: "cr" },
    { name: "CPC", key: "cpc" },
    { name: "CPA", key: "cpa" },
    { name: "ACoS", key: "acos" },
    { name: "RoAS", key: "roas" },
    { name: "AOV", key: "aov" },
];

type TSourceConfiguration = {
    type: string;
    displayCount: boolean;
    method?: string;
    hasSupportingRequest: boolean;
};

export const DataSourceConfiguration: Record<string, TSourceConfiguration> = {
    "campaign-budget-recommendation": {
        type: "recommendations/campaigns/budget",
        displayCount: false,
        hasSupportingRequest: true,
    },
    "campaign-placement-recommendation": {
        type: "recommendations/campaigns/placement",
        displayCount: false,
        hasSupportingRequest: true,
    },
    "reviniti-recommendation": {
        type: "recommendations/keywords/internal",
        displayCount: false,
        method: "get",
        hasSupportingRequest: false,
    },
    "suggested-negative-keywords": {
        type: "searchTerms/list",
        displayCount: false,
        hasSupportingRequest: false,
    },
    "existing-negative-keywords": {
        type: "keywords/negative/list",
        displayCount: false,
        hasSupportingRequest: true,
    },
    "keyword-manual-recommendation": {
        type: "recommendations/keywords/manual",
        displayCount: false,
        method: "get",
        hasSupportingRequest: false,
    },
    "keyword-auto-recommendation": {
        type: "recommendations/keywords/auto",
        displayCount: false,
        method: "get",
        hasSupportingRequest: false,
    },
    "recommendations-wasted-spends": {
        type: "keywords/list",
        displayCount: false,
        hasSupportingRequest: false,
    },
    portfolios: {
        type: "portfolios/list",
        displayCount: true,
        hasSupportingRequest: true,
    },
    keywords: {
        type: "keywords/list",
        displayCount: true,
        hasSupportingRequest: true,
    },
    placements: {
        type: "placements/list",
        displayCount: true,
        hasSupportingRequest: true,
    },
    adgroups: {
        type: "adgroups/list",
        displayCount: true,
        hasSupportingRequest: true,
    },
    campaigns: {
        type: "campaigns/list",
        displayCount: true,
        hasSupportingRequest: true,
    },
    products: {
        type: "products/list",
        displayCount: true,
        hasSupportingRequest: true,
    },
    "keyword-analytics": {
        type: "analytics/keywords/list",
        displayCount: false,
        hasSupportingRequest: false,
    },
    "product-analytics": {
        type: "analytics/products/list",
        displayCount: false,
        hasSupportingRequest: true,
    },
    "product-analytics-adgroups": {
        type: "analytics/products/adgroups/list",
        displayCount: false,
        hasSupportingRequest: true,
    },
    "product-analytics-keywords": {
        type: "analytics/products/review",
        displayCount: false,
        hasSupportingRequest: true,
    },
    history: {
        type: "history",
        displayCount: false,
        method: "post",
        hasSupportingRequest: false,
    },
    alerts: {
        type: "alerts",
        displayCount: false,
        method: "get",
        hasSupportingRequest: false,
    },
    reports: {
        type: "reports/custom",
        displayCount: false,
        method: "get",
        hasSupportingRequest: false,
    },
    "change-logs": {
        type: "rules/logs",
        displayCount: false,
        method: "post",
        hasSupportingRequest: false,
    },
    "client-requests": {
        type: "history/requests",
        displayCount: false,
        method: "get",
        hasSupportingRequest: false,
    },
    "smart-keywords": {
        type: "keywords/smart/list",
        displayCount: true,
        hasSupportingRequest: false,
    },
    "search-term-campaigns": {
        type: "analytics/keywords/campaigns",
        displayCount: true,
        hasSupportingRequest: false,
    },
    "tag-type-view": {
        method: "post",
        type: "clients/tag-type-aggregate",
        displayCount: false,
        hasSupportingRequest: false,
    },
};

export const KEY_TO_JOIN_BASE_AND_SUPPORTING_DATA: Record<
    string,
    string[] | string
> = {
    products: ["sys_adgroup_id", "sys_campaign_id", "asin_id", "program_type"],
    keywords: ["sys_ams_keyword_id"],
    placements: ["sys_ams_keyword_id"],
    campaigns: ["sys_campaign_id", "program_type"],
    adgroups: ["sys_adgroup_id", "sys_campaign_id", "program_type"],
    portfolios: ["portfolio_id"],
    "keywords/negative": ["sys_neg_kw_id"],
    "recommendations/campaigns/budget": ["sys_campaign_id"],
    "recommendations/campaigns/placement": ["sys_campaign_id"],
    "analytics/products": ["asin"],
    "analytics/products/review": ["sys_ams_keyword_id"],
    "analytics/products/adgroups": [
        "sys_adgroup_id",
        "sys_campaign_id",
        "asin",
        "program_type",
    ],
    others: "",
};

export const COLUMNS_TO_CALCULATE = [
    "ctr",
    "cr",
    "cpc",
    "cpa",
    "acos",
    "roas",
    "placement_product_page_acos",
    "placement_product_page_ctr",
    "placement_product_page_cr",
    "placement_product_page_cpc",
    "placement_product_page_cpa",
    "placement_product_page_roas",
    "placement_top_acos",
    "placement_top_ctr",
    "placement_top_cr",
    "placement_top_cpc",
    "placement_top_cpa",
    "placement_top_roas",
    "placement_rest_acos",
    "placement_rest_ctr",
    "placement_rest_cr",
    "placement_rest_cpc",
    "placement_rest_cpa",
    "placement_rest_roas",
    "placement_buybox_acos",
    "placement_buybox_ctr",
    "placement_buybox_cr",
    "placement_buybox_cpc",
    "placement_buybox_cpa",
    "placement_buybox_roas",
    "placement_homepage_acos",
    "placement_homepage_ctr",
    "placement_homepage_cr",
    "placement_homepage_cpc",
    "placement_homepage_cpa",
    "placement_homepage_roas",
    "placement_searchingrid_acos",
    "placement_searchingrid_ctr",
    "placement_searchingrid_cr",
    "placement_searchingrid_cpc",
    "placement_searchingrid_cpa",
    "placement_searchingrid_roas",
    "placement_stockup_acos",
    "placement_stockup_ctr",
    "placement_stockup_cr",
    "placement_stockup_cpc",
    "placement_stockup_cpa",
    "placement_stockup_roas",
    "total_acos",
    "total_ctr",
    "total_cr",
    "total_cpc",
    "total_cpa",
    "total_roas",
    "spend_in_adgroup",
];

export const COLORS = [
    "#0d6efd",
    "#f8b525",
    "#00a499",
    "#ec4561",
    "#626ed3",
    "#1aadce",
    "#492970",
    "#f28f43",
    "#77a1e5",
    "#c42525",
    "#a6c96a",
];

export const TAG_COLORS: ColorConfig[] = [
    { fill: "#531DAB", border: "#C8A1E0", background: "#F9F0FF" },
    { fill: "#237804", border: "#B5C0D0", background: "#F6FFED" },
    { fill: "#0958D9", border: "#91CAFF", background: "#E6F4FF" },
    { fill: "#D46B08", border: "#FFD591", background: "#FFF7E6" },
    { fill: "#08979C", border: "#b5f5ec", background: "#E6FFFB" },
    { fill: "#AD2102", border: "#FFA39E", background: "#FFF2F0" },
    { fill: "#C41D7F", border: "#FFADD2", background: "#FFF0F6" },
    { fill: "#2E4053", border: "#85929E", background: "#EBF5FB" },
    { fill: "#725E54", border: "#D2BBA9", background: "#F7F3EF" },
    { fill: "#1D39C4", border: "#ADC6FF", background: "#F0F5FF" },
];

export const FILTER_TYPE_SYMBOLS: Record<string, string> = {
    greaterThan: ">",
    greaterThanOrEqual: ">=",
    lessThan: "<",
    lessThanOrEqual: "<=",
    equals: "=",
    relative: "=",
    notEqual: "!=",
    set: "in",
    inSet: "in",
    contains: "contains",
    notContains: "does not contain",
    startsWith: "starts with",
    endsWith: "ends with",
    inRange: "between",
    percentIncreasedBy: "inc. by (%)",
    percentDecreasedBy: "dec. by (%)",
    greaterThanSuggestedMin: "> sugg_min_bid",
    greaterThanSuggestedMax: "> sugg_max_bid",
    lessThanSuggestedMin: "< sugg_min_bid",
    lessThanSuggestedMax: "< sugg_max_bid",
};

export const FUNNEL_ATTRIBUTES = [
    { name: "Ad Sales", key: "sale", isRatio: false },
    { name: "Ad Spends", key: "spend", isRatio: false },
    { name: "Clicks", key: "click", isRatio: false },
    { name: "Impressions", key: "impression", isRatio: false },
    { name: "Orders", key: "orders", isRatio: false },
    { name: "CTR", key: "ctr", isRatio: true },
    { name: "CVR", key: "cr", isRatio: true },
    { name: "CPC", key: "cpc", isRatio: true },
    { name: "ACoS", key: "acos", isRatio: true },
    { name: "AOV", key: "aov", isRatio: true },
    { name: "ROAS", key: "roas", isRatio: true },
];

type TModuleMapping = {
    nameField: string;
};

export const MODULE_MAPPING: Record<string, TModuleMapping> = {
    "ad-group": {
        nameField: "ad_group_name",
    },
    campaign: {
        nameField: "campaign_name",
    },
    keyword: {
        nameField: "keyword_name",
    },
    placement: {
        nameField: "keyword_name",
    },
    product: {
        nameField: "asin",
    },
    portfolio: {
        nameField: "portfolio_name",
    },
    productad: {
        nameField: "ad_group_name",
    },
    products: {
        nameField: "sku_title",
    },
};

export interface ISubModules {
    moduleSlug?: string;
    hasSubModules?: boolean;
    module?: string;
}

interface TModules extends ISubModules {
    subModules?: ISubModules[];
    actions?: string[];
}

export const MODULES: Record<string, TModules> = {
    "performance-overview": {
        hasSubModules: true,
        subModules: [
            {
                moduleSlug: "portfolios",
                hasSubModules: false,
            },
            {
                moduleSlug: "campaigns",
                hasSubModules: false,
            },
            {
                moduleSlug: "adgroups",
                hasSubModules: false,
            },
            {
                moduleSlug: "keywords",
                hasSubModules: false,
            },
            {
                moduleSlug: "products",
                hasSubModules: false,
            },
        ],
        actions: ["pause", "percentIncrease", "percentDecrease"],
    },
    "negative-keywords": {
        hasSubModules: false,
        moduleSlug: "negative-keywords",
        actions: ["markNegative"],
    },
    "keyword-analytics": {
        hasSubModules: false,
        moduleSlug: "keyword-analytics",
        actions: ["pause", "percentIncrease", "percentDecrease"],
    },
    "product-analytics": {
        hasSubModules: false,
        moduleSlug: "product-analytics",
        actions: [
            "pauseKeywords",
            "pauseAds",
            "percentIncrease",
            "percentDecrease",
        ],
    },
    "recommendation/keyword": {
        hasSubModules: true,
        subModules: [
            {
                moduleSlug: "manual-keyword",
                hasSubModules: false,
            },
        ],
    },
    "recommendation/campaign": {
        hasSubModules: true,
        subModules: [
            {
                moduleSlug: "budget-recommendation",
                hasSubModules: false,
            },
        ],
    },
};

export const CAMPAIGN_TYPES: Record<string, string> = {
    sp: "sponsoredProducts",
    sb: "sponsoredBrands",
    sd: "sponsoredDisplay",
    sbv: "sponsoredBrandsVideo",
};

export const TARGET_TYPES: Record<string, string> = {
    product: "PT",
    keyword: "KW",
    auto: "auto",
};

export const KEYWORD_TYPES: Record<string, string> = {
    target: "Product",
    keyword: "Keyword",
    video_keyword: "Video",
};

// common grid options

export const overlayLoadingTemplate = `
    <span class="display-6 tada" data-testid="loading-text" style="opacity:0.4;transform: translateY(-42%);">
      <div class="spinner-border" style="width: 3rem; height: 3rem;" role="status"></div>
      <div class="mt-4">Loading</div>
    </span>`;

export const overlayNoRowsTemplate = `
    <span style="filter:grayscale(1);opacity: 0.3;zoom:0.7">
      <img class="rounded " src=${nodata} width="150" />
      <div class="mt-4 display-6">No Data in this Table</div>
    </span>`;

export const CUSTOM_FILTER_TYPES = [
    "greaterThanSuggestedMin",
    "greaterThanSuggestedMax",
    "lessThanSuggestedMin",
    "lessThanSuggestedMax",
];

export const customFilterOptions = {
    number: [
        "equals",
        "notEqual",
        "greaterThan",
        "greaterThanOrEqual",
        "lessThan",
        "lessThanOrEqual",
        "inRange",
        {
            displayKey: "percentIncreasedBy",
            displayName: "Increased by %",
            test: () => true,
        },
        {
            displayKey: "percentDecreasedBy",
            displayName: "Decreased by %",
            test: () => true,
        },
    ],
    numberWithoutChanges: [
        {
            displayKey: "equals",
            displayName: "Equals",
            predicate: (filterValues: number[], cellValue: TBidCellValue) =>
                cellValue.bid === filterValues[0],
        },
        {
            displayKey: "notEqual",
            displayName: "Not equal",
            predicate: (filterValues: number[], cellValue: TBidCellValue) =>
                cellValue.bid !== filterValues[0],
        },
        {
            displayKey: "greaterThan",
            displayName: "Greater than",
            predicate: (filterValues: number[], cellValue: TBidCellValue) =>
                cellValue.bid > filterValues[0],
        },
        {
            displayKey: "lessThan",
            displayName: "Lesser than",
            predicate: (filterValues: number[], cellValue: TBidCellValue) =>
                cellValue.bid < filterValues[0],
        },
        {
            displayKey: "greaterThanOrEqual",
            displayName: "Greater than or equals",
            predicate: (filterValues: number[], cellValue: TBidCellValue) =>
                cellValue.bid >= filterValues[0],
        },
        {
            displayKey: "lessThanOrEqual",
            displayName: "Less than or equals",
            predicate: (filterValues: number[], cellValue: TBidCellValue) =>
                cellValue.bid <= filterValues[0],
        },
        {
            displayKey: "greaterThanSuggestedMin",
            displayName: "Greater Than Sugg Min Bid",
            predicate: (filterValues: number[], cellValue: TBidCellValue) =>
                cellValue.bid > cellValue.suggestedMin,
            numberOfInputs: 0,
        },
        {
            displayKey: "greaterThanSuggestedMax",
            displayName: "Greater Than Sugg Max Bid",
            predicate: (filterValues: number[], cellValue: TBidCellValue) =>
                cellValue.bid > cellValue.suggestedMax,
            numberOfInputs: 0,
        },
        {
            displayKey: "lessThanSuggestedMin",
            displayName: "Less Than Sugg Min Bid",
            predicate: (filterValues: number[], cellValue: TBidCellValue) =>
                cellValue.bid < cellValue.suggestedMin,
            numberOfInputs: 0,
        },
        {
            displayKey: "lessThanSuggestedMax",
            displayName: "Less Than Sugg Max Bid",
            predicate: (filterValues: number[], cellValue: TBidCellValue) =>
                cellValue.bid < cellValue.suggestedMax,
            numberOfInputs: 0,
        },
    ],
    text: [
        "equals",
        "notEqual",
        "contains",
        "notContains",
        "startsWith",
        "endsWith",
        // {
        // 	displayKey: "set",
        // 	displayName: "In",
        // 	test: (filterValue, cellValue) => {
        // 		const filter = Array.isArray(filterValue)
        // 			? filterValue
        // 			: filterValue.split(",");
        // 		return cellValue && filter.find((val) => cellValue === val);
        // 	},
        // },
    ],
    date: [
        {
            displayKey: "equals",
            displayName: "On",
            test: (
                filterValue: string | number | Date,
                cellValue: string | number | Date
            ) => {
                return (
                    typeof cellValue !== "string" ||
                    new Date(cellValue).setHours(0, 0, 0, 0) ===
                        +new Date(filterValue)
                );
            },
        },
        // {
        // 	// @todo temporary fix
        // 	displayKey: 'inRange',
        // 	displayName: 'between',
        // 	test: function(filterValue, cellValue)
        // 	{
        // 		/**
        // 		 * @todo Need a workaround because aggrid does not provide 'dateTo' value  in this 'inRange' test function
        // 		 * @alert https://stackoverflow.com/questions/63567485/ag-grid-date-column-inrange-filter-how-do-i-access-the-to-date-in-the-test-f
        // 		 * So, filter value is just the 'dateFrom' value, and their is no access to 'dateTo' value
        // 		 */
        // 		return  typeof cellValue !== 'string' || new Date(cellValue).setHours(0,0,0,0) === +new Date(filterValue)
        // 	}
        // },
        {
            displayKey: "lessThan",
            displayName: "Before",
            test: (
                filterValue: string | number | Date,
                cellValue: string | number | Date
            ) => {
                return (
                    typeof cellValue !== "string" ||
                    new Date(cellValue).setHours(0, 0, 0, 0) <
                        +new Date(filterValue)
                );
            },
        },
        {
            displayKey: "greaterThan",
            displayName: "After",
            test: (
                filterValue: string | number | Date,
                cellValue: string | number | Date
            ) =>
                typeof cellValue !== "string" ||
                new Date(cellValue).setHours(0, 0, 0, 0) >
                    +new Date(filterValue),
        },
        {
            displayKey: "lessThanOrEqual",
            displayName: "On or Before",
            test: (
                filterValue: string | number | Date,
                cellValue: string | number | Date
            ) =>
                typeof cellValue !== "string" ||
                new Date(cellValue).setHours(0, 0, 0, 0) <=
                    +new Date(filterValue),
        },
        {
            displayKey: "greaterThanOrEqual",
            displayName: "On or After",
            test: (
                filterValue: string | number | Date,
                cellValue: string | number | Date
            ) =>
                typeof cellValue !== "string" ||
                new Date(cellValue).setHours(0, 0, 0, 0) >=
                    +new Date(filterValue),
        },
    ],
};

export const agDefaultColDef = {
    flex: 1,
    minWidth: 150,
    filter: true,
    resizable: true,
    pivot: false,
    enablePivot: false,
    sortable: true,
    suppressCellSelection: true,
    filterParams: {
        buttons: ["reset", "apply"],
        debounceMs: 200,
    },
    headerComponentParams: {
        template: `<div class="ag-cell-label-container" role="presentation">
						<span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
						<div ref="eLabel" class="ag-header-cell-label" role="presentation">
							<span ref="eSortOrder" class="ag-header-icon ag-sort-order" ></span>
							<span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon" ></span>
							<span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon" ></span>
							<span ref="eSortNone" class="ag-header-icon ag-sort-none-icon" ></span>
							<strong><span ref="eText" class="ag-header-cell-text" role="columnheader"></span></strong>
							<span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
						</div>
					</div>`,
    },
};

export const agDefaultSidebar = {
    toolPanels: [
        {
            id: "filters",
            labelDefault: "Filters",
            labelKey: "filters",
            iconKey: "filter",
            toolPanel: "agFiltersToolPanel",
            toolPanelParams: {
                suppressExpandAll: false,
                suppressFilterSearch: false,
            },
        },
        {
            id: "columns",
            labelDefault: "Columns",
            labelKey: "columns",
            iconKey: "columns",
            toolPanel: "agColumnsToolPanel",
            toolPanelParams: {
                suppressExpandAll: true,
                suppressValues: true,
                suppressPivots: true,
                suppressPivotMode: true,
                suppressFilterSearch: true,
                suppressRowGroups: true,
            },
        },
    ],
    defaultToolPanel: "",
};

export const DAY_PARTING_DAYS = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
];

export const initialDaypartingDataObject: Record<string, Array<number>> = {
    monday: [1],
    tuesday: [1],
    wednesday: [1],
    thursday: [1],
    friday: [1],
    saturday: [1],
    sunday: [1],
};

export const initialDaypartingCardCheckedData: Record<string, object> = {
    monday: {},
    tuesday: {},
    wednesday: {},
    thursday: {},
    friday: {},
    saturday: {},
    sunday: {},
};

export const BID_LIMITS: BidLimit = {
    1: {
        INR: {
            SP: {
                seller: {
                    minimum: 1, //RD-381
                    maximum: 5000,
                },
                vendor: {
                    minimum: 1, //RD-381
                    maximum: 5000,
                },
            },
            SB: {
                seller: {
                    minimum: 1, //RD-381
                    maximum: 2000,
                },
                vendor: {
                    minimum: 1, //RD-381
                    maximum: 2000,
                },
            },
            SD: {
                seller: {
                    minimum: 1,
                    maximum: 5000,
                },
                vendor: {
                    minimum: 1,
                    maximum: 5000,
                },
            },
            "SD-VCPM": {
                seller: {
                    minimum: 4,
                    maximum: 5000,
                },
                vendor: {
                    minimum: 4,
                    maximum: 5000,
                },
            },
        },
        EUR: {
            SP: {
                seller: {
                    minimum: 0.02,
                    maximum: 1000,
                },
                vendor: {
                    minimum: 0.02,
                    maximum: 1000,
                },
            },
            SB: {
                seller: {
                    minimum: 0.1,
                    maximum: 39,
                },
                vendor: {
                    minimum: 0.1,
                    maximum: 39,
                },
            },
            SD: {
                seller: {
                    minimum: 0.02,
                    maximum: 1000,
                },
                vendor: {
                    minimum: 0.02,
                    maximum: 1000,
                },
            },
            "SD-VCPM": {
                seller: {
                    minimum: 1,
                    maximum: 1000,
                },
                vendor: {
                    minimum: 1,
                    maximum: 1000,
                },
            },
        },
        USD: {
            SP: {
                seller: {
                    minimum: 0.02, //RD-381
                    maximum: 1000,
                },
                vendor: {
                    minimum: 0.02, //RD-381
                    maximum: 1000,
                },
            },
            SB: {
                seller: {
                    minimum: 0.1, //RD-381
                    maximum: 49,
                },
                vendor: {
                    minimum: 0.1, //RD-381
                    maximum: 49,
                },
            },
            SD: {
                seller: {
                    minimum: 0.02,
                    maximum: 1000,
                },
                vendor: {
                    minimum: 0.02,
                    maximum: 1000,
                },
            },
            "SD-VCPM": {
                seller: {
                    minimum: 1,
                    maximum: 1000,
                },
                vendor: {
                    minimum: 1,
                    maximum: 1000,
                },
            },
        },
        GBP: {
            SP: {
                seller: { minimum: 0.02, maximum: 1000 },
                vendor: { minimum: 0.02, maximum: 1000 },
            },
            SB: {
                seller: { minimum: 0.1, maximum: 31 },
                vendor: { minimum: 0.1, maximum: 31 },
            },
            SD: {
                seller: {
                    minimum: 0.02,
                    maximum: 1000,
                },
                vendor: {
                    minimum: 0.02,
                    maximum: 1000,
                },
            },
            "SD-VCPM": {
                seller: {
                    minimum: 1,
                    maximum: 1000,
                },
                vendor: {
                    minimum: 1,
                    maximum: 1000,
                },
            },
        },
        CAD: {
            SP: {
                seller: { minimum: 0.02, maximum: 1000 },
                vendor: { minimum: 0.02, maximum: 1000 },
            },
            SB: {
                seller: { minimum: 0.1, maximum: 49 },
                vendor: { minimum: 0.1, maximum: 49 },
            },
            SD: {
                seller: {
                    minimum: 0.02,
                    maximum: 1000,
                },
                vendor: {
                    minimum: 0.02,
                    maximum: 1000,
                },
            },
            "SD-VCPM": {
                seller: {
                    minimum: 1,
                    maximum: 1000,
                },
                vendor: {
                    minimum: 1,
                    maximum: 1000,
                },
            },
        },
        AUD: {
            SP: {
                seller: { minimum: 0.1, maximum: 1410 },
                vendor: { minimum: 0.1, maximum: 1410 },
            },
            SB: {
                seller: { minimum: 0.1, maximum: 70 },
                vendor: { minimum: 0.1, maximum: 70 },
            },
            SD: {
                seller: {
                    minimum: 0.2,
                    maximum: 1000,
                },
                vendor: {
                    minimum: 0.2,
                    maximum: 1000,
                },
            },
            "SD-VCPM": {
                seller: {
                    minimum: 1,
                    maximum: 1000,
                },
                vendor: {
                    minimum: 1,
                    maximum: 1000,
                },
            },
        },
        JPY: {
            SP: {
                seller: { minimum: 2, maximum: 100000 },
                vendor: { minimum: 2, maximum: 100000 },
            },
            SB: {
                seller: { minimum: 10, maximum: 7760 },
                vendor: { minimum: 10, maximum: 7760 },
            },
            SD: {
                seller: {
                    minimum: 2,
                    maximum: 100000,
                },
                vendor: {
                    minimum: 2,
                    maximum: 100000,
                },
            },
            "SD-VCPM": {
                seller: {
                    minimum: 100,
                    maximum: 100000,
                },
                vendor: {
                    minimum: 100,
                    maximum: 100000,
                },
            },
        },
        AED: {
            SP: {
                seller: {
                    minimum: 0.24, //RD-381
                    maximum: 184,
                },
                vendor: {
                    minimum: 0.24, //RD-381
                    maximum: 184,
                },
            },
            SB: {
                seller: {
                    minimum: 0.4, //RD-381
                    maximum: 184,
                },
                vendor: {
                    minimum: 0.4, //RD-381
                    maximum: 184,
                },
            },
            SD: {
                seller: {
                    minimum: 0.2,
                    maximum: 3670,
                },
                vendor: {
                    minimum: 0.2,
                    maximum: 3670,
                },
            },
            "SD-VCPM": {
                seller: {
                    minimum: 1,
                    maximum: 3670,
                },
                vendor: {
                    minimum: 1,
                    maximum: 3670,
                },
            },
        },
        BRL: {
            SP: {
                seller: { minimum: 0.07, maximum: 3700 },
                vendor: { minimum: 0.07, maximum: 3700 },
            },
            SB: {
                seller: { minimum: 0.53, maximum: 200 },
                vendor: { minimum: 0.53, maximum: 200 },
            },
            SD: {
                seller: {
                    minimum: 0.07,
                    maximum: 3700,
                },
                vendor: {
                    minimum: 0.07,
                    maximum: 3700,
                },
            },
            "SD-VCPM": {
                seller: {
                    minimum: 2,
                    maximum: 3700,
                },
                vendor: {
                    minimum: 2,
                    maximum: 3700,
                },
            },
        },
        SGD: {
            SP: {
                seller: { minimum: 0.02, maximum: 1100 },
                vendor: { minimum: 0.02, maximum: 1100 },
            },
            SB: {
                seller: { minimum: 0.14, maximum: 100 },
                vendor: { minimum: 0.14, maximum: 100 },
            },
            SD: {
                seller: {
                    minimum: 0.14,
                    maximum: 1410,
                },
                vendor: {
                    minimum: 0.14,
                    maximum: 1410,
                },
            },
            "SD-VCPM": {
                seller: {
                    minimum: 4,
                    maximum: 1410,
                },
                vendor: {
                    minimum: 4,
                    maximum: 1410,
                },
            },
        },
        SEK: {
            SP: {
                seller: { minimum: 0.18, maximum: 9300 },
                vendor: { minimum: 0.18, maximum: 9300 },
            },
            SB: {
                seller: { minimum: 0.9, maximum: 500 },
                vendor: { minimum: 0.9, maximum: 500 },
            },
            SD: {
                seller: {
                    minimum: 0.02,
                    maximum: 1000,
                },
                vendor: {
                    minimum: 0.02,
                    maximum: 1000,
                },
            },
            "SD-VCPM": {
                seller: {
                    minimum: 1,
                    maximum: 1000,
                },
                vendor: {
                    minimum: 1,
                    maximum: 1000,
                },
            },
        },
        PLN: {
            SP: {
                seller: { minimum: 0.04, maximum: 2000 },
                vendor: { minimum: 0.04, maximum: 2000 },
            },
            SB: {
                seller: {
                    minimum: 0.2,
                    maximum: 200,
                },
                vendor: {
                    minimum: 0.2,
                    maximum: 200,
                },
            },
        },
        MXN: {
            SP: {
                seller: { minimum: 0.1, maximum: 20000 },
                vendor: { minimum: 0.1, maximum: 20000 },
            },
            SB: {
                seller: { minimum: 0.1, maximum: 20000 },
                vendor: { minimum: 0.1, maximum: 20000 },
            },
            SD: {
                seller: {
                    minimum: 0.1,
                    maximum: 20000,
                },
                vendor: {
                    minimum: 0.1,
                    maximum: 20000,
                },
            },
            "SD-VCPM": {
                seller: {
                    minimum: 5,
                    maximum: 20000,
                },
                vendor: {
                    minimum: 5,
                    maximum: 20000,
                },
            },
        },
        TRY: {
            SP: {
                seller: { minimum: 0.05, maximum: 2500 },
                vendor: { minimum: 0.05, maximum: 2500 },
            },
            SB: {
                seller: { minimum: 0.2, maximum: 200 },
                vendor: { minimum: 0.2, maximum: 200 },
            },
            SD: {
                seller: {
                    minimum: 0.05,
                    maximum: 2500,
                },
                vendor: {
                    minimum: 0.05,
                    maximum: 2500,
                },
            },
            "SD-VCPM": {
                seller: {
                    minimum: 1.85,
                    maximum: 2500,
                },
                vendor: {
                    minimum: 1.85,
                    maximum: 2500,
                },
            },
        },
        EGP: {
            SP: {
                seller: { minimum: 0.15, maximum: 5.5 },
                vendor: { minimum: 0.15, maximum: 5.5 },
            },
            SB: {
                seller: { minimum: 0.7, maximum: 400 },
                vendor: { minimum: 0.7, maximum: 400 },
            },
            SD: {
                seller: {
                    minimum: 0.15,
                    maximum: 7400,
                },
                vendor: {
                    minimum: 0.15,
                    maximum: 7400,
                },
            },
            "SD-VCPM": {
                seller: {
                    minimum: 5,
                    maximum: 7400,
                },
                vendor: {
                    minimum: 5,
                    maximum: 7400,
                },
            },
        },
        SAR: {
            SP: {
                seller: { minimum: 0.1, maximum: 3670 },
                vendor: { minimum: 0.1, maximum: 3670 },
            },
            SB: {
                seller: { minimum: 0.4, maximum: 184 },
                vendor: { minimum: 0.4, maximum: 184 },
            },
            SD: {
                seller: {
                    minimum: 0.1,
                    maximum: 3670,
                },
                vendor: {
                    minimum: 0.1,
                    maximum: 3670,
                },
            },
            "SD-VCPM": {
                seller: {
                    minimum: 4,
                    maximum: 3670,
                },
                vendor: {
                    minimum: 4,
                    maximum: 3670,
                },
            },
        },
    },
    2: {
        minimum: 1,
        maximum: 200,
    },
};

export const BUDGET_LIMITS = {
    INR: {
        SP: {
            seller: {
                minimum: 50, //RD-381
                maximum: 21000000,
            },
            vendor: {
                minimum: 50, //RD-381
                maximum: 21000000,
            },
        },
        SB: {
            seller: {
                minimum: 100, //RD-381
                maximum: 21000000,
            },
            vendor: {
                minimum: 500, //RD-381
                maximum: 21000000,
            },
        },
        SD: {
            seller: {
                minimum: 50,
                maximum: 21000000,
            },
            vendor: {
                minimum: 50,
                maximum: 5000000,
            },
        },
    },
    EUR: {
        SP: {
            seller: {
                minimum: 1,
                maximum: 1000000,
            },
            vendor: {
                minimum: 1,
                maximum: 1000000,
            },
        },
        SB: {
            seller: {
                minimum: 1,
                maximum: 1000000,
            },
            vendor: {
                minimum: 1,
                maximum: 1000000,
            },
        },
        SD: {
            seller: {
                minimum: 1,
                maximum: 1000000,
            },
            vendor: {
                minimum: 1,
                maximum: 50000,
            },
        },
    },
    USD: {
        SP: {
            seller: {
                minimum: 1, //RD-381
                maximum: 1000000,
            },
            vendor: {
                minimum: 1, //RD-381
                maximum: 1000000,
            },
        },
        SB: {
            seller: {
                minimum: 1, //RD-381
                maximum: 1000000,
            },
            vendor: {
                minimum: 1, //RD-381
                maximum: 1000000,
            },
        },
        SD: {
            seller: {
                minimum: 1,
                maximum: 1000000,
            },
            vendor: {
                minimum: 1,
                maximum: 50000,
            },
        },
    },
    GBP: {
        SP: {
            seller: { minimum: 1, maximum: 1000000 },
            vendor: { minimum: 1, maximum: 1000000 },
        },
        SB: {
            seller: { minimum: 1, maximum: 1000000 },
            vendor: { minimum: 1, maximum: 1000000 },
        },
        SD: {
            seller: {
                minimum: 1,
                maximum: 1000000,
            },
            vendor: {
                minimum: 1,
                maximum: 50000,
            },
        },
    },
    CAD: {
        SP: {
            seller: { minimum: 1, maximum: 1000000 },
            vendor: { minimum: 1, maximum: 1000000 },
        },
        SB: {
            seller: { minimum: 1, maximum: 1000000 },
            vendor: { minimum: 1, maximum: 1000000 },
        },
        SD: {
            seller: {
                minimum: 1,
                maximum: 1000000,
            },
            vendor: {
                minimum: 1,
                maximum: 50000,
            },
        },
    },
    AUD: {
        SP: {
            seller: { minimum: 1.4, maximum: 1500000 },
            vendor: { minimum: 1.4, maximum: 1500000 },
        },
        SB: {
            seller: { minimum: 1, maximum: 1500000 },
            vendor: { minimum: 1, maximum: 1500000 },
        },
        SD: {
            seller: {
                minimum: 1.4,
                maximum: 1500000,
            },
            vendor: {
                minimum: 1.4,
                maximum: 1500000,
            },
        },
    },
    JPY: {
        SP: {
            seller: { minimum: 100, maximum: 21000000 },
            vendor: { minimum: 100, maximum: 21000000 },
        },
        SB: {
            seller: { minimum: 100, maximum: 21000000 },
            vendor: { minimum: 100, maximum: 21000000 },
        },
        SD: {
            seller: {
                minimum: 100,
                maximum: 21000000,
            },
            vendor: {
                minimum: 100,
                maximum: 5000000,
            },
        },
    },
    AED: {
        SP: {
            seller: {
                minimum: 4, //RD-381
                maximum: 3700000,
            },
            vendor: {
                minimum: 4, //RD-381
                maximum: 3700000,
            },
        },
        SB: {
            seller: {
                minimum: 4, //RD-381
                maximum: 3700000,
            },
            vendor: {
                minimum: 4, //RD-381
                maximum: 3700000,
            },
        },
        SD: {
            seller: {
                minimum: 4,
                maximum: 3700000,
            },
            vendor: {
                minimum: 4,
                maximum: 3700000,
            },
        },
    },
    BRL: {
        SP: {
            seller: { minimum: 1.32, maximum: 5300000 },
            vendor: { minimum: 1.32, maximum: 5300000 },
        },
        SB: {
            seller: { minimum: 4, maximum: 3700000 },
            vendor: { minimum: 4, maximum: 3700000 },
        },
        SD: {
            seller: {
                minimum: 1.32,
                maximum: 5300000,
            },
            vendor: {
                minimum: 1.32,
                maximum: 5300000,
            },
        },
    },
    SGD: {
        SP: {
            seller: { minimum: 1.39, maximum: 1300000 },
            vendor: { minimum: 1.39, maximum: 1300000 },
        },
        SB: {
            seller: { minimum: 4, maximum: 3700000 },
            vendor: { minimum: 4, maximum: 3700000 },
        },
        SD: {
            seller: {
                minimum: 4,
                maximum: 1300000,
            },
            vendor: {
                minimum: 4,
                maximum: 1300000,
            },
        },
    },
    SEK: {
        SP: {
            seller: { minimum: 0.18, maximum: 9300 },
            vendor: { minimum: 0.18, maximum: 9300 },
        },
        SB: {
            seller: { minimum: 0.9, maximum: 500 },
            vendor: { minimum: 0.9, maximum: 500 },
        },
        SD: {
            seller: {
                minimum: 0.02,
                maximum: 1000,
            },
            vendor: {
                minimum: 0.02,
                maximum: 1000,
            },
        },
    },
    PLN: {
        SP: {
            seller: { minimum: 2, maximum: 2000000 },
            vendor: { minimum: 2, maximum: 2000000 },
        },
        SB: {
            seller: {
                minimum: 2,
                maximum: 2400000,
            },
            vendor: {
                minimum: 2,
                maximum: 2400000,
            },
        },
        SD: {
            seller: { minimum: 1, maximum: 1000000 },
            vendor: { minimum: 1, maximum: 1000000 },
        },
    },
    MXN: {
        SP: {
            seller: { minimum: 1, maximum: 21000000 },
            vendor: { minimum: 1, maximum: 21000000 },
        },
        SB: {
            seller: { minimum: 1, maximum: 21000000 },
            vendor: { minimum: 1, maximum: 21000000 },
        },
        SD: {
            seller: {
                minimum: 1,
                maximum: 21000000,
            },
            vendor: {
                minimum: 1,
                maximum: 21000000,
            },
        },
    },
    TRY: {
        SP: {
            seller: { minimum: 2, maximum: 2500000 },
            vendor: { minimum: 2, maximum: 2500000 },
        },
        SB: {
            seller: { minimum: 2, maximum: 2700000 },
            vendor: { minimum: 2, maximum: 2700000 },
        },
        SD: {
            seller: { minimum: 2, maximum: 2700000 },
            vendor: { minimum: 2, maximum: 2700000 },
        },
    },
    EGP: {
        SP: {
            seller: { minimum: 7, maximum: 7400000 },
            vendor: { minimum: 7, maximum: 7400000 },
        },
        SB: {
            seller: { minimum: 7, maximum: 7600000 },
            vendor: { minimum: 7, maximum: 7600000 },
        },
        SD: {
            seller: { minimum: 7, maximum: 7600000 },
            vendor: { minimum: 7, maximum: 7600000 },
        },
    },
    SAR: {
        SP: {
            seller: { minimum: 4, maximum: 3700000 },
            vendor: { minimum: 4, maximum: 3700000 },
        },
        SB: {
            seller: { minimum: 4, maximum: 3700000 },
            vendor: { minimum: 4, maximum: 3700000 },
        },
        SD: {
            seller: { minimum: 4, maximum: 3700000 },
            vendor: { minimum: 4, maximum: 3700000 },
        },
    },
};

export const FILE_SPECIFICATIONS = {
    image: {
        supportedExtensions: ["jpg", "png", "jpeg"],
        maxFileSize: 5000000,
    },
    video: {
        supportedExtensions: ["mp4", "mov"],
        maxFileSize: 500000000,
    },
};

export const CLOCK_COLORS: Record<string, string> = {
    red: "#FF5630",
    blue: "#0D6EFD",
    green: "#28A745",
    grey: "#B2B2B2",
};

export const DPR_TIME_SLOTS = () => {
    const slotDetails: Record<string, object> = {};
    DAY_PARTING_DAYS.forEach((day) => {
        slotDetails[day.toLowerCase()] = {};
    });

    return slotDetails;
};

export const channelsMapping: Record<number, string> = {
    0: ChannelName.DEFAULT,
    1: ChannelName.AMAZON,
    2: ChannelName.FLIPKART,
    27: ChannelName.BLINKIT,
    28: ChannelName.MYNTRA,
    63: ChannelName.PURPLLE,
    65: ChannelName.INSTAMART,
    87: ChannelName.NOON,
    283: ChannelName.NOON_MINUTES,
    109: ChannelName.ZEPTO,
    118: ChannelName.TAKEALOT,
    239: ChannelName.TALABAT,
    42: ChannelName.WALMART,
    30: ChannelName.SHOPEE,
    180: ChannelName.HEPSIBURADA,
};

export const statusColorMap: Record<string, string> = {
    failed: "#FF0000", // Red
    pending: "#FFA500", // Orange
    success: "#00AA00", // Green
};

export const tabLinksData: Array<ILinks> = [
    {
        title: "Overview",
        name: "overview",
        id: "overview-tab",
        path: `/${Platforms.ACTIVATION}/${ParentModule.PERFORMANCE_OVERVIEW}`,
    },
    {
        title: "Portfolios",
        name: "portfolios",
        id: "portfolios-tab",
        path: `/${Platforms.ACTIVATION}/${ParentModule.PERFORMANCE_OVERVIEW}/${SubModule.PORTFOLIO}`,
    },
    {
        title: "Campaigns",
        name: "campaigns",
        id: "campaigns-tab",
        path: `/${Platforms.ACTIVATION}/${ParentModule.PERFORMANCE_OVERVIEW}/${SubModule.CAMPAIGN}`,
    },
    {
        title: "Adgroups",
        name: "adgroups",
        id: "adgroups-tab",
        path: `/${Platforms.ACTIVATION}/${ParentModule.PERFORMANCE_OVERVIEW}/${SubModule.ADGROUP}`,
    },
    {
        title: "Keywords",
        name: "keywords",
        id: "keywords-tab",
        path: `/${Platforms.ACTIVATION}/${ParentModule.PERFORMANCE_OVERVIEW}/${SubModule.KEYWORD}`,
    },
    {
        title: "Placements",
        name: "placements",
        id: "placements-tab",
        path: `/${Platforms.ACTIVATION}/${ParentModule.PERFORMANCE_OVERVIEW}/${SubModule.PLACEMENTS}`,
    },
    {
        title: "Products",
        name: "products",
        id: "products-tab",
        path: `/${Platforms.ACTIVATION}/${ParentModule.PERFORMANCE_OVERVIEW}/${SubModule.PRODUCT}`,
    },
    {
        title: "Suggested Keywords",
        name: "suggested",
        id: "suggested-keywords-tab",
        path: `/${Platforms.ACTIVATION}/${ParentModule.NEGATIVE_KEYWORDS}/${SubModule.SUGGESTED_NEG_KWS}`,
    },
    {
        title: "Existing Keywords",
        name: "existing",
        id: "existing-keywords-tab",
        path: `/${Platforms.ACTIVATION}/${ParentModule.NEGATIVE_KEYWORDS}/${SubModule.EXISTING_NEG_KWS}`,
    },
    {
        title: "Keywords from Manual",
        name: "manual",
        id: "manual-tab",
        path: `/${Platforms.ACTIVATION}/${ParentModule.KEYWORD_RECOMMENDATIONS}/${SubModule.KEYWORD_MANUAL}`,
    },
    {
        title: "Keywords from Auto",
        name: "auto",
        id: "auto-tab",
        path: `/${Platforms.ACTIVATION}/${ParentModule.KEYWORD_RECOMMENDATIONS}/${SubModule.KEYWORD_AUTO}`,
    },
    {
        title: "Reviniti Recommendations",
        name: "reviniti",
        id: "reviniti-tab",
        path: `/${Platforms.ACTIVATION}/${ParentModule.KEYWORD_RECOMMENDATIONS}/${SubModule.KEYWORD_REVINITI}`,
    },
    {
        title: "Budget Recommendations",
        name: "budget",
        id: "budget-tab",
        path: `/${Platforms.ACTIVATION}/${ParentModule.CAMPAIGN_RECOMMENDATIONS}/${SubModule.CAMPAIGN_BUDGET}`,
    },
    {
        title: "Placement Recommendations",
        name: "placement",
        id: "placement-tab",
        path: `/${Platforms.ACTIVATION}/${ParentModule.CAMPAIGN_RECOMMENDATIONS}/${SubModule.CAMPAIGN_PLACEMENT}`,
    },
];

export const moduleConfigs: Partial<Record<ChannelName, ChannelConfig>> = {
    [ChannelName.AMAZON]: {
        options: {
            decimalValue: 2,
            recommendedBid: 10,
            isTargetCheckRequired: true,
            formatText: {
                record_type: {
                    keyword: "Keyword",
                    target: "Product",
                },
                program_type: {
                    sp: "SP",
                    sb: "SB",
                    sd: "SD",
                },
            },
            platformFeatures: [
                Features.DAY_PARTING,
                Features.BUYBOX,
                Features.REVSTACK,
                Features.KEYWORD_RECOMMENDATIONS,
                Features.NAVIGATOR,
                Features.CATEGORY_ANALYSIS,
                Features.PRODUCT_ANALYSIS,
            ],
        },
        modules: {
            [ParentModule.PERFORMANCE_OVERVIEW]: {
                name: "Performance Overview",
                isSubheaderVisible: true,
                subModules: {
                    [SubModule.OVERVIEW]: {
                        name: "Overview",
                        actions: [],
                        columns: [],
                        features: {},
                        overviewGrids: {
                            tagTypeAggregate: {
                                columns: [
                                    "tag",
                                    "spend",
                                    "spend_share",
                                    "sale",
                                    "sale_share",
                                    "click",
                                    "orders",
                                    "roas",
                                    "impression",
                                    "total_impression",
                                    "impPercShare",
                                    "market_share",
                                ],
                            },
                        },
                    },
                    [SubModule.PORTFOLIO]: {
                        name: "Portfolio",
                        actions: [Actions.NAME_CHANGE],
                        columns: [
                            "portfolio_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "acos",
                            "roas",
                        ],
                        features: {
                            [Features.BULK_ACTION]: {
                                name: "Bulk Taxonomy",
                            },
                        },
                        gridParams: {
                            rowHeight: 65,
                        },
                    },
                    [SubModule.CAMPAIGN]: {
                        name: "Campaigns",
                        gridParams: {
                            budget: {
                                minWidth: 170,
                                maxWidth: 190,
                            },
                            program_type: {
                                minWidth: 80,
                                maxWidth: 80,
                            },
                            campaign_name: {
                                minWidth: 320,
                                maxWidth: 500,
                            },
                            isCpmChangeAllowed: false,
                            isBudgetReduceAllowed: true,
                            isDateChangeAllowed: false,
                            isDateReduceAllowed: false,
                            isArchivedCampaignsSelectionAllowed: false,
                            rowHeight: 65,
                        },
                        componentConfig: {
                            till_budget_ends: {
                                isVisible: false,
                            },
                            end_date: {
                                isVisible: false,
                            },
                            equally_divide_budget: {
                                isVisible: false,
                            },
                            cpm: {
                                isVisible: false,
                            },
                        },
                        actions: [
                            Actions.NAME_CHANGE,
                            Actions.STATUS_CHANGE,
                            Actions.BUDGET_CHANGE,
                        ],
                        // Arr of columns to show (keys:string to be used in column defs)
                        columns: [
                            "campaign_name",
                            "budget",
                            "record_type",
                            "portfolio_name",
                            "program_type",
                            "automation",
                            "state",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "top_product",
                            "percent_spend",
                            "sys_campaign_id",
                            "portfolio_id",
                            "campaign_tags",
                            "tag_types",
                        ],
                        features: {
                            [Features.PAUSE]: {
                                name: "Pause",
                                actions: [Actions.PAUSE],
                            },
                            [Features.ENABLE]: {
                                name: "Enable",
                                actions: [Actions.ENABLE],
                            },
                            [Features.BULK_ACTION]: {
                                name: "Bulk Taxonomy",
                            },
                            [Features.DAY_PARTING]: {
                                name: "Day Parting",
                            },
                            [Features.TAGS_ADDITION]: {
                                name: "Add Tags",
                            },
                            [Features.BUDGET]: {
                                name: "Update Budget",
                                actions: [Actions.BUDGET_CHANGE],
                            },
                        },
                    },
                    [SubModule.ADGROUP]: {
                        name: "AdGroups",
                        actions: [Actions.NAME_CHANGE, Actions.STATUS_CHANGE],
                        // Arr of columns to show (keys:string to be used in column defs)
                        columns: [
                            "ad_group_name",
                            "state",
                            "campaign_name",
                            "portfolio_name",
                            "program_type",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "top_product",
                            "percent_spend",
                            "sys_campaign_id",
                            "sys_adgroup_id",
                            "asin_id",
                            "portfolio_id",
                        ],
                        gridParams: {
                            isDateChangeAllowed: false,
                            isDateReduceAllowed: false,
                            isCpmChangeAllowed: false,
                            isBudgetReduceAllowed: true,
                            budget: {
                                minWidth: 370,
                                maxWidth: 380,
                            },
                            rowHeight: 65,
                        },
                        features: {
                            [Features.BULK_ACTION]: {
                                name: "Bulk Taxonomy",
                            },
                        },
                    },
                    [SubModule.KEYWORD]: {
                        name: "Keywords",
                        actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                        gridParams: {
                            bid: {
                                cellRenderer: UpdateBidOrBudgetCellRenderer,
                                minWidth: 170,
                                maxWidth: 190,
                                filterOptions: [
                                    "equals",
                                    "notEqual",
                                    "greaterThan",
                                    "lessThan",
                                    "greaterThanOrEqual",
                                    "lessThanOrEqual",
                                ],
                            },
                            rowHeight: 65,
                        },
                        // Arr of columns to show (keys:string to be used in column defs)
                        columns: [
                            "keyword_name",
                            "is_archived_entity",
                            "bid",
                            "state",
                            "ad_group_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "top_product",
                            "percent_spend",
                            "creation_date",
                            "match_type",
                            "record_type",
                            "kwd_class",
                            "program_type",
                            "campaign_name",
                            "portfolio_name",
                            "portfolio_id",
                            "sys_campaign_id",
                            "sys_adgroup_id",
                            "asin_id",
                            "sys_ams_keyword_id",
                            "campaign_tags",
                            "sponsored_sov",
                            "organic_sov",
                            "overall_sov",
                        ],
                        features: {
                            [Features.BID]: {
                                name: "Update Bid",
                                actions: [
                                    Actions.BID_CHANGE_ABSOLUTE,
                                    Actions.INCREASE_BID_PERC,
                                    Actions.DECREASE_BID_PERC,
                                ],
                            },
                            [Features.PAUSE]: {
                                name: "Pause",
                                actions: [Actions.PAUSE],
                            },
                            [Features.ENABLE]: {
                                name: "Enable",
                                actions: [Actions.ENABLE],
                            },
                            [Features.MARK_NEGATIVE]: {
                                name: "Mark as negative",
                                actions: [Actions.MARK_NEGATIVE],
                            },
                            [Features.RULE]: {
                                name: "Save Actions",
                                actions: [
                                    Actions.PAUSE,
                                    Actions.INCREASE_BID_PERC,
                                    Actions.DECREASE_BID_PERC,
                                ],
                            },
                        },
                    },
                    [SubModule.PRODUCT]: {
                        name: "Products",
                        actions: [Actions.STATUS_CHANGE],
                        // Arr of columns to show (keys:string to be used in column defs)
                        columns: [
                            "sku_title",
                            "ad_group_name",
                            "campaign_name",
                            "portfolio_name",
                            "program_type",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "top_product",
                            "percent_spend",
                            "portfolio_id",
                            "sys_campaign_id",
                            "sys_adgroup_id",
                            "asin_id",
                            "state",
                        ],
                        features: {
                            [Features.PAUSE_ALL]: {
                                name: "Pause All",
                                actions: [Actions.PAUSE_ALL],
                            },
                            [Features.ENABLE_ALL]: {
                                name: "Enable All",
                                actions: [Actions.ENABLE_ALL],
                            },
                        },
                        gridParams: {
                            isKeywordPauseAllowed: true,
                            disabledProgramTypes: [
                                ProgramType.HSA,
                                ProgramType.SB,
                            ],
                            rowHeight: 65,
                        },
                    },
                },
            },
            [ParentModule.RULES]: {
                name: "Rules",
                isSubheaderVisible: false,
                // Arr of columns to show (keys:string to be used in column defs)
                columns: [
                    "rule_id",
                    "name",
                    "type",
                    "filter",
                    "module",
                    "cron_string",
                    "created_at",
                    "created_by",
                    "updated_at",
                    "updated_by",
                    "status",
                    "",
                ],
                features: {},
            },
            [ParentModule.KEYWORD_ANALYTICS]: {
                name: "Search Term Analytics",
                isSubheaderVisible: true,
                gridParams: {
                    isKeywordLinkRequired: true,
                    isAdgroupSelectionRequired: true,
                    isBidRequired: true,
                    isMatchTypeReviewRequired: false,
                    columnsToHide: ["is_exact", "impression", "cpc"],
                    isNegativeAdditionAllowed: true,
                    isMatchTypeSelectionRequired: true,
                    rowHeight: 65,
                },
                actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                columns: [
                    "keyword_name",
                    "program_type",
                    "kwd_class",
                    "is_exact",
                    "impression_share_rank",
                    "impression_share",
                    "page1OverallSov",
                    "page1OrganicSov",
                    "total_impression",
                    "top_product",
                    "rank",
                    "impression",
                    "click",
                    "spend",
                    "orders",
                    "sale",
                    "ctr",
                    "cpc",
                    "cr",
                    "cpa",
                    "acos",
                    "roas",
                    "campaign_nos",
                    "campaigns",
                    "portfolios",
                ],
                subGrids: {
                    keywords: {
                        columns: [
                            "keyword_name",
                            "bid",
                            "state",
                            "kwd_class",
                            "ad_group_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "top_product",
                            "percent_spend",
                            "creation_date",
                            "match_type",
                            "record_type",
                            "program_type",
                            "campaign_name",
                            "sys_campaign_id",
                            "sys_adgroup_id",
                            "asin_id",
                        ],
                        gridParams: {
                            isBadgeRequired: false,
                            doesAddToExact: false,
                            isRemoveSkuAllowed: false,
                            canChangeKeywordStatus: true,
                            bid: {
                                cellRenderer: UpdateBidOrBudgetCellRenderer,
                                minWidth: 150,
                                maxWidth: 170,
                            },
                            rowHeight: 65,
                        },
                    },
                    campaigns: {
                        columns: [
                            "customer_search_term",
                            "keyword_name",
                            "campaign_name",
                            "ad_group_name",
                            "match_type",
                            "spend",
                            "orders",
                            "sale",
                            "impression",
                            "click",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                        ],
                        gridParams: {
                            isBadgeRequired: false,
                            isBidRequired: true,
                            doesAddToExact: true,
                            isRemoveSkuAllowed: false,
                            bidColumn: "cpc",
                            keywordColumn: "customer_search_term",
                            cpc: {
                                minWidth: 170,
                                maxWidth: 180,
                            },
                            rowHeight: 65,
                        },
                    },
                    addAsExact: {
                        columns: [
                            "customer_search_term",
                            "keyword_name",
                            "cpc",
                            "campaign_name",
                            "ad_group_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cr",
                            "cpa",
                            "acos",
                            "program_type",
                            "roas",
                            "match_type",
                        ],
                        gridParams: {
                            isBadgeRequired: false,
                            isTypeBadgeRequired: false,
                            isBidRequired: true,
                            doesAddToExact: true,
                            isRemoveSkuAllowed: false,
                            bidColumn: "cpc",
                            keywordColumn: "customer_search_term",
                            cpc: {
                                headerName: "Suggested Bid",
                                minWidth: 170,
                                maxWidth: 180,
                            },
                            rowHeight: 65,
                        },
                    },
                },
                features: {
                    [Features.ADD_AS_EXACT]: {
                        name: "Add As Exact",
                    },
                    [Features.BULK_TGT_ACTION]: {
                        name: "Bulk Target Actions",
                    },
                    [Features.RULE]: {
                        name: "Save Actions",
                        actions: [
                            Actions.PAUSE,
                            Actions.INCREASE_BID_PERC,
                            Actions.DECREASE_BID_PERC,
                        ],
                    },
                },
            },
            [ParentModule.PRODUCT_ANALYTICS]: {
                name: "Product Analytics",
                isSubheaderVisible: true,
                gridParams: {
                    columnsToHide: [
                        "asin",
                        "is_available",
                        "click",
                        "impression",
                        "orders",
                        "cpc",
                        "cr",
                        "cpa",
                        "acos",
                    ],
                    rowHeight: 65,
                },
                actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                columns: [
                    "sku_title",
                    "asin",
                    "product_link",
                    "",
                    "category",
                    "isFocusedAsin",
                    "is_available",
                    // New metrics
                    "sov_overall_pct",
                    "sov_organic_pct",
                    "sov_paid_pct",
                    "ms_by_price_pct",
                    "ms_by_mrp_pct",
                    "ag_group_nos",
                    // "availability_percent",
                    "bsr",
                    "click",
                    "spend",
                    "impression",
                    "orders",
                    "sale",
                    "total_sale",
                    "ctr",
                    "cpc",
                    "cr",
                    "cpa",
                    "acos",
                    "tacos",
                    "roas",
                    // New averages near pricing/availability
                    // "price",
                    "avg_price",
                    "weighted_availability",
                    "latest_bb_winner",
                    "rating",
                    "rating_count",
                    "tag_types",
                    "sku_tags",
                    "deep_dive",
                ],
                subGrids: {
                    keywords: {
                        columns: [
                            "keyword_name",
                            "bid",
                            "state",
                            "kwd_class",
                            "percent_spend_in_adgroup",
                            "rank",
                            "ad_group_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "match_type",
                            "record_type",
                            "program_type",
                            "campaign_name",
                        ],
                        gridParams: {
                            isBadgeRequired: false,
                            doesAddToExact: false,
                            isRemoveSkuAllowed: false,
                            bid: {
                                cellRenderer: UpdateBidOrBudgetCellRenderer,
                                minWidth: 150,
                                maxWidth: 170,
                            },
                            rowHeight: 65,
                        },
                    },
                    adgroups: {
                        columns: [
                            "campaign_name",
                            "ad_group_name",
                            "sku_id",
                            "state",
                            "percent_spend",
                            "click",
                            "spend",
                            "impression",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                        ],
                        gridParams: {
                            doesAddToExact: false,
                            isRemoveSkuAllowed: false,
                            disabledProgramTypes: [
                                ProgramType.HSA,
                                ProgramType.SB,
                            ],
                            rowHeight: 65,
                        },
                    },
                },
                features: {
                    [Features.ADD_TO_BUYBOX]: {
                        name: "Add to Buybox",
                        actions: [Actions.ADD_TO_BUYBOX],
                    },
                    [Features.REMOVE_BUYBOX]: {
                        name: "Remove Buybox",
                        actions: [Actions.REMOVE_BUYBOX],
                    },
                    [Features.PAUSE_ALL]: {
                        name: "Pause All",
                        actions: [Actions.PAUSE_ALL],
                    },
                    [Features.ENABLE_ALL]: {
                        name: "Enable All",
                        actions: [Actions.ENABLE_ALL],
                    },
                    [Features.CREATE_CAMPAIGN]: {
                        name: "Create Campaign",
                        actions: [Actions.CREATE_CAMPAIGN],
                    },
                    [Features.TAGS_ADDITION]: {
                        name: "Add Tags",
                    },
                    [Features.RULE]: {
                        name: "Save Actions",
                        actions: [
                            Actions.PAUSE_KEYWORDS,
                            Actions.PAUSE_ADS,
                            Actions.INCREASE_BID_PERC,
                            Actions.DECREASE_BID_PERC,
                        ],
                    },
                },
            },
            [ParentModule.PRODUCT_DEEPDIVE]: {
                name: "Product Deepdive",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.NEGATIVE_KEYWORDS]: {
                name: "Negative Keywords",
                isSubheaderVisible: true,
                subModules: {
                    [SubModule.SUGGESTED_NEG_KWS]: {
                        name: "Suggested Negative Keywords",
                        slug: "suggested",
                        actions: [Actions.ADD_AS_NEGATIVE],
                        columns: [
                            "customer_search_term",
                            "",
                            "ad_group_name",
                            "adgroup_type",
                            "nature",
                            "program_type",
                            "campaign_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                        ],
                        gridParams: {
                            program_type: {
                                minWidth: 80,
                                maxWidth: 80,
                            },
                            rowHeight: 65,
                        },
                        features: {
                            [Features.ADD_NEGATIVE]: {
                                name: "Add Negatives",
                                actions: [Actions.ADD_AS_NEGATIVE],
                            },
                            [Features.RULE]: {
                                name: "Save Actions",
                                actions: [Actions.MARK_NEGATIVE],
                            },
                        },
                    },
                    [SubModule.EXISTING_NEG_KWS]: {
                        name: "Existing Negative Keywords",
                        slug: "existing",
                        actions: [Actions.REMOVE_NEGATIVE],
                        columns: [
                            "keyword_name",
                            "",
                            "match_type",
                            "record_type",
                            "ad_group_name",
                            "program_type",
                            "campaign_name",
                        ],
                        gridParams: {
                            program_type: {
                                minWidth: 80,
                                maxWidth: 80,
                            },
                        },
                        features: {
                            [Features.REMOVE_NEGATIVE]: {
                                name: "Remove Negatives",
                                actions: [Actions.REMOVE_NEGATIVE],
                            },
                        },
                    },
                },
            },
            [ParentModule.KEYWORD_RECOMMENDATIONS]: {
                name: "Keywords",
                isSubheaderVisible: true,
                subModules: {
                    [SubModule.KEYWORD_MANUAL]: {
                        name: "Keywords from Manual",
                        slug: "manual",
                        actions: [Actions.ADD_KEYWORD],
                        columns: [
                            "search_term",
                            "bid",
                            "adgroup_name",
                            "campaign_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cr",
                            "cpc",
                            "cpa",
                            "acos",
                            "roas",
                        ],
                        gridParams: {
                            rowHeight: 65,
                        },
                        features: {},
                    },
                    [SubModule.KEYWORD_AUTO]: {
                        name: "Keywords from Auto",
                        slug: "auto",
                        actions: [Actions.ADD_KEYWORD],
                        columns: [
                            "search_term",
                            "bid",
                            "adgroup_name",
                            "campaign_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cr",
                            "cpc",
                            "cpa",
                            "acos",
                            "roas",
                        ],
                        gridParams: {
                            rowHeight: 65,
                        },
                        features: {},
                    },
                    [SubModule.KEYWORD_REVINITI]: {
                        name: "Reviniti Recommedations",
                        slug: "reviniti",
                        actions: [Actions.ADD_KEYWORD],
                        columns: [
                            "search_term",
                            "bid",
                            "adgroup_name",
                            "campaign_name",
                            "targeting_class",
                            "targeting_type",
                            "match_type",
                            "program_type",
                            "",
                        ],
                        features: {},
                    },
                },
            },
            [ParentModule.CAMPAIGN_RECOMMENDATIONS]: {
                name: "Campaigns",
                isSubheaderVisible: true,
                subModules: {
                    [SubModule.CAMPAIGN_BUDGET]: {
                        name: "Budget Recommendations",
                        gridParams: {
                            isCpmChangeAllowed: false,
                            rowHeight: 65,
                        },
                        slug: "budget",
                        actions: [Actions.BUDGET_CHANGE],
                        columns: [
                            "campaign_name",
                            "record_type",
                            "budget",
                            "percentSpentout",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                        ],
                        features: {},
                    },
                    [SubModule.CAMPAIGN_PLACEMENT]: {
                        name: "Placement Recommendations",
                        slug: "placement",
                        actions: [Actions.PLACEMENT_CHANGE],
                        columns: [
                            "campaign_name",
                            "top_of_search",
                            "product_page",
                            "rest_of_search",
                            "total",
                        ],
                        gridParams: {
                            rowHeight: 65,
                        },
                        features: {
                            [Features.BULK_PLACEMENT_CHANGE]: {
                                name: "Bulk Placement Change",
                                actions: [Actions.PLACEMENT_CHANGE],
                            },
                        },
                    },
                },
            },
            [ParentModule.REVIEW_ANALYTICS]: {
                name: "Review Analytics",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.REVSTACK]: {
                name: "Custom Reports",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.NAVIGATOR]: {
                name: "Navigator",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.CATEGORY_ANALYSIS]: {
                name: "Category Analysis",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.PRODUCT_ANALYSIS]: {
                name: "Product Analysis",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.COMMON_REPORTS]: {
                name: "Common Reports",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.CHANGE_LOG]: {
                name: "History",
                isSubheaderVisible: true,
                actions: [],
                columns: [
                    "log_date_iso",
                    "log_date",
                    "revert_action",
                    "log_date",
                    "module",
                    "change_type",
                    "record_name",
                    "record_type",
                    "attribute",
                    "old_value",
                    "new_value",
                    "source",
                    "nature",
                    "source_name",
                    "source_id",
                    "log_time",
                ],
                features: {
                    [Features.REVERT_ACTION]: {
                        name: "Revert Action",
                    },
                },
            },
            [ParentModule.REPORTS_BUILDER]: {
                name: "Reports Builder",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.SETTINGS]: {
                name: "Settings",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {
                    // [Features.BULK_PRODUCT_MAPPING]: {
                    //     name: "Bulk Mapping",
                    // },
                    [Features.ADD_MAPPING]: {
                        name: "Add Mapping",
                    },
                    [Features.APPLY_1DS_MAPPING]: {
                        name: "Apply 1DS Mapping",
                    },
                },
                additionalFeatures: {
                    "buybox-add-sellers": true,
                },
            },
            [ParentModule.ONEDER]: {
                name: "1der ai",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
                additionalFeatures: {},
            },
            [ParentModule.CHATBOT]: {
                name: "Ask AI",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
                additionalFeatures: {},
            },
            [ParentModule.INTEGRATIONS]: {
                name: "Integrations",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.ADMIN_SETTINGS]: {
                name: "Admin Settings",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
        },
        dropdownOptions: {
            targetTypeOptions: [
                { value: "product", label: "Product" },
                { value: "keyword", label: "Keyword" },
                { value: "auto", label: "Auto" },
            ],
            matchTypeOptions: [
                { value: "broad", label: "Broad" },
                { value: "exact", label: "Exact" },
                { value: "phrase", label: "Phrase" },
            ],
            negativeMatchTypeOptions: [
                { value: "exact", label: "Exact" },
                { value: "phrase", label: "Phrase" },
            ],
            programTypeOptions: [
                { value: "sp", label: "SP" },
                { value: "sb", label: "SB" },
            ],
            kaCampaignTypeOptions: [
                { value: "auto", label: "Auto" },
                { value: "manual", label: "Manual" },
            ],
            chartFilterOptions: [
                { value: "sp", label: "SP" },
                { value: "sb", label: "SB" },
                { value: "sd", label: "SD" },
                { value: "all", label: "All" },
            ],
        },
    },
    [ChannelName.FLIPKART]: {
        options: {
            decimalValue: 2,
            recommendedBid: 10,
            isTargetCheckRequired: false,
            formatText: {
                record_type: {
                    keyword: "Keyword",
                    target: "Product",
                },
                marketplace_type: {
                    m: "Marketplace",
                    g: "Grocery",
                },
                program_type: {
                    PCA: "PCA",
                    PLA: "PLA",
                },
            },
            platformFeatures: [
                Features.DAY_PARTING,
                Features.REVSTACK,
                Features.CATEGORY_ANALYSIS,
                Features.PRODUCT_ANALYSIS,
                Features.NAVIGATOR,
            ],
        },
        modules: {
            [ParentModule.PERFORMANCE_OVERVIEW]: {
                name: "Performance Overview",
                isSubheaderVisible: true,
                subModules: {
                    [SubModule.OVERVIEW]: {
                        name: "Overview",
                        actions: [],
                        columns: [],
                        features: {},
                        overviewGrids: {
                            tagTypeAggregate: {
                                columns: [
                                    "tag",
                                    "spend",
                                    "spend_share",
                                    "sale",
                                    "sale_share",
                                    "click",
                                    "orders",
                                    "roas",
                                    "impression",
                                    "total_impression",
                                    "impPercShare",
                                    "market_share",
                                    "total_clicked_ad_sales",
                                    "troas",
                                ],
                            },
                        },
                    },
                    [SubModule.CAMPAIGN]: {
                        name: "Campaigns",
                        gridParams: {
                            isDateChangeAllowed: true,
                            budget: {
                                minWidth: 370,
                                maxWidth: 380,
                            },
                            program_type: {
                                minWidth: 80,
                                maxWidth: 80,
                            },
                            campaign_name: {
                                minWidth: 320,
                                maxWidth: 500,
                            },
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isCpmChangeAllowed: false,
                            isBudgetReduceAllowed: false,
                            isDailyBudgetChangeOnPlatform: false,
                            isDateReduceAllowed: true,
                            isArchivedCampaignsSelectionAllowed: false,
                        },
                        componentConfig: {
                            till_budget_ends: {
                                label: "Till Budget Ends",
                                isVisible: true,
                            },
                            end_date: {
                                isVisible: true,
                                disabledTypes: [],
                            },
                            equally_divide_budget: {
                                payloadKey: "equallyDivideBudget",
                                label: "Equally divide budget for all days",
                                isVisible: true,
                                showForTypes: [ProgramType.PLA],
                            },
                            cpm: {
                                isVisible: false,
                            },
                        },
                        actions: [
                            Actions.STATUS_CHANGE,
                            Actions.BUDGET_CHANGE,
                            Actions.NAME_CHANGE,
                            Actions.REQUIRED_DAILY_BUDGET_CHANGE,
                        ],
                        columns: [
                            "campaign_name",
                            "budget",
                            "record_type",
                            "program_type",
                            "automation",
                            "state",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "sys_campaign_id",
                            "campaign_status",
                            "campaign_tags",
                            "tag_types",
                            "required_daily_spend",
                            "percent_utilisation",
                            "total_clicked_ad_sales",
                            "troas",
                            "marketplace_type",
                        ],
                        features: {
                            [Features.BULK_ACTION]: {
                                name: "Bulk Taxonomy",
                            },
                            [Features.DAY_PARTING]: {
                                name: "Day Parting",
                            },
                            [Features.PAUSE]: {
                                name: "Pause",
                                actions: [Actions.PAUSE],
                            },
                            [Features.ENABLE]: {
                                name: "Enable",
                                actions: [Actions.ENABLE],
                            },
                            [Features.DATE_CHANGE]: {
                                name: "Change End Date",
                            },
                            [Features.TAGS_ADDITION]: {
                                name: "Add Tags",
                            },
                            [Features.BUDGET]: {
                                name: "Update Budget",
                                actions: [Actions.BUDGET_CHANGE],
                            },
                        },
                        nameValidationRegex:
                            /[^$&(),.*:/;=?@+[\]~{}_0-9a-zA-Z -]|&|₹/g,
                    },
                    [SubModule.KEYWORD]: {
                        name: "Keywords",
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            bid: {
                                cellRenderer: UpdateBidOrBudgetCellRenderer,
                                minWidth: 170,
                                maxWidth: 190,
                                filterOptions: [
                                    "equals",
                                    "notEqual",
                                    "greaterThan",
                                    "lessThan",
                                    "greaterThanOrEqual",
                                    "lessThanOrEqual",
                                ],
                            },
                            excludedBidProgramType: [ProgramType.PCA],
                            shouldRemoveTitleCase: true,
                        },
                        actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                        columns: [
                            "keyword_name",
                            "is_archived_entity",
                            "bid",
                            "ad_group_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "program_type",
                            "campaign_name",
                            "sys_campaign_id",
                            "sys_adgroup_id",
                            "sys_ams_keyword_id",
                            "campaign_tags",
                            "tag_types",
                            "percent_utilisation",
                            "serving_status",
                            "total_clicked_ad_sales",
                            "troas",
                            "match_type",
                        ],
                        features: {
                            [Features.BID]: {
                                name: "Update Bid",
                                actions: [
                                    Actions.BID_CHANGE_ABSOLUTE,
                                    Actions.INCREASE_BID_PERC,
                                    Actions.DECREASE_BID_PERC,
                                ],
                            },
                            [Features.RULE]: {
                                name: "Save Actions",
                                actions: [
                                    Actions.INCREASE_BID_PERC,
                                    Actions.DECREASE_BID_PERC,
                                ],
                            },
                        },
                    },
                    [SubModule.PLACEMENTS]: {
                        name: "Placements",
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            bid: {
                                cellRenderer: UpdateBidOrBudgetCellRenderer,
                                minWidth: 170,
                                maxWidth: 190,
                                filterOptions: [
                                    "equals",
                                    "notEqual",
                                    "greaterThan",
                                    "lessThan",
                                    "greaterThanOrEqual",
                                    "lessThanOrEqual",
                                ],
                            },
                        },
                        actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                        columns: [
                            "keyword_name",
                            "is_archived_entity",
                            "bid",
                            "ad_group_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "program_type",
                            "campaign_name",
                            "sys_campaign_id",
                            "sys_adgroup_id",
                            "sys_ams_keyword_id",
                            "campaign_tags",
                            "tag_types",
                            "percent_utilisation",
                            "serving_status",
                            "total_clicked_ad_sales",
                            "troas",
                        ],
                        features: {
                            [Features.BID]: {
                                name: "Update Bid",
                                actions: [
                                    Actions.BID_CHANGE_ABSOLUTE,
                                    Actions.INCREASE_BID_PERC,
                                    Actions.DECREASE_BID_PERC,
                                ],
                            },
                        },
                    },
                    [SubModule.PRODUCT]: {
                        name: "Products",
                        actions: [Actions.STATUS_CHANGE],
                        columns: [
                            "sku_title",
                            "ad_group_name",
                            "campaign_name",
                            "program_type",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "top_product",
                            "percent_spend",
                            "sys_campaign_id",
                            "sys_adgroup_id",
                            "asin_id",
                            "total_clicked_ad_sales",
                            "troas",
                            "state",
                        ],
                        features: {},
                        gridParams: {
                            rowHeight: 65,
                        },
                    },
                },
            },
            [ParentModule.RULES]: {
                name: "Rules",
                isSubheaderVisible: false,
                // Arr of columns to show (keys:string to be used in column defs)
                columns: [
                    "rule_id",
                    "name",
                    "type",
                    "filter",
                    "module",
                    "cron_string",
                    "created_at",
                    "created_by",
                    "updated_at",
                    "updated_by",
                    "status",
                    "",
                ],
                features: {},
            },
            [ParentModule.KEYWORD_ANALYTICS]: {
                name: "Search Term Analytics",
                isSubheaderVisible: true,
                gridParams: {
                    isKeywordLinkRequired: true,
                    isAdgroupSelectionRequired: true,
                    isBidRequired: true,
                    isMatchTypeReviewRequired: false,
                    columnsToHide: ["is_exact", "impression", "cpc"],
                    isNegativeAdditionAllowed: true,
                    isMatchTypeSelectionRequired: true,
                    excludedBidProgramType: [ProgramType.PCA],
                    rowHeight: 65,
                },
                actions: [Actions.BID_CHANGE],
                subGrids: {
                    keywords: {
                        columns: [
                            "keyword_name",
                            "bid",
                            "state",
                            // "kwd_class",
                            "ad_group_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "top_product",
                            "percent_spend",
                            // "creation_date",
                            // "match_type",
                            "record_type",
                            "program_type",
                            "campaign_name",
                            "sys_campaign_id",
                            "sys_adgroup_id",
                            "asin_id",
                        ],
                        gridParams: {
                            rowHeight: 65,
                            doesAddToExact: false,
                            isBadgeRequired: true,
                            isRemoveSkuAllowed: false,
                            canChangeKeywordStatus: false,
                            bid: {
                                cellRenderer: UpdateBidOrBudgetCellRenderer,
                                minWidth: 150,
                                maxWidth: 170,
                            },
                        },
                    },
                    campaigns: {
                        columns: [
                            "customer_search_term",
                            "keyword_name",
                            "campaign_name",
                            "ad_group_name",
                            "spend",
                            "orders",
                            "sale",
                            "impression",
                            "click",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "total_clicked_ad_sales",
                            "troas",
                        ],
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isBidRequired: false,
                            doesAddToExact: true,
                            bidColumn: "cpc",
                            keywordColumn: "keyword_name",
                            isRemoveSkuAllowed: false,
                            cpc: {
                                headerName: "Add As Exact",
                                minWidth: 100,
                                maxWidth: 110,
                            },
                        },
                    },
                    addAsExact: {
                        columns: [
                            "keyword_name",
                            "cpc",
                            "campaign_name",
                            "ad_group_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cr",
                            "cpa",
                            "acos",
                            "program_type",
                            "roas",
                            "troas",
                            "total_clicked_ad_sales",
                        ],
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isBidRequired: false,
                            isTypeBadgeRequired: false,
                            doesAddToExact: true,
                            bidColumn: "cpc",
                            keywordColumn: "keyword_name",
                            isRemoveSkuAllowed: false,
                            cpc: {
                                headerName: "Add As Exact",
                                minWidth: 100,
                                maxWidth: 110,
                            },
                        },
                    },
                },
                // Arr of columns to show (keys:string to be used in column defs)
                // TODO: temp commented "kwd_class" for fk
                columns: [
                    "keyword_name",
                    "program_type",
                    // "kwd_class",
                    "is_exact",
                    "impression_share_rank",
                    "impression_share",
                    "page1OverallSov",
                    "page1OrganicSov",
                    "total_impression",
                    "top_product",
                    "rank",
                    "impression",
                    "click",
                    "spend",
                    "orders",
                    "sale",
                    "ctr",
                    "cpc",
                    "cr",
                    "cpa",
                    "acos",
                    "roas",
                    "total_clicked_ad_sales",
                    "troas",
                    "campaign_nos",
                    "campaigns",
                ],
                features: {
                    [Features.BULK_TGT_ACTION]: {
                        name: "Bulk Target Actions",
                    },
                    [Features.ADD_AS_EXACT]: {
                        name: "Add As Exact",
                    },
                },
            },
            [ParentModule.PRODUCT_ANALYTICS]: {
                name: "Product Analytics",
                isSubheaderVisible: true,
                actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                gridParams: {
                    columnsToHide: [
                        "asin",
                        "is_available",
                        "click",
                        "impression",
                        "orders",
                        "cpc",
                        "cr",
                        "cpa",
                        "acos",
                    ],
                    rowHeight: 65,
                },
                // Arr of columns to show (keys:string to be used in column defs)
                columns: [
                    "sku_title",
                    "asin",
                    "",
                    "category",
                    "ag_group_nos",
                    "isFocusedAsin",
                    "is_available",
                    // New metrics
                    "sov_overall_pct",
                    "sov_organic_pct",
                    "sov_paid_pct",
                    "ms_by_price_pct",
                    "ms_by_mrp_pct",
                    // "availability_percent",
                    "bsr",
                    "click",
                    "spend",
                    "impression",
                    "orders",
                    "sale",
                    "ctr",
                    "cpc",
                    "cr",
                    "cpa",
                    "acos",
                    // "tacos",
                    "roas",
                    // New averages near pricing/availability
                    // "price",
                    "avg_price",
                    "weighted_availability",
                    "bb_winner",
                    "rating",
                    "rating_count",
                    // "price",
                    "total_clicked_ad_sales",
                    "troas",
                    "tag_types",
                    "sku_tags",
                    "deep_dive",
                ],
                subGrids: {
                    keywords: {
                        columns: [
                            "keyword_name",
                            "bid",
                            // "kwd_class",
                            // "percent_spend_in_adgroup",
                            // "rank",
                            "ad_group_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            // "match_type",
                            "record_type",
                            "program_type",
                            "campaign_name",
                            "troas",
                            "total_clicked_ad_sales",
                        ],
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            doesAddToExact: false,
                            isRemoveSkuAllowed: false,
                            bid: {
                                cellRenderer: UpdateBidOrBudgetCellRenderer,
                                minWidth: 150,
                                maxWidth: 170,
                            },
                        },
                    },
                    adgroups: {
                        columns: [
                            "campaign_name",
                            "ad_group_name",
                            "sku_id",
                            "state",
                            "percent_spend",
                            "click",
                            "spend",
                            "impression",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                        ],
                        gridParams: {
                            isBadgeRequired: true,
                            rowHeight: 65,
                            doesAddToExact: false,
                            isRemoveSkuAllowed: true,
                        },
                    },
                },
                features: {
                    [Features.TAGS_ADDITION]: {
                        name: "Add Tags",
                    },
                },
            },
            [ParentModule.PRODUCT_DEEPDIVE]: {
                name: "Product Deepdive",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.NEGATIVE_KEYWORDS]: {
                name: "Negative Keywords",
                isSubheaderVisible: true,
                subModules: {
                    [SubModule.SUGGESTED_NEG_KWS]: {
                        name: "Suggested Negative Keywords",
                        slug: "suggested",
                        actions: [Actions.ADD_AS_NEGATIVE],
                        columns: [
                            "customer_search_term",
                            "",
                            "ad_group_name",
                            "adgroup_type",
                            "nature",
                            "program_type",
                            "campaign_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "total_clicked_ad_sales",
                            "troas",
                        ],
                        gridParams: {
                            program_type: {
                                minWidth: 80,
                                maxWidth: 80,
                            },
                        },
                        features: {
                            [Features.ADD_NEGATIVE]: {
                                name: "Add Negatives",
                                actions: [Actions.ADD_AS_NEGATIVE],
                            },
                            [Features.RULE]: {
                                name: "Save Actions",
                                actions: [Actions.MARK_NEGATIVE],
                            },
                        },
                    },
                    [SubModule.EXISTING_NEG_KWS]: {
                        name: "Existing Negative Keywords",
                        slug: "existing",
                        actions: [Actions.REMOVE_NEGATIVE],
                        columns: [
                            "keyword_name",
                            "",
                            "match_type",
                            "record_type",
                            "ad_group_name",
                            "program_type",
                            "campaign_name",
                        ],
                        gridParams: {
                            program_type: {
                                minWidth: 80,
                                maxWidth: 80,
                            },
                        },
                        features: {
                            [Features.REMOVE_NEGATIVE]: {
                                name: "Remove Negatives",
                                actions: [Actions.REMOVE_NEGATIVE],
                            },
                        },
                    },
                },
            },
            [ParentModule.REVIEW_ANALYTICS]: {
                name: "Review Analytics",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.REVSTACK]: {
                name: "Custom Reports",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.NAVIGATOR]: {
                name: "Navigator",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.CATEGORY_ANALYSIS]: {
                name: "Category Analysis",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.PRODUCT_ANALYSIS]: {
                name: "Product Analysis",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.COMMON_REPORTS]: {
                name: "Common Reports",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.CHANGE_LOG]: {
                name: "History",
                isSubheaderVisible: true,
                actions: [],
                columns: [
                    "log_date_iso",
                    "log_date",
                    "log_date",
                    "revert_action",
                    "module",
                    "change_type",
                    "record_name",
                    "record_type",
                    "attribute",
                    "old_value",
                    "new_value",
                    "source",
                    "nature",
                    "source_name",
                    "source_id",
                    "log_time",
                ],
                features: {
                    [Features.REVERT_ACTION]: {
                        name: "Revert Action",
                    },
                },
            },
            [ParentModule.REPORTS_BUILDER]: {
                name: "Reports Builder",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.SETTINGS]: {
                name: "Settings",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {
                    // [Features.BULK_PRODUCT_MAPPING]: {
                    //     name: "Bulk Mapping",
                    // },
                    [Features.ADD_MAPPING]: {
                        name: "Add Mapping",
                    },
                    [Features.APPLY_1DS_MAPPING]: {
                        name: "Apply 1DS Mapping",
                    },
                },
                additionalFeatures: {
                    "buybox-add-sellers": false,
                },
            },
            [ParentModule.ONEDER]: {
                name: "1der ai",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
                additionalFeatures: {},
            },
            [ParentModule.CHATBOT]: {
                name: "Ask AI",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
                additionalFeatures: {},
            },
            [ParentModule.ADMIN_SETTINGS]: {
                name: "Admin Settings",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
        },
        dropdownOptions: {
            targetTypeOptions: [],
            matchTypeOptions: [
                { value: "exact", label: "Exact" },
                { value: "phrase", label: "Phrase" },
            ],
            negativeMatchTypeOptions: [{ value: "exact", label: "Exact" }],
            programTypeOptions: [
                { value: "PLA", label: "PLA" },
                { value: "PCA", label: "PCA" },
            ],
            kaCampaignTypeOptions: [{ value: "manual", label: "Manual" }],
            chartFilterOptions: [
                { value: "PLA", label: "PLA" },
                { value: "PCA", label: "PCA" },
                { value: "all", label: "All" },
            ],
        },
    },
    [ChannelName.BLINKIT]: {
        options: {
            decimalValue: 0,
            recommendedBid: 250,
            isTargetCheckRequired: false,
            isSmartInfoRequired: true,
            formatText: {
                program_type: {
                    p: "Performance",
                    r: "Reach",
                },
                record_type: {
                    keyword: "Keyword",
                    target: "Category",
                },
                targetingtype: {
                    banner_listing: "Listing Spotlight",
                    product_listing: "Product Booster",
                    product_recommendation: "Recommendation Ads",
                    search_suggestion: "Brand Suggestions",
                },
            },
            platformFeatures: [
                Features.DAY_PARTING,
                Features.REVSTACK,
                Features.CATEGORY_ANALYSIS,
                Features.PRODUCT_ANALYSIS,
                Features.NAVIGATOR,
            ],
            minCampaignBudget: 500,
            maxCampaignBudget: 100000000,
            minExactMatchBid: 200,
        },
        modules: {
            [ParentModule.PERFORMANCE_OVERVIEW]: {
                name: "Performance Overview",
                isSubheaderVisible: true,
                subModules: {
                    [SubModule.OVERVIEW]: {
                        name: "Overview",
                        actions: [],
                        columns: [],
                        features: {},
                        overviewGrids: {
                            tagTypeAggregate: {
                                columns: [
                                    "tag",
                                    "spend",
                                    "spend_share",
                                    "sale",
                                    "sale_share",
                                    "click",
                                    "orders",
                                    "roas",
                                    "impression",
                                    "total_clicked_ad_sales",
                                    "total_clicked_ad_units",
                                    "troas",
                                ],
                            },
                        },
                    },
                    [SubModule.CAMPAIGN]: {
                        name: "Campaigns",
                        gridParams: {
                            budget: {
                                minWidth: 370,
                                maxWidth: 380,
                            },
                            program_type: {
                                minWidth: 120,
                                maxWidth: 120,
                            },
                            campaign_name: {
                                minWidth: 340,
                                maxWidth: 520,
                            },
                            click: {
                                headerName: "Direct ATC",
                            },
                            cpc: {
                                headerName: "CPATC",
                            },
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            isCpmChangeAllowed: false,
                            isBudgetReduceAllowed: true,
                            isDateChangeAllowed: true,
                            isDateReduceAllowed: true,
                            isCampaignRestartAllowed: true,
                            isArchivedCampaignsSelectionAllowed: true,
                            isCampaignSyncStatusRequired: true,
                        },
                        componentConfig: {
                            till_budget_ends: {
                                label: "No End Date",
                                isVisible: true,
                            },
                            end_date: {
                                isVisible: true,
                                disabledTypes: [],
                            },
                            equally_divide_budget: {
                                isVisible: false,
                            },
                            cpm: {
                                isVisible: false,
                            },
                        },
                        actions: [
                            Actions.STATUS_CHANGE,
                            Actions.BUDGET_CHANGE,
                            Actions.NAME_CHANGE,
                            Actions.BULK_CAMPAIGN_CREATION,
                        ],
                        columns: [
                            "creation_date",
                            "campaign_name",
                            "budget",
                            "state",
                            "start_date",
                            "record_type",
                            "program_type",
                            "automation",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "sys_campaign_id",
                            "campaign_status",
                            "campaign_tags",
                            "tag_types",
                            "required_daily_spend",
                            "percent_utilisation",
                            "total_clicked_ad_sales",
                            "troas",
                            "total_clicked_ad_units",
                            "cpm",
                            "targetingtype",
                            "origin",
                        ],
                        columnsOverwrite: [
                            {
                                field: "budget",
                                headerName: "End Date + Budget",
                            },
                        ],
                        features: {
                            [Features.BULK_ACTION]: {
                                name: "Bulk Taxonomy",
                            },
                            [Features.DAY_PARTING]: {
                                name: "Day Parting",
                            },
                            [Features.DATE_CHANGE]: {
                                name: "Change End Date",
                            },
                            [Features.TAGS_ADDITION]: {
                                name: "Add Tags",
                            },
                            [Features.BUDGET]: {
                                name: "Update Budget",
                                actions: [Actions.BUDGET_CHANGE],
                            },
                            [Features.STOP]: {
                                name: "Stop",
                                actions: [Actions.ARCHIVE],
                            },
                            [Features.ENABLE]: {
                                name: "Enable",
                                actions: [Actions.ENABLE],
                            },
                        },
                    },
                    [SubModule.KEYWORD]: {
                        name: "Keywords",
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            isKeywordPauseAllowed: false,
                            bid: {
                                cellRenderer: PopOverBidCellRenderer,
                                minWidth: 150,
                                maxWidth: 160,
                                filterOptions: [
                                    "equals",
                                    "notEqual",
                                    "greaterThan",
                                    "lessThan",
                                    "greaterThanOrEqual",
                                    "lessThanOrEqual",
                                    "greaterThanSuggestedMin",
                                    "greaterThanSuggestedMax",
                                    "lessThanSuggestedMin",
                                    "lessThanSuggestedMax",
                                ],
                                maxBidLimitRecordTypes: [RecordType.KEYWORD],
                                canBidInputBeDisabled: true,
                                isSlotChangeRequired: false,
                                bidType: "Exact",
                            },
                            click: {
                                headerName: "Direct ATC",
                            },
                            cpc: {
                                headerName: "CPATC",
                            },
                        },
                        actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                        columns: [
                            "keyword_name",
                            "is_archived_entity",
                            "bid",
                            "state",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "program_type",
                            "campaign_name",
                            "sys_campaign_id",
                            "sys_ams_keyword_id",
                            "campaign_tags",
                            "tag_types",
                            "percent_utilisation",
                            "serving_status",
                            "total_clicked_ad_sales",
                            "troas",
                            "total_clicked_ad_units",
                            "cpm",
                            "record_type",
                            "targetingtype",
                            "kwd_class",
                            "latest_top_viewed_pos",
                            "avg_top_viewed_pos",
                            "search_volume",
                            "sponsored_sov",
                            "organic_sov",
                            "overall_sov",
                        ],
                        features: {
                            [Features.ARCHIVE]: {
                                name: "Archive",
                                actions: [Actions.ARCHIVE],
                            },
                            [Features.BID]: {
                                name: "Update Bid",
                                actions: [
                                    Actions.BID_CHANGE_ABSOLUTE,
                                    Actions.INCREASE_BID_PERC,
                                    Actions.DECREASE_BID_PERC,
                                ],
                            },
                            [Features.RULE]: {
                                name: "Save Actions",
                                actions: [
                                    Actions.INCREASE_BID_PERC,
                                    Actions.DECREASE_BID_PERC,
                                    Actions.MOVE_TO_SUGG_MIN,
                                    Actions.MOVE_TO_SUGG_MAX,
                                    Actions.MOVE_TO_SUGG_AVG,
                                ],
                            },
                        },
                    },
                },
            },
            [ParentModule.RULES]: {
                name: "Rules",
                isSubheaderVisible: false,
                // Arr of columns to show (keys:string to be used in column defs)
                columns: [
                    "rule_id",
                    "name",
                    "type",
                    "filter",
                    "module",
                    "cron_string",
                    "created_at",
                    "created_by",
                    "updated_at",
                    "updated_by",
                    "status",
                    "",
                ],
                features: {},
            },
            [ParentModule.KEYWORD_ANALYTICS]: {
                name: "Search Term Analytics",
                isSubheaderVisible: true,
                gridParams: {
                    isKeywordLinkRequired: true,
                    isAdgroupSelectionRequired: false,
                    isBidRequired: true,
                    isMatchTypeReviewRequired: false,
                    isNegativeAdditionAllowed: true,
                    isMatchTypeSelectionRequired: true,
                    columnsToHide: [
                        "page1OrganicSov",
                        "impression_share",
                        "total_impression",
                        "rank",
                    ],
                    rowHeight: 65,
                    cpc: {
                        headerName: "CPATC",
                    },
                    click: {
                        headerName: "Direct ATC",
                    },
                    is_exact: {
                        headerName: "Is Exact",
                    },
                },
                actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                columns: [
                    "keyword_name",
                    "program_type",
                    "kwd_class",
                    "is_exact",
                    "impression",
                    "click",
                    "spend",
                    "orders",
                    "sale",
                    "ctr",
                    "cpc",
                    "cr",
                    "cpa",
                    "acos",
                    "roas",
                    // New averages near pricing/availability
                    // "price",
                    "avg_price",
                    // "availability_percent",
                    "weighted_availability",
                    "cpm",
                    "total_clicked_ad_sales",
                    "troas",
                    "campaigns",
                    "campaign_nos",
                    "sponsored_sov",
                    "organic_sov",
                    "overall_sov",
                    "search_volume",
                ],
                subGrids: {
                    keywords: {
                        columns: [
                            "keyword_name",
                            "bid",
                            "state",
                            "kwd_class",
                            // "ad_group_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "top_product",
                            "percent_spend",
                            // "creation_date",
                            "match_type",
                            "record_type",
                            "program_type",
                            "campaign_name",
                            "sys_campaign_id",
                            "sys_adgroup_id",
                            "asin_id",
                            "cpm",
                            "targetingtype",
                            "latest_top_viewed_pos",
                            "avg_top_viewed_pos",
                            "sponsored_sov",
                            "organic_sov",
                            "overall_sov",
                            "search_volume",
                        ],
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            doesAddToExact: false,
                            isKeywordPauseAllowed: false,
                            isRemoveSkuAllowed: false,
                            canChangeKeywordStatus: true,
                            bid: {
                                cellRenderer: PopOverBidCellRenderer,
                                minWidth: 150,
                                maxWidth: 160,
                                maxBidLimitRecordTypes: [RecordType.KEYWORD],
                                canBidInputBeDisabled: true,
                                isSlotChangeRequired: false,
                                bidType: "Exact",
                            },
                            cpc: {
                                headerName: "CPATC",
                            },
                            click: {
                                headerName: "Direct ATC",
                            },
                        },
                    },
                    campaigns: {
                        columns: [
                            "customer_search_term",
                            "keyword_name",
                            "cpm",
                            "campaign_name",
                            "spend",
                            "orders",
                            "sale",
                            "impression",
                            "click",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "total_clicked_ad_sales",
                            "troas",
                        ],
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            isBidRequired: true,
                            doesAddToExact: true,
                            bidColumn: "cpm",
                            keywordColumn: "keyword_name",
                            isRemoveSkuAllowed: false,
                            cpm: {
                                headerName: "Suggested CPM",
                                minWidth: 170,
                                maxWidth: 180,
                            },
                        },
                    },
                    addAsExact: {
                        columns: [
                            "keyword_name",
                            "cpc",
                            "cpm",
                            "campaign_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cr",
                            "cpa",
                            "acos",
                            "program_type",
                            "roas",
                            "troas",
                            "total_clicked_ad_sales",
                        ],
                        gridParams: {
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            isBidRequired: true,
                            doesAddToExact: true,
                            rowHeight: 65,
                            bidColumn: "cpm",
                            keywordColumn: "keyword_name",
                            isRemoveSkuAllowed: false,
                            cpm: {
                                headerName: "Suggested CPM",
                                minWidth: 170,
                                maxWidth: 180,
                            },
                        },
                    },
                },
                features: {
                    [Features.BULK_TGT_ACTION]: {
                        name: "Bulk Target Actions",
                    },
                    [Features.ADD_AS_EXACT]: {
                        name: "Add As Exact",
                    },
                },
            },
            [ParentModule.PRODUCT_ANALYTICS]: {
                name: "Product Analytics",
                isSubheaderVisible: true,
                actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                columns: [
                    "sku_title",
                    "asin",
                    "product_link",
                    "",
                    "isFocusedAsin",
                    "is_available",
                    // New metrics
                    "sov_overall_pct",
                    "sov_organic_pct",
                    "sov_paid_pct",
                    "ms_by_price_pct",
                    "ms_by_mrp_pct",
                    "click",
                    "spend",
                    "impression",
                    "orders",
                    "sale",
                    "ctr",
                    // "total_sale",
                    "cpc",
                    "cr",
                    "cpa",
                    "acos",
                    // "tacos",
                    "roas",
                    // New averages near pricing/availability
                    // "price",
                    "avg_price",
                    // "availability_percent",
                    "weighted_availability",
                    "total_clicked_ad_sales",
                    "troas",
                    "cpm",
                    "tag_types",
                    "sku_tags",
                ],
                gridParams: {
                    cpc: {
                        headerName: "CPATC",
                    },
                    click: {
                        headerName: "Direct ATC",
                    },
                    columnsToHide: [
                        "asin",
                        "is_available",
                        // Keep SOV/MS visible by default; hide on request
                        "click",
                        "impression",
                        "orders",
                        "cpc",
                        "cr",
                        "cpa",
                        "acos",
                    ],
                },
                subGrids: {
                    keywords: {
                        columns: [
                            "keyword_name",
                            "bid",
                            "state",
                            "kwd_class",
                            // "percent_spend_in_adgroup",
                            // "rank",
                            // "ad_group_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "match_type",
                            "record_type",
                            "program_type",
                            "campaign_name",
                            "troas",
                            "total_clicked_ad_sales",
                            "cpm",
                            "targetingtype",
                        ],
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            doesAddToExact: false,
                            isRemoveSkuAllowed: false,
                            isKeywordPauseAllowed: false,
                            bid: {
                                cellRenderer: PopOverBidCellRenderer,
                                minWidth: 150,
                                maxWidth: 160,
                                maxBidLimitRecordTypes: [RecordType.KEYWORD],
                                canBidInputBeDisabled: true,
                                isSlotChangeRequired: false,
                                bidType: "Exact",
                            },
                            cpc: {
                                headerName: "CPATC",
                            },
                            click: {
                                headerName: "Direct ATC",
                            },
                        },
                    },
                },
                features: {
                    [Features.TAGS_ADDITION]: {
                        name: "Add Tags",
                    },
                },
            },
            [ParentModule.PRODUCT_DEEPDIVE]: {
                name: "Product Deepdive",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.NEGATIVE_KEYWORDS]: {
                name: "Negative Keywords",
                isSubheaderVisible: true,
                subModules: {
                    [SubModule.SUGGESTED_NEG_KWS]: {
                        name: "Suggested Negative Keywords",
                        slug: "suggested",
                        actions: [Actions.ADD_AS_NEGATIVE],
                        columns: [
                            "customer_search_term",
                            "",
                            "adgroup_type",
                            "nature",
                            "program_type",
                            "campaign_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "total_clicked_ad_sales",
                            "troas",
                        ],
                        gridParams: {
                            program_type: {
                                minWidth: 120,
                                maxWidth: 120,
                            },
                            cpc: {
                                headerName: "CPATC",
                            },
                            click: {
                                headerName: "Direct ATC",
                            },
                        },
                        features: {
                            [Features.ADD_NEGATIVE]: {
                                name: "Add Negatives",
                                actions: [Actions.ADD_AS_NEGATIVE],
                            },
                            [Features.RULE]: {
                                name: "Save Actions",
                                actions: [Actions.MARK_NEGATIVE],
                            },
                        },
                    },
                    [SubModule.EXISTING_NEG_KWS]: {
                        name: "Existing Negative Keywords",
                        slug: "existing",
                        actions: [Actions.REMOVE_NEGATIVE],
                        columns: [
                            "keyword_name",
                            "",
                            "match_type",
                            "record_type",
                            "program_type",
                            "campaign_name",
                        ],
                        gridParams: {
                            program_type: {
                                minWidth: 120,
                                maxWidth: 120,
                            },
                        },
                        features: {
                            [Features.REMOVE_NEGATIVE]: {
                                name: "Remove Negatives",
                                actions: [Actions.REMOVE_NEGATIVE],
                            },
                        },
                    },
                },
            },
            [ParentModule.COMMON_REPORTS]: {
                name: "Common Reports",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.REVIEW_ANALYTICS]: {
                name: "Review Analytics",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.REVSTACK]: {
                name: "Custom Reports",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.NAVIGATOR]: {
                name: "Navigator",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.CATEGORY_ANALYSIS]: {
                name: "Category Analysis",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.PRODUCT_ANALYSIS]: {
                name: "Product Analysis",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.CHANGE_LOG]: {
                name: "History",
                isSubheaderVisible: true,
                actions: [],
                columns: [
                    "log_date_iso",
                    "log_date",
                    "log_date",
                    "revert_action",
                    "module",
                    "change_type",
                    "record_name",
                    "record_type",
                    "attribute",
                    "old_value",
                    "new_value",
                    "source",
                    "nature",
                    "source_name",
                    "source_id",
                    "log_time",
                ],
                features: {
                    [Features.REVERT_ACTION]: {
                        name: "Revert Action",
                    },
                },
            },
            [ParentModule.REPORTS_BUILDER]: {
                name: "Reports Builder",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.SETTINGS]: {
                name: "Settings",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {
                    // [Features.BULK_PRODUCT_MAPPING]: {
                    //     name: "Bulk Mapping",
                    // },
                    [Features.ADD_MAPPING]: {
                        name: "Add Mapping",
                    },
                    [Features.APPLY_1DS_MAPPING]: {
                        name: "Apply 1DS Mapping",
                    },
                },
                additionalFeatures: {
                    "buybox-add-sellers": false,
                },
            },
            [ParentModule.ONEDER]: {
                name: "1der ai",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
                additionalFeatures: {},
            },
            [ParentModule.CHATBOT]: {
                name: "Ask AI",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
                additionalFeatures: {},
            },
            [ParentModule.ADMIN_SETTINGS]: {
                name: "Admin Settings",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
        },
        dropdownOptions: {
            targetTypeOptions: [],
            matchTypeOptions: [{ value: "exact", label: "Exact" }],
            negativeMatchTypeOptions: [{ value: "exact", label: "Exact" }],
            programTypeOptions: [
                { value: "p", label: "Performance" },
                { value: "r", label: "Reach" },
            ],
            kaCampaignTypeOptions: [{ value: "manual", label: "Manual" }],
            chartFilterOptions: [
                { value: "p", label: "Performance" },
                { value: "r", label: "Reach" },
                { value: "all", label: "All" },
            ],
        },
    },
    [ChannelName.MYNTRA]: {
        options: {
            decimalValue: 2,
            recommendedBid: 10,
            isTargetCheckRequired: false,
            formatText: {
                program_type: {
                    sp: "SP",
                    pla: "PLA",
                },
                record_type: {
                    keyword: "Keyword",
                    target: "Category",
                },
            },
            platformFeatures: [
                Features.DAY_PARTING,
                Features.REVSTACK,
                Features.CATEGORY_ANALYSIS,
                Features.PRODUCT_ANALYSIS,
                Features.NAVIGATOR,
            ],
        },
        modules: {
            [ParentModule.PERFORMANCE_OVERVIEW]: {
                name: "Performance Overview",
                isSubheaderVisible: true,
                subModules: {
                    [SubModule.OVERVIEW]: {
                        name: "Overview",
                        actions: [],
                        columns: [],
                        features: {},
                        overviewGrids: {
                            tagTypeAggregate: {
                                columns: [
                                    "tag",
                                    "spend",
                                    "spend_share",
                                    "sale",
                                    "sale_share",
                                    "click",
                                    "orders",
                                    "roas",
                                    "impression",
                                    "market_share",
                                    "total_clicked_ad_sales",
                                    "troas",
                                ],
                            },
                        },
                    },
                    [SubModule.CAMPAIGN]: {
                        name: "Campaigns",
                        gridParams: {
                            budget: {
                                minWidth: 370,
                                maxWidth: 380,
                            },
                            program_type: {
                                minWidth: 80,
                                maxWidth: 80,
                            },
                            campaign_name: {
                                minWidth: 320,
                                maxWidth: 500,
                            },
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isCpmChangeAllowed: false,
                            isBudgetReduceAllowed: false,
                            isDateChangeAllowed: true,
                            isDateReduceAllowed: true,
                            isArchivedCampaignsSelectionAllowed: false,
                        },
                        componentConfig: {
                            till_budget_ends: {
                                label: "Till Budget Ends",
                                isVisible: true,
                            },
                            end_date: {
                                isVisible: true,
                                disabledTypes: [],
                            },
                            equally_divide_budget: {
                                isVisible: false,
                            },
                            cpm: {
                                isVisible: false,
                            },
                        },
                        actions: [
                            Actions.STATUS_CHANGE,
                            Actions.BUDGET_CHANGE,
                            Actions.NAME_CHANGE,
                        ],
                        columns: [
                            "campaign_name",
                            "budget",
                            "record_type",
                            "program_type",
                            "automation",
                            "state",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "sys_campaign_id",
                            "campaign_status",
                            "campaign_tags",
                            "tag_types",
                            "required_daily_spend",
                            "percent_utilisation",
                            "total_clicked_ad_sales",
                            "troas",
                        ],
                        features: {
                            [Features.BULK_ACTION]: {
                                name: "Bulk Taxonomy",
                            },
                            [Features.DAY_PARTING]: {
                                name: "Day Parting",
                            },
                            [Features.PAUSE]: {
                                name: "Pause",
                                actions: [Actions.PAUSE],
                            },
                            [Features.ENABLE]: {
                                name: "Enable",
                                actions: [Actions.ENABLE],
                            },
                            [Features.DATE_CHANGE]: {
                                name: "Change End Date",
                            },
                            [Features.TAGS_ADDITION]: {
                                name: "Add Tags",
                            },
                            [Features.BUDGET]: {
                                name: "Update Budget",
                                actions: [Actions.BUDGET_CHANGE],
                            },
                        },
                        nameValidationRegex:
                            /[^$&(),.*:/;=?@+[\]~{}_0-9a-zA-Z -]|&|₹/g,
                    },

                    [SubModule.ADGROUP]: {
                        name: "AdGroups",
                        actions: [
                            Actions.NAME_CHANGE,
                            Actions.STATUS_CHANGE,
                            Actions.BUDGET_CHANGE,
                        ],
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isCpmChangeAllowed: true,
                            isBudgetReduceAllowed: true,
                            isDateChangeAllowed: true,
                            isDateReduceAllowed: true,
                            budget: {
                                minWidth: 370,
                                maxWidth: 380,
                            },
                            cpmChangeAllowedTypes: [
                                ProgramType.SP,
                                ProgramType.Awareness,
                            ],
                        },
                        columns: [
                            "ad_group_name",
                            "state",
                            "budget",
                            "campaign_name",
                            "portfolio_name",
                            "program_type",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "top_product",
                            "percent_spend",
                            "sys_campaign_id",
                            "sys_adgroup_id",
                            "asin_id",
                            "portfolio_id",
                        ],
                        componentConfig: {
                            till_budget_ends: {
                                label: "No Expiry",
                                isVisible: true,
                            },
                            end_date: {
                                isVisible: true,
                                disabledTypes: [],
                            },
                            equally_divide_budget: {
                                isVisible: false,
                            },
                            cpm: {
                                label: "CPC",
                                isVisible: true,
                            },
                        },
                        features: {
                            [Features.BULK_ACTION]: {
                                name: "Bulk Taxonomy",
                            },
                            [Features.DATE_CHANGE]: {
                                name: "Change End Date",
                            },
                        },
                    },
                    [SubModule.KEYWORD]: {
                        name: "Keywords",
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            bid: {
                                cellRenderer: UpdateBidOrBudgetCellRenderer,
                                minWidth: 170,
                                maxWidth: 190,
                                filterOptions: [
                                    "equals",
                                    "notEqual",
                                    "greaterThan",
                                    "lessThan",
                                    "greaterThanOrEqual",
                                    "lessThanOrEqual",
                                ],
                            },
                        },
                        actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                        columns: [
                            "keyword_name",
                            "is_archived_entity",
                            "bid",
                            "ad_group_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "program_type",
                            "campaign_name",
                            "sys_campaign_id",
                            "sys_adgroup_id",
                            "sys_ams_keyword_id",
                            "campaign_tags",
                            "percent_utilisation",
                            "serving_status",
                            "total_clicked_ad_sales",
                            "troas",
                        ],
                        features: {
                            [Features.BID]: {
                                name: "Update Bid",
                                actions: [
                                    Actions.BID_CHANGE_ABSOLUTE,
                                    Actions.INCREASE_BID_PERC,
                                    Actions.DECREASE_BID_PERC,
                                ],
                            },
                            [Features.RULE]: {
                                name: "Save Actions",
                                actions: [
                                    Actions.INCREASE_BID_PERC,
                                    Actions.DECREASE_BID_PERC,
                                ],
                            },
                        },
                    },
                    [SubModule.PRODUCT]: {
                        name: "Products",
                        actions: [],
                        columns: [
                            "sku_title",
                            "ad_group_name",
                            "campaign_name",
                            "program_type",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "top_product",
                            "percent_spend",
                            "sys_campaign_id",
                            "sys_adgroup_id",
                            "asin_id",
                            "total_clicked_ad_sales",
                            "troas",
                        ],
                        features: {},
                        gridParams: {
                            rowHeight: 65,
                        },
                    },
                },
            },
            [ParentModule.RULES]: {
                name: "Rules",
                isSubheaderVisible: false,
                // Arr of columns to show (keys:string to be used in column defs)
                columns: [
                    "rule_id",
                    "name",
                    "type",
                    "filter",
                    "module",
                    "cron_string",
                    "created_at",
                    "created_by",
                    "updated_at",
                    "updated_by",
                    "status",
                    "",
                ],
                features: {},
            },
            [ParentModule.PRODUCT_ANALYTICS]: {
                name: "Product Analytics",
                isSubheaderVisible: true,
                actions: [Actions.BID_CHANGE],
                gridParams: {
                    columnsToHide: [
                        "asin",
                        "is_available",
                        "click",
                        "impression",
                        "orders",
                        "cpc",
                        "cr",
                        "cpa",
                        "acos",
                    ],
                    rowHeight: 65,
                },
                // Arr of columns to show (keys:string to be used in column defs)
                columns: [
                    "sku_title",
                    "asin",
                    "",
                    "category",
                    "isFocusedAsin",
                    "is_available",
                    // New metrics
                    "sov_overall_pct",
                    "sov_organic_pct",
                    "sov_paid_pct",
                    "ms_by_price_pct",
                    "ms_by_mrp_pct",
                    "ag_group_nos",
                    // "availability_percent",
                    "bsr",
                    "click",
                    "spend",
                    "impression",
                    "orders",
                    "sale",
                    "ctr",
                    "cpc",
                    "cr",
                    "cpa",
                    "acos",
                    // "tacos",
                    "roas",
                    // New averages near pricing/availability
                    // "price",
                    "avg_price",
                    "weighted_availability",
                    "bb_winner",
                    "rating",
                    "rating_count",
                    // "price",
                    "total_clicked_ad_sales",
                    "troas",
                    "tag_types",
                    "sku_tags",
                ],
                subGrids: {
                    keywords: {
                        columns: [
                            "keyword_name",
                            "bid",
                            // "kwd_class",
                            // "percent_spend_in_adgroup",
                            // "rank",
                            "ad_group_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            // "match_type",
                            "record_type",
                            "program_type",
                            "campaign_name",
                            "troas",
                            "total_clicked_ad_sales",
                        ],
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            doesAddToExact: false,
                            isRemoveSkuAllowed: false,
                            bid: {
                                cellRenderer: UpdateBidOrBudgetCellRenderer,
                                minWidth: 150,
                                maxWidth: 170,
                            },
                        },
                    },
                    adgroups: {
                        columns: [
                            "campaign_name",
                            "ad_group_name",
                            "sku_id",
                            "",
                            "percent_spend",
                            "click",
                            "spend",
                            "impression",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                        ],
                        gridParams: {
                            isBadgeRequired: true,
                            rowHeight: 65,
                            doesAddToExact: false,
                            isRemoveSkuAllowed: true,
                        },
                    },
                },
                features: {
                    [Features.TAGS_ADDITION]: {
                        name: "Add Tags",
                    },
                },
            },
            [ParentModule.PRODUCT_DEEPDIVE]: {
                name: "Product Deepdive",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.REVIEW_ANALYTICS]: {
                name: "Review Analytics",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.REVSTACK]: {
                name: "Custom Reports",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.NAVIGATOR]: {
                name: "Navigator",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.CATEGORY_ANALYSIS]: {
                name: "Category Analysis",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.PRODUCT_ANALYSIS]: {
                name: "Product Analysis",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.COMMON_REPORTS]: {
                name: "Common Reports",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.CHANGE_LOG]: {
                name: "History",
                isSubheaderVisible: true,
                actions: [],
                columns: [
                    "log_date_iso",
                    "log_date",
                    "log_date",
                    "revert_action",
                    "module",
                    "change_type",
                    "record_name",
                    "record_type",
                    "attribute",
                    "old_value",
                    "new_value",
                    "source",
                    "nature",
                    "source_name",
                    "source_id",
                    "log_time",
                ],
                features: {
                    [Features.REVERT_ACTION]: {
                        name: "Revert Action",
                    },
                },
            },
            [ParentModule.SETTINGS]: {
                name: "Settings",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {
                    // [Features.BULK_PRODUCT_MAPPING]: {
                    //     name: "Bulk Mapping",
                    // },
                    [Features.ADD_MAPPING]: {
                        name: "Add Mapping",
                    },
                    [Features.APPLY_1DS_MAPPING]: {
                        name: "Apply 1DS Mapping",
                    },
                },
                additionalFeatures: {
                    "buybox-add-sellers": false,
                },
            },
            [ParentModule.ONEDER]: {
                name: "1der ai",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
                additionalFeatures: {},
            },
            [ParentModule.CHATBOT]: {
                name: "Ask AI",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
                additionalFeatures: {},
            },
            [ParentModule.ADMIN_SETTINGS]: {
                name: "Admin Settings",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
        },
        dropdownOptions: {
            targetTypeOptions: [],
            matchTypeOptions: [
                { value: "exact", label: "Exact" },
                { value: "phrase", label: "Phrase" },
            ],
            negativeMatchTypeOptions: [{ value: "exact", label: "Exact" }],
            programTypeOptions: [{ value: "sp", label: "SP" }],
            kaCampaignTypeOptions: [{ value: "manual", label: "Manual" }],
            chartFilterOptions: [{ value: "sp", label: "SP" }],
        },
    },
    [ChannelName.ZEPTO]: {
        options: {
            decimalValue: 0,
            recommendedBid: 10,
            isTargetCheckRequired: false,
            isSmartInfoRequired: false,
            formatText: {
                program_type: {
                    a: "Awareness",
                    p: "Performance",
                    sp: "SP",
                    sd: "SD",
                    sb: "SB",
                },
                record_type: {
                    keyword: "Keyword",
                    target: "Category",
                },
                targetingtype: {
                    AUCTION: "Auction",
                    OS_ADS_SEARCH: "Search Ads",
                    SMART_SHOPPING: "Smart Shopping",
                    BANNER: "Banner",
                    PDA: "PDA",
                    MANUAL: "Manual",
                    KEYWORD_TARGETED_DISPLAY: "Keyword Targeted Display",
                    PCA: "PCA",
                    AUTO: "Auto",
                },
            },
            platformFeatures: [
                Features.DAY_PARTING,
                Features.REVSTACK,
                Features.CATEGORY_ANALYSIS,
                Features.PRODUCT_ANALYSIS,
                Features.NAVIGATOR,
            ],
        },
        modules: {
            [ParentModule.PERFORMANCE_OVERVIEW]: {
                name: "Performance Overview",
                isSubheaderVisible: true,
                subModules: {
                    [SubModule.OVERVIEW]: {
                        name: "Overview",
                        actions: [],
                        columns: [],
                        features: {},
                        overviewGrids: {
                            tagTypeAggregate: {
                                columns: [
                                    "tag",
                                    "spend",
                                    "spend_share",
                                    "sale",
                                    "sale_share",
                                    "click",
                                    "orders",
                                    "roas",
                                    "impression",
                                    "total_clicked_ad_sales",
                                    "total_clicked_ad_units",
                                    "troas",
                                ],
                            },
                        },
                    },
                    [SubModule.CAMPAIGN]: {
                        name: "Campaigns",
                        gridParams: {
                            budget: {
                                minWidth: 370,
                                maxWidth: 380,
                            },
                            program_type: {
                                minWidth: 120,
                                maxWidth: 120,
                            },
                            campaign_name: {
                                minWidth: 290,
                                maxWidth: 310,
                            },
                            cost_type: {
                                minWidth: 180,
                                maxWidth: 190,
                            },
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            isCpmChangeAllowed: true,
                            isBudgetReduceAllowed: true,
                            isDateChangeAllowed: true,
                            isDateReduceAllowed: true,
                            cpmChangeAllowedTypes: [
                                ProgramType.SD,
                                ProgramType.Awareness,
                                ProgramTypeText.keyword_targeted_display,
                            ],
                            isArchivedCampaignsSelectionAllowed: false,
                        },
                        componentConfig: {
                            till_budget_ends: {
                                label: "No End Date",
                                isVisible: true,
                            },
                            end_date: {
                                isVisible: true,
                                disabledTypes: [
                                    ProgramType.SP,
                                    ProgramType.Performance,
                                ],
                            },
                            equally_divide_budget: {
                                isVisible: false,
                            },
                            cpm: {
                                isVisible: true,
                                label: "CPM",
                            },
                            auto: {
                                isVisible: true,
                                label: "Auto bid",
                                disabledTypes: [
                                    ProgramType.SD,
                                    ProgramType.Performance,
                                    ProgramType.Awareness,
                                ],
                            },
                            contextual_multiplier: {
                                isVisible: true,
                                label: "Contextual Multiplier",
                                disabledTypes: [
                                    ProgramType.SP,
                                    ProgramType.Performance,
                                    ProgramType.Awareness,
                                    ProgramTypeText.banner,
                                    ProgramTypeText.keyword_targeted_display,
                                    ProgramType.PCA,
                                    ProgramTypeText.auto,
                                ],
                            },
                            premium_placements: {
                                isVisible: true,
                                label: "Premium Placements",
                                disabledTypes: [
                                    ProgramType.SP,
                                    ProgramType.Performance,
                                    ProgramType.Awareness,
                                    ProgramTypeText.keyword_targeted_display,
                                    ProgramType.PCA,
                                    ProgramTypeText.auto,
                                ],
                            },
                            product_listing_page: {
                                isVisible: true,
                                label: "Category Product Listing Page",
                                disabledTypes: [
                                    ProgramType.SP,
                                    ProgramType.Performance,
                                    ProgramType.Awareness,
                                    ProgramTypeText.pda,
                                    ProgramTypeText.keyword_targeted_display,
                                    ProgramType.PCA,
                                    ProgramTypeText.auto,
                                ],
                            },
                            home_page: {
                                isVisible: true,
                                label: "Home Page",
                                disabledTypes: [
                                    ProgramType.SP,
                                    ProgramType.Performance,
                                    ProgramType.Awareness,
                                    ProgramTypeText.pda,
                                    ProgramTypeText.keyword_targeted_display,
                                    ProgramType.PCA,
                                    ProgramTypeText.auto,
                                ],
                            },
                            top_of_search: {
                                isVisible: true,
                                label: "Top of Search",
                                disabledTypes: [
                                    ProgramType.SD,
                                    ProgramType.Performance,
                                    ProgramType.Awareness,
                                    ProgramTypeText.keyword_targeted_display,
                                    ProgramType.PCA,
                                ],
                            },
                            premium_users: {
                                isVisible: true,
                                label: "Premium Users",
                                disabledTypes: [
                                    ProgramType.SD,
                                    ProgramType.Performance,
                                    ProgramType.Awareness,
                                    ProgramTypeText.keyword_targeted_display,
                                    ProgramType.PCA,
                                ],
                            },
                        },
                        actions: [
                            Actions.STATUS_CHANGE,
                            Actions.BUDGET_CHANGE,
                            Actions.NAME_CHANGE,
                        ],
                        columns: [
                            "campaign_name",
                            "state",
                            "budget",
                            "record_type",
                            "program_type",
                            "automation",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "sys_campaign_id",
                            "campaign_status",
                            "campaign_tags",
                            "tag_types",
                            "cpm",
                            "targetingtype",
                            "bidding",
                            "aov",
                            "total_clicked_ad_sales",
                            "total_clicked_ad_units",
                            "total_view_ad_units",
                            "troas",
                            "ctr",
                        ],
                        features: {
                            [Features.BULK_ACTION]: {
                                name: "Bulk Taxonomy",
                            },
                            [Features.DAY_PARTING]: {
                                name: "Day Parting",
                            },
                            [Features.PAUSE]: {
                                name: "Pause",
                                actions: [Actions.PAUSE],
                            },
                            [Features.ENABLE]: {
                                name: "Enable",
                                actions: [Actions.ENABLE],
                            },
                            [Features.DATE_CHANGE]: {
                                name: "Change End Date",
                            },
                            [Features.TAGS_ADDITION]: {
                                name: "Add Tags",
                            },
                            [Features.BUDGET]: {
                                name: "Update Budget",
                                actions: [Actions.BUDGET_CHANGE],
                            },
                        },
                    },
                    [SubModule.KEYWORD]: {
                        name: "Keywords",
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            bid: {
                                cellRenderer: UpdateBidOrBudgetCellRenderer,
                                minWidth: 150,
                                maxWidth: 170,
                                filterOptions: [
                                    "equals",
                                    "notEqual",
                                    "greaterThan",
                                    "lessThan",
                                    "greaterThanOrEqual",
                                    "lessThanOrEqual",
                                ],
                            },
                            isKeywordPauseAllowed: false,
                            excludedBidProgramType: [ProgramType.SD],
                        },
                        actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                        columns: [
                            "keyword_name",
                            "is_archived_entity",
                            "bid",
                            "state",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "program_type",
                            "campaign_name",
                            "sys_campaign_id",
                            "sys_ams_keyword_id",
                            "campaign_tags",
                            "tag_types",
                            "percent_utilisation",
                            "serving_status",
                            "cpm",
                            "record_type",
                            "targetingtype",
                            "match_type",
                            "aov",
                            "troas",
                            "total_clicked_ad_sales",
                            "total_clicked_ad_units",
                            "total_view_ad_units",
                            "ctr",
                        ],
                        features: {
                            [Features.PAUSE]: {
                                name: "Pause",
                                actions: [Actions.PAUSE],
                            },
                            [Features.ENABLE]: {
                                name: "Enable",
                                actions: [Actions.ENABLE],
                            },
                            [Features.BID]: {
                                name: "Update Bid",
                                actions: [
                                    Actions.BID_CHANGE_ABSOLUTE,
                                    Actions.INCREASE_BID_PERC,
                                    Actions.DECREASE_BID_PERC,
                                ],
                            },
                            [Features.RULE]: {
                                name: "Save Actions",
                                actions: [
                                    Actions.INCREASE_BID_PERC,
                                    Actions.DECREASE_BID_PERC,
                                ],
                            },
                        },
                    },
                    [SubModule.PRODUCT]: {
                        name: "Products",
                        actions: [],
                        columns: [
                            "sku_title",
                            "campaign_name",
                            "program_type",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "sys_campaign_id",
                            "asin_id",
                            "aov",
                            "troas",
                            "total_view_ad_units",
                            "total_clicked_ad_sales",
                            "total_clicked_ad_units",
                        ],
                        features: {},
                        gridParams: {
                            rowHeight: 65,
                        },
                    },
                },
            },
            [ParentModule.RULES]: {
                name: "Rules",
                isSubheaderVisible: false,
                // Arr of columns to show (keys:string to be used in column defs)
                columns: [
                    "rule_id",
                    "name",
                    "type",
                    "filter",
                    "module",
                    "cron_string",
                    "created_at",
                    "created_by",
                    "updated_at",
                    "updated_by",
                    "status",
                    "",
                ],
                features: {},
            },
            [ParentModule.KEYWORD_ANALYTICS]: {
                name: "Keyword Analytics",
                isSubheaderVisible: true,
                gridParams: {
                    isKeywordLinkRequired: true,
                    isAdgroupSelectionRequired: false,
                    isBidRequired: true,
                    excludedBidProgramType: [ProgramType.SD],
                    isMatchTypeReviewRequired: false,
                    isNegativeAdditionAllowed: true,
                    isMatchTypeSelectionRequired: true,
                    columnsToHide: [
                        "page1OrganicSov",
                        "impression_share",
                        "total_impression",
                        "rank",
                        "is_exact",
                    ],
                    rowHeight: 65,
                    is_exact: {
                        headerName: "Is Exact",
                    },
                },
                actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                columns: [
                    "keyword_name",
                    "program_type",
                    "is_exact",
                    "impression_share_rank",
                    "impression_share",
                    "page1OverallSov",
                    "page1OrganicSov",
                    "total_impression",
                    "top_product",
                    "rank",
                    "impression",
                    "click",
                    "spend",
                    "orders",
                    "sale",
                    "cpc",
                    "cr",
                    "cpa",
                    "acos",
                    "roas",
                    // New averages near pricing/availability
                    // "price",
                    "avg_price",
                    // "availability_percent",
                    "weighted_availability",
                    "cpm",
                    "campaigns",
                    "ctr",
                ],
                subGrids: {
                    keywords: {
                        columns: [
                            "keyword_name",
                            "bid",
                            "state",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "match_type",
                            "record_type",
                            "program_type",
                            "campaign_name",
                            "sys_campaign_id",
                            "cpm",
                            "targetingtype",
                        ],
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            canChangeKeywordStatus: true,
                            bid: {
                                cellRenderer: UpdateBidOrBudgetCellRenderer,
                                minWidth: 150,
                                maxWidth: 170,
                            },
                        },
                    },
                    addAsExact: {
                        columns: [
                            "keyword_name",
                            "cpc",
                            "campaign_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cr",
                            "cpa",
                            "acos",
                            "program_type",
                            "roas",
                            "troas",
                            "total_clicked_ad_sales",
                        ],
                        gridParams: {
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            isBidRequired: true,
                            doesAddToExact: true,
                            rowHeight: 65,
                            bidColumn: "cpm",
                            keywordColumn: "keyword_name",
                            isRemoveSkuAllowed: false,
                            cpm: {
                                headerName: "Suggested CPM",
                                minWidth: 170,
                                maxWidth: 180,
                            },
                        },
                    },
                },
                features: {
                    [Features.BULK_TGT_ACTION]: {
                        name: "Bulk Target Actions",
                    },
                    [Features.ADD_AS_EXACT]: {
                        name: "Add As Exact",
                    },
                },
            },
            [ParentModule.PRODUCT_ANALYTICS]: {
                name: "Product Analytics",
                isSubheaderVisible: true,
                actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                columns: [
                    "sku_title",
                    "asin",
                    "",
                    "isFocusedAsin",
                    "is_available",
                    // New metrics
                    "sov_overall_pct",
                    "sov_organic_pct",
                    "sov_paid_pct",
                    "ms_by_price_pct",
                    "ms_by_mrp_pct",
                    "click",
                    "spend",
                    "impression",
                    "orders",
                    "sale",
                    "cpc",
                    "cr",
                    "cpa",
                    "acos",
                    "roas",
                    // New averages near pricing/availability
                    // "price",
                    "avg_price",
                    // "availability_percent",
                    "weighted_availability",
                    "cpm",
                    "tag_types",
                    "sku_tags",
                    "total_clicked_ad_sales",
                    "total_view_ad_units",
                    "total_clicked_ad_units",
                    "aov",
                    "troas",
                    "ctr",
                ],
                gridParams: {
                    columnsToHide: ["asin", "is_available", "cpc", "cr", "cpa"],
                },
                subGrids: {
                    keywords: {
                        columns: [
                            "keyword_name",
                            "bid",
                            "state",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "match_type",
                            "record_type",
                            "program_type",
                            "campaign_name",
                            "cpm",
                            "targetingtype",
                        ],
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            bid: {
                                cellRenderer: UpdateBidOrBudgetCellRenderer,
                                minWidth: 150,
                                maxWidth: 170,
                            },
                        },
                    },
                },
                features: {
                    [Features.TAGS_ADDITION]: {
                        name: "Add Tags",
                    },
                },
            },
            [ParentModule.PRODUCT_DEEPDIVE]: {
                name: "Product Deepdive",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.NEGATIVE_KEYWORDS]: {
                name: "Negative Keywords",
                isSubheaderVisible: true,
                subModules: {
                    [SubModule.SUGGESTED_NEG_KWS]: {
                        name: "Suggested Negative Keywords",
                        slug: "suggested",
                        actions: [Actions.ADD_AS_NEGATIVE],
                        columns: [
                            "customer_search_term",
                            "",
                            "adgroup_type",
                            "nature",
                            "program_type",
                            "campaign_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "total_clicked_ad_sales",
                            "troas",
                        ],
                        gridParams: {
                            program_type: {
                                minWidth: 120,
                                maxWidth: 120,
                            },
                            cpc: {
                                headerName: "CPATC",
                            },
                            click: {
                                headerName: "Direct ATC",
                            },
                        },
                        features: {
                            [Features.ADD_NEGATIVE]: {
                                name: "Add Negatives",
                                actions: [Actions.ADD_AS_NEGATIVE],
                            },
                            [Features.RULE]: {
                                name: "Save Actions",
                                actions: [Actions.MARK_NEGATIVE],
                            },
                        },
                    },
                    [SubModule.EXISTING_NEG_KWS]: {
                        name: "Existing Negative Keywords",
                        slug: "existing",
                        actions: [Actions.REMOVE_NEGATIVE],
                        columns: [
                            "keyword_name",
                            "",
                            "match_type",
                            "record_type",
                            "program_type",
                            "campaign_name",
                        ],
                        gridParams: {
                            program_type: {
                                minWidth: 120,
                                maxWidth: 120,
                            },
                        },
                        features: {
                            [Features.REMOVE_NEGATIVE]: {
                                name: "Remove Negatives",
                                actions: [Actions.REMOVE_NEGATIVE],
                            },
                        },
                    },
                },
            },
            [ParentModule.COMMON_REPORTS]: {
                name: "Common Reports",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.REVIEW_ANALYTICS]: {
                name: "Review Analytics",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.REVSTACK]: {
                name: "Custom Reports",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.NAVIGATOR]: {
                name: "Navigator",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.CATEGORY_ANALYSIS]: {
                name: "Category Analysis",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.PRODUCT_ANALYSIS]: {
                name: "Product Analysis",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.CHANGE_LOG]: {
                name: "History",
                isSubheaderVisible: true,
                actions: [],
                columns: [
                    "log_date_iso",
                    "log_date",
                    "log_date",
                    "revert_action",
                    "module",
                    "change_type",
                    "record_name",
                    "record_type",
                    "attribute",
                    "old_value",
                    "new_value",
                    "source",
                    "nature",
                    "source_name",
                    "source_id",
                    "log_time",
                ],
                features: {
                    [Features.REVERT_ACTION]: {
                        name: "Revert Action",
                    },
                },
            },
            [ParentModule.REPORTS_BUILDER]: {
                name: "Reports Builder",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.SETTINGS]: {
                name: "Settings",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {
                    // [Features.BULK_PRODUCT_MAPPING]: {
                    //     name: "Bulk Mapping",
                    // },
                    [Features.ADD_MAPPING]: {
                        name: "Add Mapping",
                    },
                    [Features.APPLY_1DS_MAPPING]: {
                        name: "Apply 1DS Mapping",
                    },
                },
                additionalFeatures: {
                    "buybox-add-sellers": false,
                },
            },
            [ParentModule.ONEDER]: {
                name: "1der ai",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
                additionalFeatures: {},
            },
            [ParentModule.CHATBOT]: {
                name: "Ask AI",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
                additionalFeatures: {},
            },
            [ParentModule.ADMIN_SETTINGS]: {
                name: "Admin Settings",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
        },
        dropdownOptions: {
            targetTypeOptions: [],
            matchTypeOptions: [
                { value: "broad", label: "Broad" },
                { value: "exact", label: "Exact" },
                { value: "phrase", label: "Phrase" },
            ],
            negativeMatchTypeOptions: [
                { value: "exact", label: "Exact" },
                { value: "phrase", label: "Phrase" },
            ],
            programTypeOptions: [
                { value: "sp", label: "SP" },
                { value: "sd", label: "SD" },
            ],
            kaCampaignTypeOptions: [{ value: "manual", label: "Manual" }],
            chartFilterOptions: [
                { value: "sp", label: "SP" },
                { value: "sd", label: "SD   " },
                { value: "all", label: "All" },
            ],
        },
    },
    [ChannelName.INSTAMART]: {
        options: {
            decimalValue: 0,
            recommendedBid: 50,
            isTargetCheckRequired: false,
            isSmartInfoRequired: false,
            formatText: {
                record_type: {
                    keyword: "Keyword",
                    target: "Category",
                },
                targetingtype: {
                    CAMPAIGN_AD_TYPE_BANNER: "Banner",
                    CAMPAIGN_AD_TYPE_SPONSORED_COLLECTION:
                        "Sponsored Collection",
                    CAMPAIGN_AD_TYPE_SPONSORED_PRODUCT: "Sponsored Product",
                },
                program_type: {
                    ki: "Keyword Item",
                    kb: "Keyword Banner",
                    ib: "Item Boost",
                    ba: "Banner",
                    sc: "Speciality Collection",
                    gts: "Guaranteed Top Slot",
                },
            },
            platformFeatures: [
                Features.DAY_PARTING,
                Features.REVSTACK,
                Features.CATEGORY_ANALYSIS,
                Features.PRODUCT_ANALYSIS,
                Features.NAVIGATOR,
            ],
        },
        modules: {
            [ParentModule.PERFORMANCE_OVERVIEW]: {
                name: "Performance Overview",
                isSubheaderVisible: true,
                subModules: {
                    [SubModule.OVERVIEW]: {
                        name: "Overview",
                        actions: [],
                        columns: [],
                        features: {},
                        overviewGrids: {
                            tagTypeAggregate: {
                                columns: [
                                    "tag",
                                    "spend",
                                    "spend_share",
                                    "sale",
                                    "sale_share",
                                    "click",
                                    "orders",
                                    "roas",
                                    "impression",
                                    "total_clicked_ad_sales",
                                    "troas",
                                ],
                            },
                        },
                    },
                    [SubModule.CAMPAIGN]: {
                        name: "Campaigns",
                        gridParams: {
                            budget: {
                                minWidth: 370,
                                maxWidth: 380,
                            },
                            program_type: {
                                minWidth: 180,
                                maxWidth: 190,
                            },
                            campaign_name: {
                                minWidth: 250,
                                maxWidth: 270,
                            },
                            cost_type: {
                                minWidth: 180,
                                maxWidth: 190,
                            },
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            isCpmChangeAllowed: false,
                            isBudgetReduceAllowed: true,
                            isDateChangeAllowed: true,
                            isDateReduceAllowed: true,
                            isArchivedCampaignsSelectionAllowed: false,
                        },
                        componentConfig: {
                            till_budget_ends: {
                                label: "No End Date",
                                isVisible: true,
                            },
                            end_date: {
                                isVisible: true,
                            },
                            equally_divide_budget: {
                                payloadKey: "pacing",
                                label: "Ad Pacing",
                                isVisible: true,
                                showForTypes: [
                                    ProgramType["Keyword Item"],
                                    ProgramType["Item Boost"],
                                ],
                            },
                            cpm: {
                                isVisible: false,
                            },
                        },
                        actions: [Actions.BUDGET_CHANGE],
                        columns: [
                            "campaign_name",
                            "budget",
                            "cost_type",
                            "budget_type",
                            "record_type",
                            "program_type",
                            "automation",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "sys_campaign_id",
                            "campaign_status",
                            "campaign_tags",
                            "tag_types",
                            "cpm",
                            "targetingtype",
                            "total_clicked_ad_sales",
                            "troas",
                            "ctr",
                            "budget_utilization",
                            "campaign_uptime",
                        ],
                        features: {
                            [Features.BULK_ACTION]: {
                                name: "Bulk Taxonomy",
                            },
                            [Features.DATE_CHANGE]: {
                                name: "Change End Date",
                            },
                            [Features.TAGS_ADDITION]: {
                                name: "Add Tags",
                            },
                            [Features.BUDGET]: {
                                name: "Update Budget",
                                actions: [Actions.BUDGET_CHANGE],
                            },
                        },
                    },
                    [SubModule.KEYWORD]: {
                        name: "Keywords",
                        gridParams: {
                            bid: {
                                cellRenderer: PopOverBidCellRenderer,
                                minWidth: 150,
                                maxWidth: 160,
                                filterOptions: [
                                    "equals",
                                    "notEqual",
                                    "greaterThan",
                                    "lessThan",
                                    "greaterThanOrEqual",
                                    "lessThanOrEqual",
                                    "greaterThanSuggestedMin",
                                    "greaterThanSuggestedMax",
                                    "lessThanSuggestedMin",
                                    "lessThanSuggestedMax",
                                ],
                                maxBidLimitRecordTypes: [],
                                canBidInputBeDisabled: true,
                                isSlotChangeRequired: false,
                                bidType: "Exact",
                            },
                            isKeywordPauseAllowed: false,
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            rowHeight: 65,
                        },
                        actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                        columns: [
                            "keyword_name",
                            "is_archived_entity",
                            "bid",
                            "state",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "program_type",
                            "campaign_name",
                            "sys_campaign_id",
                            "sys_ams_keyword_id",
                            "campaign_tags",
                            "tag_types",
                            "serving_status",
                            "cpm",
                            "record_type",
                            "targetingtype",
                            "match_type",
                            "total_clicked_ad_sales",
                            "troas",
                            "ctr",
                            "search_volume_monthly",
                        ],
                        features: {
                            [Features.ARCHIVE]: {
                                name: "Archive",
                                actions: [Actions.ARCHIVE],
                            },
                            [Features.BID]: {
                                name: "Update Bid",
                                actions: [
                                    Actions.BID_CHANGE_ABSOLUTE,
                                    Actions.INCREASE_BID_PERC,
                                    Actions.DECREASE_BID_PERC,
                                ],
                            },
                            [Features.RULE]: {
                                name: "Save Actions",
                                actions: [
                                    Actions.INCREASE_BID_PERC,
                                    Actions.DECREASE_BID_PERC,
                                    Actions.MOVE_TO_SUGG_MIN,
                                    Actions.MOVE_TO_SUGG_MAX,
                                    Actions.MOVE_TO_SUGG_AVG,
                                ],
                            },
                        },
                    },
                    [SubModule.PRODUCT]: {
                        name: "Products",
                        actions: [],
                        columns: [
                            "sku_title",
                            "campaign_name",
                            "program_type",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "sys_campaign_id",
                            "asin_id",
                            "total_clicked_ad_sales",
                            "troas",
                        ],
                        features: {},
                        gridParams: {
                            rowHeight: 65,
                        },
                    },
                },
            },
            [ParentModule.RULES]: {
                name: "Rules",
                isSubheaderVisible: false,
                // Arr of columns to show (keys:string to be used in column defs)
                columns: [
                    "rule_id",
                    "name",
                    "type",
                    "filter",
                    "module",
                    "cron_string",
                    "created_at",
                    "created_by",
                    "updated_at",
                    "updated_by",
                    "status",
                    "",
                ],
                features: {},
            },
            [ParentModule.KEYWORD_ANALYTICS]: {
                name: "Keyword Analytics",
                isSubheaderVisible: true,
                gridParams: {
                    isKeywordLinkRequired: true,
                    isAdgroupSelectionRequired: true,
                    isBidRequired: true,
                    isMatchTypeReviewRequired: true,
                    isNegativeAdditionAllowed: false,
                    isMatchTypeSelectionRequired: true,
                    columnsToHide: [
                        "page1OrganicSov",
                        "impression_share",
                        "total_impression",
                        "rank",
                        "is_exact",
                    ],
                    rowHeight: 65,
                    is_exact: {
                        headerName: "Is Exact",
                    },
                },
                actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                columns: [
                    "keyword_name",
                    "program_type",
                    "is_exact",
                    "impression_share_rank",
                    "impression_share",
                    "page1OverallSov",
                    "page1OrganicSov",
                    "total_impression",
                    "top_product",
                    "rank",
                    "impression",
                    "click",
                    "spend",
                    "orders",
                    "sale",
                    "cpc",
                    "cr",
                    "cpa",
                    "acos",
                    "roas",
                    // New averages near pricing/availability
                    // "price",
                    "avg_price",
                    // "availability_percent",
                    "weighted_availability",
                    "cpm",
                    "campaigns",
                    "campaign_nos",
                    "total_clicked_ad_sales",
                    "troas",
                    "ctr",
                ],
                subGrids: {
                    keywords: {
                        columns: [
                            "keyword_name",
                            "bid",
                            "state",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "match_type",
                            "record_type",
                            "program_type",
                            "campaign_name",
                            "sys_campaign_id",
                            "cpm",
                            "targetingtype",
                        ],
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            canChangeKeywordStatus: true,
                            bid: {
                                cellRenderer: PopOverBidCellRenderer,
                                minWidth: 150,
                                maxWidth: 160,
                                maxBidLimitRecordTypes: [],
                                canBidInputBeDisabled: true,
                                isSlotChangeRequired: false,
                                bidType: "Exact",
                            },
                            isKeywordPauseAllowed: false,
                        },
                    },
                    campaigns: {
                        columns: [
                            "customer_search_term",
                            "keyword_name",
                            "cpm",
                            "campaign_name",
                            "spend",
                            "orders",
                            "sale",
                            "impression",
                            "click",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "total_clicked_ad_sales",
                            "troas",
                        ],
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isBidRequired: true,
                            doesAddToExact: true,
                            bidColumn: "cpc",
                            keywordColumn: "keyword_name",
                            isRemoveSkuAllowed: false,
                            cpc: {
                                headerName: "Suggested CPC",
                                minWidth: 170,
                                maxWidth: 180,
                            },
                        },
                    },
                },
                features: {
                    [Features.BULK_TGT_ACTION]: {
                        name: "Bulk Target Actions",
                    },
                },
            },
            [ParentModule.PRODUCT_ANALYTICS]: {
                name: "Product Analytics",
                isSubheaderVisible: true,
                actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                columns: [
                    "sku_title",
                    "asin",
                    "",
                    "isFocusedAsin",
                    "is_available",
                    // New metrics
                    "sov_overall_pct",
                    "sov_organic_pct",
                    "sov_paid_pct",
                    "ms_by_price_pct",
                    "ms_by_mrp_pct",
                    "click",
                    "spend",
                    "impression",
                    "orders",
                    "sale",
                    "cpc",
                    "cr",
                    "cpa",
                    "acos",
                    "roas",
                    // New averages near pricing/availability
                    // "price",
                    "avg_price",
                    // "availability_percent",
                    "weighted_availability",
                    "cpm",
                    "tag_types",
                    "sku_tags",
                    "total_clicked_ad_sales",
                    "troas",
                    "ctr",
                ],
                gridParams: {
                    columnsToHide: [
                        "asin",
                        "is_available",
                        "cpc",
                        "cr",
                        "cpa",
                        "orders",
                    ],
                },
                subGrids: {
                    keywords: {
                        columns: [
                            "keyword_name",
                            "bid",
                            "state",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "match_type",
                            "record_type",
                            "program_type",
                            "campaign_name",
                            "cpm",
                            "targetingtype",
                            "total_clicked_ad_sales",
                            "troas",
                        ],
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isKeywordPauseAllowed: false,
                            isTypeBadgeRequired: true,
                            bid: {
                                cellRenderer: PopOverBidCellRenderer,
                                minWidth: 150,
                                maxWidth: 160,
                                maxBidLimitRecordTypes: [],
                                canBidInputBeDisabled: true,
                                isSlotChangeRequired: false,
                                bidType: "Exact",
                            },
                        },
                    },
                },
                features: {
                    [Features.TAGS_ADDITION]: {
                        name: "Add Tags",
                    },
                },
            },
            [ParentModule.PRODUCT_DEEPDIVE]: {
                name: "Product Deepdive",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.COMMON_REPORTS]: {
                name: "Common Reports",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.REVIEW_ANALYTICS]: {
                name: "Review Analytics",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.REVSTACK]: {
                name: "Custom Reports",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.NAVIGATOR]: {
                name: "Navigator",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.CATEGORY_ANALYSIS]: {
                name: "Category Analysis",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.PRODUCT_ANALYSIS]: {
                name: "Product Analysis",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.CHANGE_LOG]: {
                name: "History",
                isSubheaderVisible: true,
                actions: [],
                columns: [
                    "log_date_iso",
                    "log_date",
                    "log_date",
                    "revert_action",
                    "module",
                    "change_type",
                    "record_name",
                    "record_type",
                    "attribute",
                    "old_value",
                    "new_value",
                    "source",
                    "nature",
                    "source_name",
                    "source_id",
                    "log_time",
                ],
                features: {
                    [Features.REVERT_ACTION]: {
                        name: "Revert Action",
                    },
                },
            },
            [ParentModule.REPORTS_BUILDER]: {
                name: "Reports Builder",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.SETTINGS]: {
                name: "Settings",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {
                    // [Features.BULK_PRODUCT_MAPPING]: {
                    //     name: "Bulk Mapping",
                    // },
                    [Features.ADD_MAPPING]: {
                        name: "Add Mapping",
                    },
                    [Features.APPLY_1DS_MAPPING]: {
                        name: "Apply 1DS Mapping",
                    },
                },
                additionalFeatures: {
                    "buybox-add-sellers": false,
                },
            },
            [ParentModule.ONEDER]: {
                name: "1der ai",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
                additionalFeatures: {},
            },
            [ParentModule.CHATBOT]: {
                name: "Ask AI",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
                additionalFeatures: {},
            },
            [ParentModule.ADMIN_SETTINGS]: {
                name: "Admin Settings",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
        },
        dropdownOptions: {
            targetTypeOptions: [],
            matchTypeOptions: [
                { value: "broad", label: "Broad" },
                { value: "exact", label: "Exact" },
            ],
            negativeMatchTypeOptions: [{ value: "exact", label: "Exact" }],
            programTypeOptions: [
                { value: "ki", label: "Keyword Item" },
                { value: "kb", label: "Keyword Banner" },
            ],
            kaCampaignTypeOptions: [{ value: "manual", label: "Manual" }],
            chartFilterOptions: [
                { value: "ki", label: "Keyword Item" },
                { value: "kb", label: "Keyword Banner" },
                { value: "ib", label: "Item Boost" },
                { value: "ba", label: "Banner" },
                { value: "sc", label: "Speciality Collection" },
                { value: "all", label: "All" },
            ],
        },
    },
    [ChannelName.TAKEALOT]: {
        options: {
            decimalValue: 0,
            recommendedBid: 1,
            isTargetCheckRequired: false,
            isSmartInfoRequired: false,
            formatText: {
                program_type: {
                    a: "Awareness",
                    p: "Performance",
                },
                record_type: {
                    keyword: "Keyword",
                    target: "Category",
                },
                targetingtype: {
                    AUCTION: "Auction",
                    OS_ADS_SEARCH: "Search Ads",
                    SMART_SHOPPING: "Smart Shopping",
                },
            },
            platformFeatures: [
                Features.DAY_PARTING,
                Features.REVSTACK,
                Features.CATEGORY_ANALYSIS,
                Features.PRODUCT_ANALYSIS,
                Features.NAVIGATOR,
            ],
        },
        modules: {
            [ParentModule.PERFORMANCE_OVERVIEW]: {
                name: "Performance Overview",
                isSubheaderVisible: true,
                subModules: {
                    [SubModule.OVERVIEW]: {
                        name: "Overview",
                        actions: [],
                        columns: [],
                        features: {},
                        overviewGrids: {
                            tagTypeAggregate: {
                                columns: [
                                    "tag",
                                    "spend",
                                    "spend_share",
                                    "sale",
                                    "sale_share",
                                    "click",
                                    "orders",
                                    "roas",
                                    "impression",
                                ],
                            },
                        },
                    },
                    [SubModule.CAMPAIGN]: {
                        name: "Campaigns",
                        gridParams: {
                            budget: {
                                minWidth: 370,
                                maxWidth: 380,
                            },
                            program_type: {
                                minWidth: 120,
                                maxWidth: 120,
                            },
                            campaign_name: {
                                minWidth: 290,
                                maxWidth: 310,
                            },
                            cost_type: {
                                minWidth: 180,
                                maxWidth: 190,
                            },
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            isCpmChangeAllowed: true,
                            isBudgetReduceAllowed: true,
                            isDateChangeAllowed: true,
                            isDateReduceAllowed: true,
                            cpmChangeAllowedTypes: [
                                ProgramType.SP,
                                ProgramType.Awareness,
                            ],
                            isArchivedCampaignsSelectionAllowed: false,
                        },
                        componentConfig: {
                            till_budget_ends: {
                                label: "No End Date",
                                isVisible: false,
                            },
                            end_date: {
                                isVisible: true,
                                disabledTypes: [ProgramType.Performance],
                            },
                            equally_divide_budget: {
                                isVisible: false,
                            },
                            cpm: {
                                isVisible: true,
                                label: "CPM",
                            },
                        },
                        actions: [
                            Actions.STATUS_CHANGE,
                            Actions.BUDGET_CHANGE,
                            Actions.NAME_CHANGE,
                        ],
                        columns: [
                            "campaign_name",
                            "state",
                            "budget",
                            "record_type",
                            "program_type",
                            "automation",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "sys_campaign_id",
                            "campaign_status",
                            "campaign_tags",
                            "tag_types",
                            "cpm",
                            "targetingtype",
                        ],
                        features: {
                            [Features.BULK_ACTION]: {
                                name: "Bulk Taxonomy",
                            },
                            [Features.DAY_PARTING]: {
                                name: "Day Parting",
                            },
                            [Features.PAUSE]: {
                                name: "Pause",
                                actions: [Actions.PAUSE],
                            },
                            [Features.ENABLE]: {
                                name: "Enable",
                                actions: [Actions.ENABLE],
                            },
                            [Features.DATE_CHANGE]: {
                                name: "Change End Date",
                            },
                            [Features.TAGS_ADDITION]: {
                                name: "Add Tags",
                            },
                            [Features.BUDGET]: {
                                name: "Update Budget",
                                actions: [Actions.BUDGET_CHANGE],
                            },
                        },
                    },
                    [SubModule.KEYWORD]: {
                        name: "Keywords",
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            bid: {
                                cellRenderer: UpdateBidOrBudgetCellRenderer,
                                minWidth: 150,
                                maxWidth: 170,
                                filterOptions: [
                                    "equals",
                                    "notEqual",
                                    "greaterThan",
                                    "lessThan",
                                    "greaterThanOrEqual",
                                    "lessThanOrEqual",
                                ],
                            },
                        },
                        actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                        columns: [
                            "keyword_name",
                            "is_archived_entity",
                            "bid",
                            "state",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "program_type",
                            "campaign_name",
                            "sys_campaign_id",
                            "sys_ams_keyword_id",
                            "campaign_tags",
                            "tag_types",
                            "percent_utilisation",
                            "serving_status",
                            "cpm",
                            "record_type",
                            "targetingtype",
                            "match_type",
                        ],
                        features: {
                            [Features.PAUSE]: {
                                name: "Pause",
                                actions: [Actions.PAUSE],
                            },
                            [Features.ENABLE]: {
                                name: "Enable",
                                actions: [Actions.ENABLE],
                            },
                            [Features.BID]: {
                                name: "Update Bid",
                                actions: [
                                    Actions.BID_CHANGE_ABSOLUTE,
                                    Actions.INCREASE_BID_PERC,
                                    Actions.DECREASE_BID_PERC,
                                ],
                            },
                            [Features.RULE]: {
                                name: "Save Actions",
                                actions: [
                                    Actions.INCREASE_BID_PERC,
                                    Actions.DECREASE_BID_PERC,
                                ],
                            },
                        },
                    },
                    [SubModule.PRODUCT]: {
                        name: "Products",
                        actions: [],
                        columns: [
                            "sku_title",
                            "campaign_name",
                            "program_type",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "sys_campaign_id",
                            "asin_id",
                        ],
                        features: {},
                        gridParams: {
                            rowHeight: 65,
                        },
                    },
                },
            },
            [ParentModule.RULES]: {
                name: "Rules",
                isSubheaderVisible: false,
                // Arr of columns to show (keys:string to be used in column defs)
                columns: [
                    "rule_id",
                    "name",
                    "type",
                    "filter",
                    "module",
                    "cron_string",
                    "created_at",
                    "created_by",
                    "updated_at",
                    "updated_by",
                    "status",
                    "",
                ],
                features: {},
            },
            [ParentModule.KEYWORD_ANALYTICS]: {
                name: "Keyword Analytics",
                isSubheaderVisible: true,
                gridParams: {
                    isKeywordLinkRequired: true,
                    isAdgroupSelectionRequired: false,
                    isBidRequired: true,
                    isMatchTypeReviewRequired: false,
                    isNegativeAdditionAllowed: true,
                    isMatchTypeSelectionRequired: true,
                    columnsToHide: [
                        "page1OrganicSov",
                        "impression_share",
                        "total_impression",
                        "rank",
                        "is_exact",
                    ],
                    rowHeight: 65,
                    is_exact: {
                        headerName: "Is Exact",
                    },
                },
                actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                columns: [
                    "keyword_name",
                    "program_type",
                    "is_exact",
                    "impression_share_rank",
                    "impression_share",
                    "page1OverallSov",
                    "page1OrganicSov",
                    "total_impression",
                    "top_product",
                    "rank",
                    "impression",
                    "click",
                    "spend",
                    "orders",
                    "sale",
                    "cpc",
                    "cr",
                    "cpa",
                    "acos",
                    "roas",
                    // New averages near pricing/availability
                    // "price",
                    "avg_price",
                    // "availability_percent",
                    "weighted_availability",
                    "cpm",
                    "campaigns",
                ],
                subGrids: {
                    keywords: {
                        columns: [
                            "keyword_name",
                            "bid",
                            "state",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "match_type",
                            "record_type",
                            "program_type",
                            "campaign_name",
                            "sys_campaign_id",
                            "cpm",
                            "targetingtype",
                        ],
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            canChangeKeywordStatus: true,
                            bid: {
                                cellRenderer: UpdateBidOrBudgetCellRenderer,
                                minWidth: 150,
                                maxWidth: 170,
                            },
                        },
                    },
                },
                features: {
                    [Features.BULK_TGT_ACTION]: {
                        name: "Bulk Target Actions",
                    },
                },
            },
            [ParentModule.PRODUCT_ANALYTICS]: {
                name: "Product Analytics",
                isSubheaderVisible: true,
                actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                columns: [
                    "sku_title",
                    "asin",
                    "",
                    "isFocusedAsin",
                    "is_available",
                    // New metrics
                    "sov_overall_pct",
                    "sov_organic_pct",
                    "sov_paid_pct",
                    "ms_by_price_pct",
                    "ms_by_mrp_pct",
                    "click",
                    "spend",
                    "impression",
                    "orders",
                    "sale",
                    "cpc",
                    "cr",
                    "cpa",
                    "acos",
                    "roas",
                    // New averages near pricing/availability
                    // "price",
                    "avg_price",
                    // "availability_percent",
                    "weighted_availability",
                    "cpm",
                    "tag_types",
                    "sku_tags",
                ],
                gridParams: {
                    columnsToHide: ["asin", "is_available", "cpc", "cr", "cpa"],
                },
                subGrids: {
                    keywords: {
                        columns: [
                            "keyword_name",
                            "bid",
                            "state",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "match_type",
                            "record_type",
                            "program_type",
                            "campaign_name",
                            "cpm",
                            "targetingtype",
                        ],
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            bid: {
                                cellRenderer: UpdateBidOrBudgetCellRenderer,
                                minWidth: 150,
                                maxWidth: 170,
                            },
                        },
                    },
                },
                features: {
                    [Features.TAGS_ADDITION]: {
                        name: "Add Tags",
                    },
                },
            },
            [ParentModule.PRODUCT_DEEPDIVE]: {
                name: "Product Deepdive",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.NEGATIVE_KEYWORDS]: {
                name: "Negative Keywords",
                isSubheaderVisible: true,
                subModules: {
                    [SubModule.SUGGESTED_NEG_KWS]: {
                        name: "Suggested Negative Keywords",
                        slug: "suggested",
                        actions: [Actions.ADD_AS_NEGATIVE],
                        columns: [
                            "customer_search_term",
                            "",
                            "adgroup_type",
                            "nature",
                            "program_type",
                            "campaign_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "total_clicked_ad_sales",
                            "troas",
                        ],
                        gridParams: {
                            program_type: {
                                minWidth: 120,
                                maxWidth: 120,
                            },
                            cpc: {
                                headerName: "CPATC",
                            },
                            click: {
                                headerName: "Direct ATC",
                            },
                        },
                        features: {
                            [Features.ADD_NEGATIVE]: {
                                name: "Add Negatives",
                                actions: [Actions.ADD_AS_NEGATIVE],
                            },
                            [Features.RULE]: {
                                name: "Save Actions",
                                actions: [Actions.MARK_NEGATIVE],
                            },
                        },
                    },
                    [SubModule.EXISTING_NEG_KWS]: {
                        name: "Existing Negative Keywords",
                        slug: "existing",
                        actions: [Actions.REMOVE_NEGATIVE],
                        columns: [
                            "keyword_name",
                            "",
                            "match_type",
                            "record_type",
                            "program_type",
                            "campaign_name",
                        ],
                        gridParams: {
                            program_type: {
                                minWidth: 120,
                                maxWidth: 120,
                            },
                        },
                        features: {
                            [Features.REMOVE_NEGATIVE]: {
                                name: "Remove Negatives",
                                actions: [Actions.REMOVE_NEGATIVE],
                            },
                        },
                    },
                },
            },
            [ParentModule.COMMON_REPORTS]: {
                name: "Common Reports",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.REVIEW_ANALYTICS]: {
                name: "Review Analytics",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.REVSTACK]: {
                name: "Custom Reports",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.NAVIGATOR]: {
                name: "Navigator",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.CATEGORY_ANALYSIS]: {
                name: "Category Analysis",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.PRODUCT_ANALYSIS]: {
                name: "Product Analysis",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.CHANGE_LOG]: {
                name: "History",
                isSubheaderVisible: true,
                actions: [],
                columns: [
                    "log_date_iso",
                    "log_date",
                    "log_date",
                    "revert_action",
                    "module",
                    "change_type",
                    "record_name",
                    "record_type",
                    "attribute",
                    "old_value",
                    "new_value",
                    "source",
                    "nature",
                    "source_name",
                    "source_id",
                    "log_time",
                ],
                features: {
                    [Features.REVERT_ACTION]: {
                        name: "Revert Action",
                    },
                },
            },
            [ParentModule.REPORTS_BUILDER]: {
                name: "Reports Builder",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.SETTINGS]: {
                name: "Settings",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {
                    // [Features.BULK_PRODUCT_MAPPING]: {
                    //     name: "Bulk Mapping",
                    // },
                    [Features.ADD_MAPPING]: {
                        name: "Add Mapping",
                    },
                    [Features.APPLY_1DS_MAPPING]: {
                        name: "Apply 1DS Mapping",
                    },
                },
                additionalFeatures: {
                    "buybox-add-sellers": false,
                },
            },
            [ParentModule.ONEDER]: {
                name: "1der ai",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
                additionalFeatures: {},
            },
            [ParentModule.CHATBOT]: {
                name: "Ask AI",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
                additionalFeatures: {},
            },
            [ParentModule.ADMIN_SETTINGS]: {
                name: "Admin Settings",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
        },
        dropdownOptions: {
            targetTypeOptions: [],
            matchTypeOptions: [
                { value: "broad", label: "Broad" },
                { value: "exact", label: "Exact" },
                { value: "phrase", label: "Phrase" },
            ],
            negativeMatchTypeOptions: [
                { value: "exact", label: "Exact" },
                { value: "phrase", label: "Phrase" },
            ],
            programTypeOptions: [
                { value: "p", label: "Performance" },
                { value: "a", label: "Awareness" },
            ],
            kaCampaignTypeOptions: [{ value: "manual", label: "Manual" }],
            chartFilterOptions: [
                { value: "p", label: "Performance" },
                { value: "a", label: "Awareness" },
                { value: "all", label: "All" },
            ],
        },
    },
    [ChannelName.PURPLLE]: {
        options: {
            decimalValue: 0,
            recommendedBid: 10,
            isTargetCheckRequired: false,
            isSmartInfoRequired: false,
            formatText: {
                program_type: {
                    a: "Awareness",
                    p: "Performance",
                },
                record_type: {
                    keyword: "Keyword",
                    target: "Category",
                },
                targetingtype: {
                    AUCTION: "Auction",
                    OS_ADS_SEARCH: "Search Ads",
                    SMART_SHOPPING: "Smart Shopping",
                },
            },
            platformFeatures: [
                Features.DAY_PARTING,
                Features.REVSTACK,
                Features.CATEGORY_ANALYSIS,
                Features.PRODUCT_ANALYSIS,
                Features.NAVIGATOR,
            ],
        },
        modules: {
            [ParentModule.PERFORMANCE_OVERVIEW]: {
                name: "Performance Overview",
                isSubheaderVisible: true,
                subModules: {
                    [SubModule.OVERVIEW]: {
                        name: "Overview",
                        actions: [],
                        columns: [],
                        features: {},
                        overviewGrids: {
                            tagTypeAggregate: {
                                columns: [
                                    "tag",
                                    "spend",
                                    "spend_share",
                                    "sale",
                                    "sale_share",
                                    "click",
                                    "orders",
                                    "roas",
                                    "impression",
                                ],
                            },
                        },
                    },
                    [SubModule.CAMPAIGN]: {
                        name: "Campaigns",
                        gridParams: {
                            budget: {
                                minWidth: 370,
                                maxWidth: 380,
                            },
                            program_type: {
                                minWidth: 120,
                                maxWidth: 120,
                            },
                            campaign_name: {
                                minWidth: 290,
                                maxWidth: 310,
                            },
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            isCpmChangeAllowed: true,
                            isBudgetReduceAllowed: true,
                            isDateChangeAllowed: true,
                            isDateReduceAllowed: true,
                            cpmChangeAllowedTypes: [
                                ProgramType.SP,
                                ProgramType.Awareness,
                            ],
                            isArchivedCampaignsSelectionAllowed: false,
                        },
                        componentConfig: {
                            till_budget_ends: {
                                label: "No End Date",
                                isVisible: false,
                            },
                            end_date: {
                                isVisible: true,
                                disabledTypes: [ProgramType.Performance],
                            },
                            equally_divide_budget: {
                                isVisible: false,
                            },
                            cpm: {
                                isVisible: true,
                                label: "CPM",
                            },
                        },
                        actions: [
                            Actions.STATUS_CHANGE,
                            Actions.BUDGET_CHANGE,
                            Actions.NAME_CHANGE,
                        ],
                        columns: [
                            "campaign_name",
                            "state",
                            "budget",
                            "record_type",
                            "program_type",
                            "automation",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "sys_campaign_id",
                            "campaign_status",
                            "campaign_tags",
                            "tag_types",
                            "cpm",
                            "targetingtype",
                        ],
                        features: {
                            [Features.BULK_ACTION]: {
                                name: "Bulk Taxonomy",
                            },
                            [Features.DAY_PARTING]: {
                                name: "Day Parting",
                            },
                            [Features.PAUSE]: {
                                name: "Pause",
                                actions: [Actions.PAUSE],
                            },
                            [Features.ENABLE]: {
                                name: "Enable",
                                actions: [Actions.ENABLE],
                            },
                            [Features.DATE_CHANGE]: {
                                name: "Change End Date",
                            },
                            [Features.TAGS_ADDITION]: {
                                name: "Add Tags",
                            },
                            [Features.BUDGET]: {
                                name: "Update Budget",
                                actions: [Actions.BUDGET_CHANGE],
                            },
                        },
                    },
                    [SubModule.KEYWORD]: {
                        name: "Keywords",
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            bid: {
                                cellRenderer: UpdateBidOrBudgetCellRenderer,
                                minWidth: 150,
                                maxWidth: 170,
                                filterOptions: [
                                    "equals",
                                    "notEqual",
                                    "greaterThan",
                                    "lessThan",
                                    "greaterThanOrEqual",
                                    "lessThanOrEqual",
                                ],
                            },
                        },
                        actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                        columns: [
                            "keyword_name",
                            "is_archived_entity",
                            "bid",
                            "state",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "program_type",
                            "campaign_name",
                            "sys_campaign_id",
                            "sys_ams_keyword_id",
                            "campaign_tags",
                            "tag_types",
                            "percent_utilisation",
                            "serving_status",
                            "cpm",
                            "record_type",
                            "targetingtype",
                            "match_type",
                        ],
                        features: {
                            [Features.PAUSE]: {
                                name: "Pause",
                                actions: [Actions.PAUSE],
                            },
                            [Features.ENABLE]: {
                                name: "Enable",
                                actions: [Actions.ENABLE],
                            },
                            [Features.BID]: {
                                name: "Update Bid",
                                actions: [
                                    Actions.BID_CHANGE_ABSOLUTE,
                                    Actions.INCREASE_BID_PERC,
                                    Actions.DECREASE_BID_PERC,
                                ],
                            },
                            [Features.RULE]: {
                                name: "Save Actions",
                                actions: [
                                    Actions.INCREASE_BID_PERC,
                                    Actions.DECREASE_BID_PERC,
                                ],
                            },
                        },
                    },
                    [SubModule.PRODUCT]: {
                        name: "Products",
                        actions: [],
                        columns: [
                            "sku_title",
                            "campaign_name",
                            "program_type",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "sys_campaign_id",
                            "asin_id",
                        ],
                        features: {},
                        gridParams: {
                            rowHeight: 65,
                        },
                    },
                },
            },
            [ParentModule.RULES]: {
                name: "Rules",
                isSubheaderVisible: false,
                // Arr of columns to show (keys:string to be used in column defs)
                columns: [
                    "rule_id",
                    "name",
                    "type",
                    "filter",
                    "module",
                    "cron_string",
                    "created_at",
                    "created_by",
                    "updated_at",
                    "updated_by",
                    "status",
                    "",
                ],
                features: {},
            },
            [ParentModule.KEYWORD_ANALYTICS]: {
                name: "Keyword Analytics",
                isSubheaderVisible: true,
                gridParams: {
                    isKeywordLinkRequired: true,
                    isAdgroupSelectionRequired: false,
                    isBidRequired: true,
                    isMatchTypeReviewRequired: false,
                    isNegativeAdditionAllowed: true,
                    isMatchTypeSelectionRequired: true,
                    columnsToHide: [
                        "page1OrganicSov",
                        "impression_share",
                        "total_impression",
                        "rank",
                        "is_exact",
                    ],
                    rowHeight: 65,
                    is_exact: {
                        headerName: "Is Exact",
                    },
                },
                actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                columns: [
                    "keyword_name",
                    "program_type",
                    "is_exact",
                    "impression_share_rank",
                    "impression_share",
                    "page1OverallSov",
                    "page1OrganicSov",
                    "total_impression",
                    "top_product",
                    "rank",
                    "impression",
                    "click",
                    "spend",
                    "orders",
                    "sale",
                    "cpc",
                    "cr",
                    "cpa",
                    "acos",
                    "roas",
                    // New averages near pricing/availability
                    // "price",
                    "avg_price",
                    // "availability_percent",
                    "weighted_availability",
                    "cpm",
                    "campaigns",
                ],
                subGrids: {
                    keywords: {
                        columns: [
                            "keyword_name",
                            "bid",
                            "state",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "match_type",
                            "record_type",
                            "program_type",
                            "campaign_name",
                            "sys_campaign_id",
                            "cpm",
                            "targetingtype",
                        ],
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            canChangeKeywordStatus: true,
                            bid: {
                                cellRenderer: UpdateBidOrBudgetCellRenderer,
                                minWidth: 150,
                                maxWidth: 170,
                            },
                        },
                    },
                },
                features: {
                    [Features.BULK_TGT_ACTION]: {
                        name: "Bulk Target Actions",
                    },
                },
            },
            [ParentModule.PRODUCT_ANALYTICS]: {
                name: "Product Analytics",
                isSubheaderVisible: true,
                actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                columns: [
                    "sku_title",
                    "asin",
                    "",
                    "isFocusedAsin",
                    "is_available",
                    // New metrics
                    "sov_overall_pct",
                    "sov_organic_pct",
                    "sov_paid_pct",
                    "ms_by_price_pct",
                    "ms_by_mrp_pct",
                    "click",
                    "spend",
                    "impression",
                    "orders",
                    "sale",
                    "cpc",
                    "cr",
                    "cpa",
                    "acos",
                    "roas",
                    // New averages near pricing/availability
                    // "price",
                    "avg_price",
                    // "availability_percent",
                    "weighted_availability",
                    "cpm",
                    "tag_types",
                    "sku_tags",
                ],
                gridParams: {
                    columnsToHide: ["asin", "is_available", "cpc", "cr", "cpa"],
                },
                subGrids: {
                    keywords: {
                        columns: [
                            "keyword_name",
                            "bid",
                            "state",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "match_type",
                            "record_type",
                            "program_type",
                            "campaign_name",
                            "cpm",
                            "targetingtype",
                        ],
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            bid: {
                                cellRenderer: UpdateBidOrBudgetCellRenderer,
                                minWidth: 150,
                                maxWidth: 170,
                            },
                        },
                    },
                },
                features: {
                    [Features.TAGS_ADDITION]: {
                        name: "Add Tags",
                    },
                },
            },
            [ParentModule.PRODUCT_DEEPDIVE]: {
                name: "Product Deepdive",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.NEGATIVE_KEYWORDS]: {
                name: "Negative Keywords",
                isSubheaderVisible: true,
                subModules: {
                    [SubModule.SUGGESTED_NEG_KWS]: {
                        name: "Suggested Negative Keywords",
                        slug: "suggested",
                        actions: [Actions.ADD_AS_NEGATIVE],
                        columns: [
                            "customer_search_term",
                            "",
                            "adgroup_type",
                            "nature",
                            "program_type",
                            "campaign_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "total_clicked_ad_sales",
                            "troas",
                        ],
                        gridParams: {
                            program_type: {
                                minWidth: 120,
                                maxWidth: 120,
                            },
                            cpc: {
                                headerName: "CPATC",
                            },
                            click: {
                                headerName: "Direct ATC",
                            },
                        },
                        features: {
                            [Features.ADD_NEGATIVE]: {
                                name: "Add Negatives",
                                actions: [Actions.ADD_AS_NEGATIVE],
                            },
                            [Features.RULE]: {
                                name: "Save Actions",
                                actions: [Actions.MARK_NEGATIVE],
                            },
                        },
                    },
                    [SubModule.EXISTING_NEG_KWS]: {
                        name: "Existing Negative Keywords",
                        slug: "existing",
                        actions: [Actions.REMOVE_NEGATIVE],
                        columns: [
                            "keyword_name",
                            "",
                            "match_type",
                            "record_type",
                            "program_type",
                            "campaign_name",
                        ],
                        gridParams: {
                            program_type: {
                                minWidth: 120,
                                maxWidth: 120,
                            },
                        },
                        features: {
                            [Features.REMOVE_NEGATIVE]: {
                                name: "Remove Negatives",
                                actions: [Actions.REMOVE_NEGATIVE],
                            },
                        },
                    },
                },
            },
            [ParentModule.COMMON_REPORTS]: {
                name: "Common Reports",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.REVIEW_ANALYTICS]: {
                name: "Review Analytics",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.REVSTACK]: {
                name: "Custom Reports",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.NAVIGATOR]: {
                name: "Navigator",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.CATEGORY_ANALYSIS]: {
                name: "Category Analysis",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.PRODUCT_ANALYSIS]: {
                name: "Product Analysis",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.CHANGE_LOG]: {
                name: "History",
                isSubheaderVisible: true,
                actions: [],
                columns: [
                    "log_date_iso",
                    "log_date",
                    "log_date",
                    "revert_action",
                    "module",
                    "change_type",
                    "record_name",
                    "record_type",
                    "attribute",
                    "old_value",
                    "new_value",
                    "source",
                    "nature",
                    "source_name",
                    "source_id",
                    "log_time",
                ],
                features: {
                    [Features.REVERT_ACTION]: {
                        name: "Revert Action",
                    },
                },
            },
            [ParentModule.REPORTS_BUILDER]: {
                name: "Reports Builder",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.SETTINGS]: {
                name: "Settings",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {
                    // [Features.BULK_PRODUCT_MAPPING]: {
                    //     name: "Bulk Mapping",
                    // },
                    [Features.ADD_MAPPING]: {
                        name: "Add Mapping",
                    },
                    [Features.APPLY_1DS_MAPPING]: {
                        name: "Apply 1DS Mapping",
                    },
                },
                additionalFeatures: {
                    "buybox-add-sellers": false,
                },
            },
            [ParentModule.ONEDER]: {
                name: "1der ai",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
                additionalFeatures: {},
            },
            [ParentModule.CHATBOT]: {
                name: "Ask AI",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
                additionalFeatures: {},
            },
            [ParentModule.ADMIN_SETTINGS]: {
                name: "Admin Settings",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
        },
        dropdownOptions: {
            targetTypeOptions: [],
            matchTypeOptions: [
                { value: "broad", label: "Broad" },
                { value: "exact", label: "Exact" },
                { value: "phrase", label: "Phrase" },
            ],
            negativeMatchTypeOptions: [
                { value: "exact", label: "Exact" },
                { value: "phrase", label: "Phrase" },
            ],
            programTypeOptions: [
                { value: "p", label: "Performance" },
                { value: "a", label: "Awareness" },
            ],
            kaCampaignTypeOptions: [{ value: "manual", label: "Manual" }],
            chartFilterOptions: [
                { value: "p", label: "Performance" },
                { value: "a", label: "Awareness" },
                { value: "all", label: "All" },
            ],
        },
    },
    [ChannelName.NOON]: {
        options: {
            decimalValue: 0,
            recommendedBid: 10,
            isTargetCheckRequired: false,
            isSmartInfoRequired: false,
            formatText: {
                record_type: {
                    keyword: "Keyword",
                    target: "Category",
                },
                program_type: {
                    sp: "SP",
                    sb: "SB",
                },
                targetingtype: {
                    product: "Product",
                    brand: "Brand",
                },
            },
            platformFeatures: [
                Features.DAY_PARTING,
                Features.REVSTACK,
                Features.CATEGORY_ANALYSIS,
                Features.PRODUCT_ANALYSIS,
                Features.NAVIGATOR,
            ],
        },
        modules: {
            [ParentModule.PERFORMANCE_OVERVIEW]: {
                name: "Performance Overview",
                isSubheaderVisible: true,
                subModules: {
                    [SubModule.OVERVIEW]: {
                        name: "Overview",
                        actions: [],
                        columns: [],
                        features: {},
                        overviewGrids: {
                            tagTypeAggregate: {
                                columns: [
                                    "tag",
                                    "spend",
                                    "spend_share",
                                    "sale",
                                    "sale_share",
                                    "click",
                                    "orders",
                                    "roas",
                                    "impression",
                                ],
                            },
                        },
                    },
                    [SubModule.CAMPAIGN]: {
                        name: "Campaigns",
                        gridParams: {
                            budget: {
                                minWidth: 370,
                                maxWidth: 380,
                            },
                            program_type: {
                                minWidth: 120,
                                maxWidth: 120,
                            },
                            campaign_name: {
                                minWidth: 290,
                                maxWidth: 310,
                            },
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            isCpmChangeAllowed: false,
                            isBudgetReduceAllowed: true,
                            isDateChangeAllowed: true,
                            isDateReduceAllowed: true,
                            isArchivedCampaignsSelectionAllowed: false,
                        },
                        componentConfig: {
                            till_budget_ends: {
                                label: "No End Date",
                                isVisible: true,
                            },
                            end_date: {
                                isVisible: true,
                                disabledTypes: [ProgramType.Performance],
                            },
                            equally_divide_budget: {
                                isVisible: false,
                            },
                            cpm: {
                                isVisible: false,
                            },
                        },
                        actions: [
                            Actions.STATUS_CHANGE,
                            Actions.BUDGET_CHANGE,
                            Actions.NAME_CHANGE,
                        ],
                        columns: [
                            "campaign_name",
                            "state",
                            "budget",
                            "record_type",
                            "program_type",
                            "automation",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "sys_campaign_id",
                            "campaign_status",
                            "campaign_tags",
                            "tag_types",
                            "targetingtype",
                        ],
                        features: {
                            [Features.BULK_ACTION]: {
                                name: "Bulk Taxonomy",
                            },
                            [Features.DAY_PARTING]: {
                                name: "Day Parting",
                            },
                            [Features.PAUSE]: {
                                name: "Pause",
                                actions: [Actions.PAUSE],
                            },
                            [Features.ENABLE]: {
                                name: "Enable",
                                actions: [Actions.ENABLE],
                            },
                            [Features.DATE_CHANGE]: {
                                name: "Change End Date",
                            },
                            [Features.TAGS_ADDITION]: {
                                name: "Add Tags",
                            },
                            [Features.BUDGET]: {
                                name: "Update Budget",
                                actions: [Actions.BUDGET_CHANGE],
                            },
                        },
                    },
                    [SubModule.KEYWORD]: {
                        name: "Keywords",
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            bid: {
                                cellRenderer: PopOverBidCellRenderer,
                                minWidth: 150,
                                maxWidth: 160,
                                filterOptions: [
                                    "equals",
                                    "notEqual",
                                    "greaterThan",
                                    "lessThan",
                                    "greaterThanOrEqual",
                                    "lessThanOrEqual",
                                ],
                                maxBidLimitRecordTypes: [
                                    RecordType.KEYWORD,
                                    RecordType.TARGET,
                                ],
                                canBidInputBeDisabled: true,
                                isSlotChangeRequired: false,
                                bidType: "Exact",
                            },
                        },
                        actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                        columns: [
                            "keyword_name",
                            "is_archived_entity",
                            "bid",
                            "state",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "program_type",
                            "campaign_name",
                            "sys_campaign_id",
                            "sys_ams_keyword_id",
                            "campaign_tags",
                            "tag_types",
                            "percent_utilisation",
                            "serving_status",
                            "record_type",
                            "targetingtype",
                            "match_type",
                        ],
                        features: {
                            [Features.PAUSE]: {
                                name: "Pause",
                                actions: [Actions.PAUSE],
                            },
                            [Features.ENABLE]: {
                                name: "Enable",
                                actions: [Actions.ENABLE],
                            },
                            [Features.BID]: {
                                name: "Update Bid",
                                actions: [
                                    Actions.BID_CHANGE_ABSOLUTE,
                                    Actions.INCREASE_BID_PERC,
                                    Actions.DECREASE_BID_PERC,
                                ],
                            },
                            [Features.RULE]: {
                                name: "Save Actions",
                                actions: [
                                    Actions.INCREASE_BID_PERC,
                                    Actions.DECREASE_BID_PERC,
                                ],
                            },
                        },
                    },
                    [SubModule.PRODUCT]: {
                        name: "Products",
                        actions: [],
                        columns: [
                            "sku_title",
                            "campaign_name",
                            "program_type",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "sys_campaign_id",
                            "asin_id",
                        ],
                        features: {},
                        gridParams: {
                            rowHeight: 65,
                        },
                    },
                },
            },
            [ParentModule.RULES]: {
                name: "Rules",
                isSubheaderVisible: false,
                // Arr of columns to show (keys:string to be used in column defs)
                columns: [
                    "rule_id",
                    "name",
                    "type",
                    "filter",
                    "module",
                    "cron_string",
                    "created_at",
                    "created_by",
                    "updated_at",
                    "updated_by",
                    "status",
                    "",
                ],
                features: {},
            },
            [ParentModule.KEYWORD_ANALYTICS]: {
                name: "Keyword Analytics",
                isSubheaderVisible: true,
                gridParams: {
                    isKeywordLinkRequired: true,
                    isAdgroupSelectionRequired: false,
                    isBidRequired: true,
                    isMatchTypeReviewRequired: false,
                    isNegativeAdditionAllowed: true,
                    isMatchTypeSelectionRequired: true,
                    columnsToHide: [
                        "page1OrganicSov",
                        "impression_share",
                        "total_impression",
                        "rank",
                        "is_exact",
                    ],
                    rowHeight: 65,
                    is_exact: {
                        headerName: "Is Exact",
                    },
                },
                actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                columns: [
                    "keyword_name",
                    "program_type",
                    "is_exact",
                    "impression_share_rank",
                    "impression_share",
                    "page1OverallSov",
                    "page1OrganicSov",
                    "total_impression",
                    "top_product",
                    "rank",
                    "impression",
                    "click",
                    "spend",
                    "orders",
                    "sale",
                    "cpc",
                    "cr",
                    "cpa",
                    "acos",
                    "roas",
                    "campaigns",
                ],
                subGrids: {
                    keywords: {
                        columns: [
                            "keyword_name",
                            "bid",
                            "state",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "match_type",
                            "record_type",
                            "program_type",
                            "campaign_name",
                            "sys_campaign_id",
                            "targetingtype",
                        ],
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            canChangeKeywordStatus: true,
                            bid: {
                                cellRenderer: PopOverBidCellRenderer,
                                minWidth: 150,
                                maxWidth: 160,
                                maxBidLimitRecordTypes: [
                                    RecordType.KEYWORD,
                                    RecordType.TARGET,
                                ],
                                canBidInputBeDisabled: true,
                                isSlotChangeRequired: false,
                                bidType: "Exact",
                            },
                        },
                    },
                },
                features: {
                    [Features.BULK_TGT_ACTION]: {
                        name: "Bulk Target Actions",
                    },
                },
            },
            [ParentModule.PRODUCT_ANALYTICS]: {
                name: "Product Analytics",
                isSubheaderVisible: true,
                actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                columns: [
                    "sku_title",
                    "asin",
                    "",
                    "isFocusedAsin",
                    "is_available",
                    // New metrics
                    "sov_overall_pct",
                    "sov_organic_pct",
                    "sov_paid_pct",
                    "ms_by_price_pct",
                    "ms_by_mrp_pct",
                    "click",
                    "spend",
                    "impression",
                    "orders",
                    "sale",
                    "cpc",
                    "cr",
                    "cpa",
                    "acos",
                    "roas",
                    // New averages near pricing/availability
                    // "price",
                    "avg_price",
                    // "availability_percent",
                    "weighted_availability",
                    "cpm",
                    "tag_types",
                    "sku_tags",
                ],
                gridParams: {
                    columnsToHide: ["asin", "is_available", "cpc", "cr", "cpa"],
                },
                subGrids: {
                    keywords: {
                        columns: [
                            "keyword_name",
                            "bid",
                            "state",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "match_type",
                            "record_type",
                            "program_type",
                            "campaign_name",
                            "cpm",
                            "targetingtype",
                        ],
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            bid: {
                                cellRenderer: PopOverBidCellRenderer,
                                minWidth: 150,
                                maxWidth: 160,
                                maxBidLimitRecordTypes: [
                                    RecordType.KEYWORD,
                                    RecordType.TARGET,
                                ],
                                canBidInputBeDisabled: true,
                                isSlotChangeRequired: false,
                                bidType: "Exact",
                            },
                        },
                    },
                },
                features: {
                    [Features.TAGS_ADDITION]: {
                        name: "Add Tags",
                    },
                },
            },
            [ParentModule.PRODUCT_DEEPDIVE]: {
                name: "Product Deepdive",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.COMMON_REPORTS]: {
                name: "Common Reports",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.REVIEW_ANALYTICS]: {
                name: "Review Analytics",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.REVSTACK]: {
                name: "Custom Reports",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.NAVIGATOR]: {
                name: "Navigator",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.CATEGORY_ANALYSIS]: {
                name: "Category Analysis",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.PRODUCT_ANALYSIS]: {
                name: "Product Analysis",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.CHANGE_LOG]: {
                name: "History",
                isSubheaderVisible: true,
                actions: [],
                columns: [
                    "log_date_iso",
                    "log_date",
                    "log_date",
                    "revert_action",
                    "module",
                    "change_type",
                    "record_name",
                    "record_type",
                    "attribute",
                    "old_value",
                    "new_value",
                    "source",
                    "nature",
                    "source_name",
                    "source_id",
                    "log_time",
                ],
                features: {
                    [Features.REVERT_ACTION]: {
                        name: "Revert Action",
                    },
                },
            },
            [ParentModule.REPORTS_BUILDER]: {
                name: "Reports Builder",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.SETTINGS]: {
                name: "Settings",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {
                    // [Features.BULK_PRODUCT_MAPPING]: {
                    //     name: "Bulk Mapping",
                    // },
                    [Features.ADD_MAPPING]: {
                        name: "Add Mapping",
                    },
                    [Features.APPLY_1DS_MAPPING]: {
                        name: "Apply 1DS Mapping",
                    },
                },
                additionalFeatures: {
                    "buybox-add-sellers": false,
                },
            },
            [ParentModule.ONEDER]: {
                name: "1der ai",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
                additionalFeatures: {},
            },
            [ParentModule.CHATBOT]: {
                name: "Ask AI",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
                additionalFeatures: {},
            },
            [ParentModule.ADMIN_SETTINGS]: {
                name: "Admin Settings",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
        },
        dropdownOptions: {
            targetTypeOptions: [],
            matchTypeOptions: [
                { value: "exact", label: "Exact" },
                { value: "phrase", label: "Phrase" },
            ],
            negativeMatchTypeOptions: [
                { value: "exact", label: "Exact" },
                { value: "phrase", label: "Phrase" },
            ],
            programTypeOptions: [
                { value: "sp", label: "SP" },
                { value: "sb", label: "SB" },
            ],
            kaCampaignTypeOptions: [{ value: "manual", label: "Manual" }],
            chartFilterOptions: [
                { value: "sp", label: "SP" },
                { value: "sb", label: "SB" },
                { value: "all", label: "All" },
            ],
        },
    },
    [ChannelName.TALABAT]: {
        options: {
            decimalValue: 0,
            recommendedBid: 10,
            isTargetCheckRequired: false,
            isSmartInfoRequired: false,
            formatText: {
                record_type: {
                    keyword: "Keyword",
                    target: "Category",
                },
                program_type: {
                    sp: "SP",
                    sd: "SD",
                },
                targetingtype: {
                    search: "Search",
                    listing: "Listing",
                },
            },
            platformFeatures: [
                Features.DAY_PARTING,
                Features.REVSTACK,
                Features.CATEGORY_ANALYSIS,
                Features.PRODUCT_ANALYSIS,
                Features.NAVIGATOR,
            ],
            defaultActionLevel: "campaign",
        },
        modules: {
            [ParentModule.PERFORMANCE_OVERVIEW]: {
                name: "Performance Overview",
                isSubheaderVisible: true,
                subModules: {
                    [SubModule.OVERVIEW]: {
                        name: "Overview",
                        actions: [],
                        columns: [],
                        features: {},
                        overviewGrids: {
                            tagTypeAggregate: {
                                columns: [
                                    "tag",
                                    "spend",
                                    "spend_share",
                                    "sale",
                                    "sale_share",
                                    "click",
                                    "orders",
                                    "roas",
                                    "impression",
                                ],
                            },
                        },
                    },
                    [SubModule.CAMPAIGN]: {
                        name: "Campaigns",
                        gridParams: {
                            budget: {
                                minWidth: 370,
                                maxWidth: 380,
                            },
                            program_type: {
                                minWidth: 120,
                                maxWidth: 120,
                            },
                            campaign_name: {
                                minWidth: 290,
                                maxWidth: 310,
                            },
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            isCpmChangeAllowed: true,
                            isBudgetReduceAllowed: true,
                            isDateChangeAllowed: true,
                            isDateReduceAllowed: true,
                            cpmChangeAllowedTypes: [
                                ProgramType.SP,
                                ProgramType.SD,
                            ],
                            isDailyBudgetChangeOnPlatform: true,
                            isArchivedCampaignsSelectionAllowed: false,
                        },
                        componentConfig: {
                            till_budget_ends: {
                                label: "Always on",
                                isVisible: true,
                            },
                            end_date: {
                                isVisible: true,
                            },
                            equally_divide_budget: {
                                isVisible: false,
                            },
                            cpm: {
                                label: "CPC",
                                isVisible: true,
                            },
                        },
                        actions: [
                            Actions.STATUS_CHANGE,
                            Actions.BUDGET_CHANGE,
                            Actions.NAME_CHANGE,
                            Actions.REQUIRED_DAILY_BUDGET_CHANGE,
                        ],
                        columns: [
                            "campaign_name",
                            "state",
                            "budget",
                            "record_type",
                            "program_type",
                            "automation",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "sys_campaign_id",
                            "campaign_status",
                            "campaign_tags",
                            "tag_types",
                            "targetingtype",
                        ],
                        features: {
                            [Features.BULK_ACTION]: {
                                name: "Bulk Taxonomy",
                            },
                            [Features.DAY_PARTING]: {
                                name: "Day Parting",
                            },
                            [Features.PAUSE]: {
                                name: "Pause",
                                actions: [Actions.PAUSE],
                            },
                            [Features.ENABLE]: {
                                name: "Enable",
                                actions: [Actions.ENABLE],
                            },
                            [Features.DATE_CHANGE]: {
                                name: "Change End Date",
                            },
                            [Features.TAGS_ADDITION]: {
                                name: "Add Tags",
                            },
                            [Features.BUDGET]: {
                                name: "Update Budget",
                                actions: [Actions.BUDGET_CHANGE],
                            },
                        },
                    },
                    [SubModule.KEYWORD]: {
                        name: "Keywords",
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            bid: {
                                cellRenderer: PopOverBidCellRenderer,
                                minWidth: 150,
                                maxWidth: 160,
                                filterOptions: [
                                    "equals",
                                    "notEqual",
                                    "greaterThan",
                                    "lessThan",
                                    "greaterThanOrEqual",
                                    "lessThanOrEqual",
                                ],
                                maxBidLimitRecordTypes: [],
                                canBidInputBeDisabled: false,
                                isSlotChangeRequired: true,
                                bidType: "",
                            },
                        },
                        actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                        columns: [
                            "keyword_name",
                            "is_archived_entity",
                            "bid",
                            "state",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "program_type",
                            "campaign_name",
                            "sys_campaign_id",
                            "sys_ams_keyword_id",
                            "campaign_tags",
                            "tag_types",
                            "percent_utilisation",
                            "serving_status",
                            "record_type",
                            "targetingtype",
                        ],
                        features: {
                            [Features.PAUSE]: {
                                name: "Pause",
                                actions: [Actions.PAUSE],
                            },
                            [Features.ENABLE]: {
                                name: "Enable",
                                actions: [Actions.ENABLE],
                            },
                            [Features.BID]: {
                                name: "Update Bid",
                                actions: [
                                    Actions.BID_CHANGE_ABSOLUTE,
                                    Actions.INCREASE_BID_PERC,
                                    Actions.DECREASE_BID_PERC,
                                ],
                            },
                            [Features.RULE]: {
                                name: "Save Actions",
                                actions: [
                                    Actions.INCREASE_BID_PERC,
                                    Actions.DECREASE_BID_PERC,
                                ],
                            },
                        },
                    },
                    [SubModule.PRODUCT]: {
                        name: "Products",
                        actions: [],
                        columns: [
                            "sku_title",
                            "campaign_name",
                            "program_type",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "sys_campaign_id",
                            "asin_id",
                        ],
                        features: {},
                        gridParams: {
                            rowHeight: 65,
                        },
                    },
                },
            },
            [ParentModule.RULES]: {
                name: "Rules",
                isSubheaderVisible: false,
                // Arr of columns to show (keys:string to be used in column defs)
                columns: [
                    "rule_id",
                    "name",
                    "type",
                    "filter",
                    "module",
                    "cron_string",
                    "created_at",
                    "created_by",
                    "updated_at",
                    "updated_by",
                    "status",
                    "",
                ],
                features: {},
            },
            [ParentModule.KEYWORD_ANALYTICS]: {
                name: "Keyword Analytics",
                isSubheaderVisible: true,
                gridParams: {
                    isKeywordLinkRequired: true,
                    isAdgroupSelectionRequired: false,
                    isBidRequired: false,
                    isMatchTypeReviewRequired: false,
                    isMatchTypeSelectionRequired: false,
                    isNegativeAdditionAllowed: false,
                    columnsToHide: [
                        "page1OrganicSov",
                        "impression_share",
                        "total_impression",
                        "rank",
                        "is_exact",
                    ],
                    is_exact: {
                        headerName: "Is Exact",
                    },
                },
                actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                columns: [
                    "keyword_name",
                    "program_type",
                    "is_exact",
                    "impression_share_rank",
                    "impression_share",
                    "page1OverallSov",
                    "page1OrganicSov",
                    "total_impression",
                    "top_product",
                    "rank",
                    "impression",
                    "click",
                    "spend",
                    "orders",
                    "sale",
                    "cpc",
                    "cr",
                    "cpa",
                    "acos",
                    "roas",
                    "campaigns",
                ],
                subGrids: {
                    keywords: {
                        columns: [
                            "keyword_name",
                            "bid",
                            "state",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "record_type",
                            "program_type",
                            "campaign_name",
                            "sys_campaign_id",
                            "targetingtype",
                        ],
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            canChangeKeywordStatus: true,
                            bid: {
                                cellRenderer: PopOverBidCellRenderer,
                                minWidth: 150,
                                maxWidth: 160,
                                maxBidLimitRecordTypes: [],
                                canBidInputBeDisabled: false,
                                isSlotChangeRequired: true,
                                bidType: "",
                            },
                        },
                    },
                },
                features: {
                    [Features.BULK_TGT_ACTION]: {
                        name: "Bulk Target Actions",
                    },
                },
            },
            [ParentModule.PRODUCT_ANALYTICS]: {
                name: "Product Analytics",
                isSubheaderVisible: true,
                actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                columns: [
                    "sku_title",
                    "asin",
                    "",
                    "isFocusedAsin",
                    "is_available",
                    // New metrics
                    "sov_overall_pct",
                    "sov_organic_pct",
                    "sov_paid_pct",
                    "ms_by_price_pct",
                    "ms_by_mrp_pct",
                    "click",
                    "spend",
                    "impression",
                    "orders",
                    "sale",
                    "cpc",
                    "cr",
                    "cpa",
                    "acos",
                    "roas",
                    // New averages near pricing/availability
                    // "price",
                    "avg_price",
                    // "availability_percent",
                    "weighted_availability",
                    "cpm",
                    "tag_types",
                    "sku_tags",
                ],
                gridParams: {
                    columnsToHide: ["asin", "is_available", "cpc", "cr", "cpa"],
                },
                subGrids: {
                    keywords: {
                        columns: [
                            "keyword_name",
                            "bid",
                            "state",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "record_type",
                            "program_type",
                            "campaign_name",
                            "cpm",
                            "targetingtype",
                        ],
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            bid: {
                                cellRenderer: PopOverBidCellRenderer,
                                minWidth: 150,
                                maxWidth: 160,
                                maxBidLimitRecordTypes: [],
                                canBidInputBeDisabled: false,
                                isSlotChangeRequired: true,
                                bidType: "",
                            },
                        },
                    },
                },
                features: {
                    [Features.TAGS_ADDITION]: {
                        name: "Add Tags",
                    },
                },
            },
            [ParentModule.PRODUCT_DEEPDIVE]: {
                name: "Product Deepdive",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.COMMON_REPORTS]: {
                name: "Common Reports",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.REVSTACK]: {
                name: "Custom Reports",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.NAVIGATOR]: {
                name: "Navigator",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.CATEGORY_ANALYSIS]: {
                name: "Category Analysis",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.PRODUCT_ANALYSIS]: {
                name: "Product Analysis",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.CHANGE_LOG]: {
                name: "History",
                isSubheaderVisible: true,
                actions: [],
                columns: [
                    "log_date_iso",
                    "log_date",
                    "log_date",
                    "revert_action",
                    "module",
                    "change_type",
                    "record_name",
                    "record_type",
                    "attribute",
                    "old_value",
                    "new_value",
                    "source",
                    "nature",
                    "source_name",
                    "source_id",
                    "log_time",
                ],
                features: {
                    [Features.REVERT_ACTION]: {
                        name: "Revert Action",
                    },
                },
            },
            [ParentModule.REPORTS_BUILDER]: {
                name: "Reports Builder",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.SETTINGS]: {
                name: "Settings",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {
                    // [Features.BULK_PRODUCT_MAPPING]: {
                    //     name: "Bulk Mapping",
                    // },
                    [Features.ADD_MAPPING]: {
                        name: "Add Mapping",
                    },
                    [Features.APPLY_1DS_MAPPING]: {
                        name: "Apply 1DS Mapping",
                    },
                },
                additionalFeatures: {
                    "buybox-add-sellers": false,
                },
            },
            [ParentModule.ONEDER]: {
                name: "1der ai",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
                additionalFeatures: {},
            },
            [ParentModule.CHATBOT]: {
                name: "Ask AI",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
                additionalFeatures: {},
            },
            [ParentModule.ADMIN_SETTINGS]: {
                name: "Admin Settings",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
        },
        dropdownOptions: {
            targetTypeOptions: [],
            matchTypeOptions: [
                { value: "exact", label: "Exact" },
                { value: "phrase", label: "Phrase" },
            ],
            negativeMatchTypeOptions: [
                { value: "exact", label: "Exact" },
                { value: "phrase", label: "Phrase" },
            ],
            programTypeOptions: [
                { value: "sp", label: "SP" },
                { value: "sd", label: "SD" },
            ],
            kaCampaignTypeOptions: [{ value: "manual", label: "Manual" }],
            chartFilterOptions: [
                { value: "sp", label: "SP" },
                { value: "sd", label: "SD" },
                { value: "all", label: "All" },
            ],
        },
    },
    [ChannelName.WALMART]: {
        options: {
            decimalValue: 2,
            recommendedBid: 10,
            isTargetCheckRequired: true,
            formatText: {
                record_type: {
                    keyword: "Keyword",
                    target: "Product",
                },
                program_type: {
                    sp: "SP",
                    sb: "SB",
                    sd: "SD",
                },
            },
            platformFeatures: [
                Features.DAY_PARTING,
                Features.REVSTACK,
                Features.CATEGORY_ANALYSIS,
                Features.PRODUCT_ANALYSIS,
                Features.NAVIGATOR,
            ],
        },
        modules: {
            [ParentModule.PERFORMANCE_OVERVIEW]: {
                name: "Performance Overview",
                isSubheaderVisible: true,
                subModules: {
                    [SubModule.OVERVIEW]: {
                        name: "Overview",
                        actions: [],
                        columns: [],
                        features: {},
                        overviewGrids: {
                            tagTypeAggregate: {
                                columns: [
                                    "tag",
                                    "spend",
                                    "spend_share",
                                    "sale",
                                    "sale_share",
                                    "click",
                                    "orders",
                                    "roas",
                                    "impression",
                                    "total_impression",
                                    "impPercShare",
                                    "market_share",
                                ],
                            },
                        },
                    },
                    [SubModule.CAMPAIGN]: {
                        name: "Campaigns",
                        gridParams: {
                            budget: {
                                minWidth: 170,
                                maxWidth: 190,
                                minAllowedBudget: 100,
                            },
                            program_type: {
                                minWidth: 80,
                                maxWidth: 80,
                            },
                            campaign_name: {
                                minWidth: 320,
                                maxWidth: 500,
                            },
                            isCpmChangeAllowed: false,
                            isBudgetReduceAllowed: true,
                            isDateChangeAllowed: false,
                            isDateReduceAllowed: false,
                            isArchivedCampaignsSelectionAllowed: false,
                        },
                        componentConfig: {
                            till_budget_ends: {
                                isVisible: false,
                            },
                            end_date: {
                                isVisible: false,
                            },
                            equally_divide_budget: {
                                isVisible: false,
                            },
                            cpm: {
                                isVisible: false,
                            },
                        },
                        actions: [
                            Actions.NAME_CHANGE,
                            Actions.STATUS_CHANGE,
                            Actions.BUDGET_CHANGE,
                        ],
                        // Arr of columns to show (keys:string to be used in column defs)
                        columns: [
                            "campaign_name",
                            "budget",
                            "record_type",
                            "portfolio_name",
                            "program_type",
                            "automation",
                            "state",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "top_product",
                            "percent_spend",
                            "sys_campaign_id",
                            "portfolio_id",
                            "campaign_tags",
                            "tag_types",
                        ],
                        features: {
                            [Features.PAUSE]: {
                                name: "Pause",
                                actions: [Actions.PAUSE],
                            },
                            [Features.ENABLE]: {
                                name: "Enable",
                                actions: [Actions.ENABLE],
                            },
                            [Features.BULK_ACTION]: {
                                name: "Bulk Taxonomy",
                            },
                            [Features.DAY_PARTING]: {
                                name: "Day Parting",
                            },
                            [Features.TAGS_ADDITION]: {
                                name: "Add Tags",
                            },
                            [Features.BUDGET]: {
                                name: "Update Budget",
                                actions: [Actions.BUDGET_CHANGE],
                            },
                        },
                    },
                    [SubModule.ADGROUP]: {
                        name: "AdGroups",
                        actions: [Actions.NAME_CHANGE, Actions.STATUS_CHANGE],
                        // Arr of columns to show (keys:string to be used in column defs)
                        columns: [
                            "ad_group_name",
                            "state",
                            "campaign_name",
                            "portfolio_name",
                            "program_type",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "top_product",
                            "percent_spend",
                            "sys_campaign_id",
                            "sys_adgroup_id",
                            "asin_id",
                            "portfolio_id",
                        ],
                        gridParams: {
                            isDateChangeAllowed: false,
                            isDateReduceAllowed: false,
                            isCpmChangeAllowed: false,
                            isBudgetReduceAllowed: true,
                            budget: {
                                minWidth: 370,
                                maxWidth: 380,
                            },
                        },
                        features: {
                            [Features.BULK_ACTION]: {
                                name: "Bulk Taxonomy",
                            },
                        },
                    },
                    [SubModule.KEYWORD]: {
                        name: "Keywords",
                        actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                        gridParams: {
                            bid: {
                                cellRenderer: UpdateBidOrBudgetCellRenderer,
                                minWidth: 170,
                                maxWidth: 190,
                                filterOptions: [
                                    "equals",
                                    "notEqual",
                                    "greaterThan",
                                    "lessThan",
                                    "greaterThanOrEqual",
                                    "lessThanOrEqual",
                                ],
                            },
                        },
                        // Arr of columns to show (keys:string to be used in column defs)
                        columns: [
                            "keyword_name",
                            "is_archived_entity",
                            "bid",
                            "state",
                            "ad_group_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "top_product",
                            "percent_spend",
                            "match_type",
                            "record_type",
                            "kwd_class",
                            "program_type",
                            "campaign_name",
                            "portfolio_name",
                            "portfolio_id",
                            "sys_campaign_id",
                            "sys_adgroup_id",
                            "asin_id",
                            "sys_ams_keyword_id",
                            "campaign_tags",
                        ],
                        features: {
                            [Features.BID]: {
                                name: "Update Bid",
                                actions: [
                                    Actions.BID_CHANGE_ABSOLUTE,
                                    Actions.INCREASE_BID_PERC,
                                    Actions.DECREASE_BID_PERC,
                                ],
                            },
                            [Features.PAUSE]: {
                                name: "Pause",
                                actions: [Actions.PAUSE],
                            },
                            [Features.ENABLE]: {
                                name: "Enable",
                                actions: [Actions.ENABLE],
                            },
                            // !Note : Hidden for time being
                            // [Features.MARK_NEGATIVE]: {
                            //     name: "Mark as negative",
                            //     actions: [Actions.MARK_NEGATIVE],
                            // },
                            [Features.RULE]: {
                                name: "Save Actions",
                                actions: [
                                    Actions.PAUSE,
                                    Actions.INCREASE_BID_PERC,
                                    Actions.DECREASE_BID_PERC,
                                ],
                            },
                        },
                    },
                    [SubModule.PRODUCT]: {
                        name: "Products",
                        actions: [Actions.STATUS_CHANGE],
                        // Arr of columns to show (keys:string to be used in column defs)
                        columns: [
                            "sku_title",
                            "ad_group_name",
                            "campaign_name",
                            "portfolio_name",
                            "program_type",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "top_product",
                            "percent_spend",
                            "portfolio_id",
                            "sys_campaign_id",
                            "sys_adgroup_id",
                            "asin_id",
                            "state",
                        ],
                        features: {},
                        gridParams: {
                            isKeywordPauseAllowed: true,
                            disabledProgramTypes: [
                                ProgramType.HSA,
                                ProgramType.SB,
                            ],
                        },
                    },
                },
            },
            [ParentModule.RULES]: {
                name: "Rules",
                isSubheaderVisible: false,
                // Arr of columns to show (keys:string to be used in column defs)
                columns: [
                    "rule_id",
                    "name",
                    "type",
                    "filter",
                    "module",
                    "cron_string",
                    "created_at",
                    "created_by",
                    "updated_at",
                    "updated_by",
                    "status",
                    "",
                ],
                features: {},
            },
            [ParentModule.KEYWORD_ANALYTICS]: {
                name: "Search Term Analytics",
                isSubheaderVisible: true,
                gridParams: {
                    isKeywordLinkRequired: false,
                    isAdgroupSelectionRequired: true,
                    isBidRequired: true,
                    isMatchTypeReviewRequired: false,
                    columnsToHide: ["is_exact", "impression", "cpc"],
                    isNegativeAdditionAllowed: true,
                    isMatchTypeSelectionRequired: true,
                    rowHeight: 65,
                },
                actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                columns: [
                    "keyword_name",
                    "program_type",
                    "kwd_class",
                    "is_exact",
                    "impression_share_rank",
                    "impression_share",
                    "page1OverallSov",
                    "top_product",
                    "impression",
                    "click",
                    "spend",
                    "orders",
                    "sale",
                    "ctr",
                    "cpc",
                    "cr",
                    "cpa",
                    "acos",
                    "roas",
                    "campaigns",
                    "portfolios",
                ],
                subGrids: {
                    keywords: {
                        columns: [
                            "keyword_name",
                            "bid",
                            "state",
                            "kwd_class",
                            "ad_group_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "top_product",
                            "percent_spend",
                            "creation_date",
                            "match_type",
                            "record_type",
                            "program_type",
                            "campaign_name",
                            "sys_campaign_id",
                            "sys_adgroup_id",
                            "asin_id",
                        ],
                        gridParams: {
                            isBadgeRequired: false,
                            doesAddToExact: false,
                            isRemoveSkuAllowed: false,
                            canChangeKeywordStatus: true,
                            bid: {
                                cellRenderer: UpdateBidOrBudgetCellRenderer,
                                minWidth: 150,
                                maxWidth: 170,
                            },
                        },
                    },
                    campaigns: {
                        columns: [
                            "customer_search_term",
                            "keyword_name",
                            "campaign_name",
                            "ad_group_name",
                            "match_type",
                            "spend",
                            "orders",
                            "sale",
                            "impression",
                            "click",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                        ],
                        gridParams: {
                            isBadgeRequired: false,
                            isBidRequired: true,
                            doesAddToExact: true,
                            isRemoveSkuAllowed: false,
                            bidColumn: "cpc",
                            keywordColumn: "customer_search_term",
                            cpc: {
                                minWidth: 170,
                                maxWidth: 180,
                            },
                        },
                    },
                    addAsExact: {
                        columns: [
                            "customer_search_term",
                            "keyword_name",
                            "cpc",
                            "campaign_name",
                            "ad_group_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cr",
                            "cpa",
                            "acos",
                            "program_type",
                            "roas",
                            "match_type",
                        ],
                        gridParams: {
                            isBadgeRequired: false,
                            isTypeBadgeRequired: false,
                            isBidRequired: true,
                            doesAddToExact: true,
                            isRemoveSkuAllowed: false,
                            bidColumn: "cpc",
                            keywordColumn: "customer_search_term",
                            cpc: {
                                headerName: "Suggested Bid",
                                minWidth: 170,
                                maxWidth: 180,
                            },
                        },
                    },
                },
                features: {
                    // !NOTE: Hidden for time being
                    // [Features.ADD_AS_EXACT]: {
                    //     name: "Add As Exact",
                    // },
                    // [Features.BULK_TGT_ACTION]: {
                    //     name: "Bulk Target Actions",
                    // },
                    [Features.RULE]: {
                        name: "Save Actions",
                        actions: [
                            Actions.PAUSE,
                            Actions.INCREASE_BID_PERC,
                            Actions.DECREASE_BID_PERC,
                        ],
                    },
                },
            },
            [ParentModule.PRODUCT_ANALYTICS]: {
                name: "Product Analytics",
                isSubheaderVisible: true,
                gridParams: {
                    columnsToHide: [
                        "asin",
                        "is_available",
                        "click",
                        "impression",
                        "orders",
                        "cpc",
                        "cr",
                        "cpa",
                        "acos",
                    ],
                    rowHeight: 65,
                },
                actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                columns: [
                    "sku_title",
                    "asin",
                    "product_link",
                    "",
                    "category",
                    "isFocusedAsin",
                    "is_available",
                    // New metrics
                    "sov_overall_pct",
                    "sov_organic_pct",
                    "sov_paid_pct",
                    "ms_by_price_pct",
                    "ms_by_mrp_pct",
                    "ag_group_nos",
                    // "availability_percent",
                    "click",
                    "spend",
                    "impression",
                    "orders",
                    "sale",
                    "ctr",
                    "cpc",
                    "cr",
                    "cpa",
                    "acos",
                    "roas",
                    "latest_bb_winner",
                    "rating",
                    "rating_count",
                    // "price",
                    "tag_types",
                    "sku_tags",
                ],
                subGrids: {
                    keywords: {
                        columns: [
                            "keyword_name",
                            "bid",
                            "state",
                            "kwd_class",
                            "percent_spend_in_adgroup",
                            "rank",
                            "ad_group_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "match_type",
                            "record_type",
                            "program_type",
                            "campaign_name",
                        ],
                        gridParams: {
                            isBadgeRequired: false,
                            doesAddToExact: false,
                            isRemoveSkuAllowed: false,
                            bid: {
                                cellRenderer: UpdateBidOrBudgetCellRenderer,
                                minWidth: 150,
                                maxWidth: 170,
                            },
                        },
                    },
                    adgroups: {
                        columns: [
                            "campaign_name",
                            "ad_group_name",
                            "sku_id",
                            "state",
                            "percent_spend",
                            "click",
                            "spend",
                            "impression",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                        ],
                        gridParams: {
                            doesAddToExact: false,
                            isRemoveSkuAllowed: false,
                            disabledProgramTypes: [
                                ProgramType.HSA,
                                ProgramType.SB,
                            ],
                        },
                    },
                },
                features: {
                    [Features.ADD_TO_BUYBOX]: {
                        name: "Add to Buybox",
                        actions: [Actions.ADD_TO_BUYBOX],
                    },
                    [Features.REMOVE_BUYBOX]: {
                        name: "Remove Buybox",
                        actions: [Actions.REMOVE_BUYBOX],
                    },
                    [Features.PAUSE_ALL]: {
                        name: "Pause All",
                        actions: [Actions.PAUSE_ALL],
                    },
                    [Features.ENABLE_ALL]: {
                        name: "Enable All",
                        actions: [Actions.ENABLE_ALL],
                    },
                    [Features.CREATE_CAMPAIGN]: {
                        name: "Create Campaign",
                        actions: [Actions.CREATE_CAMPAIGN],
                    },
                    [Features.TAGS_ADDITION]: {
                        name: "Add Tags",
                    },
                    [Features.RULE]: {
                        name: "Save Actions",
                        actions: [
                            Actions.PAUSE_KEYWORDS,
                            Actions.PAUSE_ADS,
                            Actions.INCREASE_BID_PERC,
                            Actions.DECREASE_BID_PERC,
                        ],
                    },
                },
            },
            [ParentModule.PRODUCT_DEEPDIVE]: {
                name: "Product Deepdive",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.CAMPAIGN_RECOMMENDATIONS]: {
                name: "Campaigns",
                isSubheaderVisible: true,
                subModules: {
                    [SubModule.CAMPAIGN_BUDGET]: {
                        name: "Budget Recommendations",
                        gridParams: {
                            isCpmChangeAllowed: false,
                            rowHeight: 65,
                        },
                        slug: "budget",
                        actions: [Actions.BUDGET_CHANGE],
                        columns: [
                            "campaign_name",
                            "record_type",
                            "budget",
                            "percentSpentout",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                        ],
                        features: {},
                    },
                    [SubModule.CAMPAIGN_PLACEMENT]: {
                        name: "Placement Recommendations",
                        slug: "placement",
                        actions: [Actions.PLACEMENT_CHANGE],
                        columns: [
                            "campaign_name",
                            "buybox",
                            "homepage",
                            "searchin_grid",
                            "stockup",
                            "total",
                        ],
                        features: {},
                    },
                },
            },
            [ParentModule.REVIEW_ANALYTICS]: {
                name: "Review Analytics",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.REVSTACK]: {
                name: "Custom Reports",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.NAVIGATOR]: {
                name: "Navigator",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.CATEGORY_ANALYSIS]: {
                name: "Category Analysis",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.PRODUCT_ANALYSIS]: {
                name: "Product Analysis",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.COMMON_REPORTS]: {
                name: "Common Reports",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.CHANGE_LOG]: {
                name: "History",
                isSubheaderVisible: true,
                actions: [],
                columns: [
                    "log_date_iso",
                    "log_date",
                    "revert_action",
                    "log_date",
                    "module",
                    "change_type",
                    "record_name",
                    "record_type",
                    "attribute",
                    "old_value",
                    "new_value",
                    "source",
                    "nature",
                    "source_name",
                    "source_id",
                    "log_time",
                ],
                features: {
                    [Features.REVERT_ACTION]: {
                        name: "Revert Action",
                    },
                },
            },
            [ParentModule.REPORTS_BUILDER]: {
                name: "Reports Builder",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.SETTINGS]: {
                name: "Settings",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {
                    // [Features.BULK_PRODUCT_MAPPING]: {
                    //     name: "Bulk Mapping",
                    // },
                    [Features.ADD_MAPPING]: {
                        name: "Add Mapping",
                    },
                    [Features.APPLY_1DS_MAPPING]: {
                        name: "Apply 1DS Mapping",
                    },
                },
                additionalFeatures: {
                    "buybox-add-sellers": true,
                },
            },
            [ParentModule.INTEGRATIONS]: {
                name: "Integrations",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.ONEDER]: {
                name: "1der ai",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
                additionalFeatures: {},
            },
            [ParentModule.CHATBOT]: {
                name: "Ask AI",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
                additionalFeatures: {},
            },
            [ParentModule.ADMIN_SETTINGS]: {
                name: "Admin Settings",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
        },
        dropdownOptions: {
            targetTypeOptions: [
                { value: "product", label: "Product" },
                { value: "keyword", label: "Keyword" },
                { value: "auto", label: "Auto" },
            ],
            matchTypeOptions: [
                { value: "broad", label: "Broad" },
                { value: "exact", label: "Exact" },
                { value: "phrase", label: "Phrase" },
            ],
            negativeMatchTypeOptions: [
                { value: "exact", label: "Exact" },
                { value: "phrase", label: "Phrase" },
            ],
            programTypeOptions: [
                { value: "sp", label: "SP" },
                { value: "sb", label: "SB" },
            ],
            kaCampaignTypeOptions: [
                { value: "auto", label: "Auto" },
                { value: "manual", label: "Manual" },
            ],
            chartFilterOptions: [
                { value: "sp", label: "SP" },
                { value: "sb", label: "SB" },
                { value: "sd", label: "SD" },
                { value: "all", label: "All" },
            ],
        },
    },
    [ChannelName.NOON_MINUTES]: {
        options: {
            decimalValue: 0,
            recommendedBid: 10,
            isTargetCheckRequired: false,
            isSmartInfoRequired: false,
            formatText: {
                record_type: {
                    keyword: "Keyword",
                    target: "Category",
                },
                program_type: {
                    sp: "SP",
                    sb: "SB",
                },
                targetingtype: {
                    product: "Product",
                    brand: "Brand",
                },
            },
            platformFeatures: [
                Features.DAY_PARTING,
                Features.REVSTACK,
                Features.CATEGORY_ANALYSIS,
                Features.PRODUCT_ANALYSIS,
                Features.NAVIGATOR,
            ],
        },
        modules: {
            [ParentModule.PERFORMANCE_OVERVIEW]: {
                name: "Performance Overview",
                isSubheaderVisible: true,
                subModules: {
                    [SubModule.OVERVIEW]: {
                        name: "Overview",
                        actions: [],
                        columns: [],
                        features: {},
                        overviewGrids: {
                            tagTypeAggregate: {
                                columns: [
                                    "tag",
                                    "spend",
                                    "spend_share",
                                    "sale",
                                    "sale_share",
                                    "click",
                                    "orders",
                                    "roas",
                                    "impression",
                                ],
                            },
                        },
                    },
                    [SubModule.CAMPAIGN]: {
                        name: "Campaigns",
                        gridParams: {
                            budget: {
                                minWidth: 370,
                                maxWidth: 380,
                            },
                            program_type: {
                                minWidth: 120,
                                maxWidth: 120,
                            },
                            campaign_name: {
                                minWidth: 290,
                                maxWidth: 310,
                            },
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            isCpmChangeAllowed: false,
                            isBudgetReduceAllowed: true,
                            isDateChangeAllowed: true,
                            isDateReduceAllowed: true,
                            isArchivedCampaignsSelectionAllowed: false,
                        },
                        componentConfig: {
                            till_budget_ends: {
                                label: "No End Date",
                                isVisible: true,
                            },
                            end_date: {
                                isVisible: true,
                                disabledTypes: [ProgramType.Performance],
                            },
                            equally_divide_budget: {
                                isVisible: false,
                            },
                            cpm: {
                                isVisible: false,
                            },
                        },
                        actions: [
                            Actions.STATUS_CHANGE,
                            Actions.BUDGET_CHANGE,
                            Actions.NAME_CHANGE,
                        ],
                        columns: [
                            "campaign_name",
                            "state",
                            "budget",
                            "record_type",
                            "program_type",
                            "automation",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "sys_campaign_id",
                            "campaign_status",
                            "campaign_tags",
                            "tag_types",
                            "targetingtype",
                        ],
                        features: {
                            [Features.BULK_ACTION]: {
                                name: "Bulk Taxonomy",
                            },
                            [Features.DAY_PARTING]: {
                                name: "Day Parting",
                            },
                            [Features.PAUSE]: {
                                name: "Pause",
                                actions: [Actions.PAUSE],
                            },
                            [Features.ENABLE]: {
                                name: "Enable",
                                actions: [Actions.ENABLE],
                            },
                            [Features.DATE_CHANGE]: {
                                name: "Change End Date",
                            },
                            [Features.TAGS_ADDITION]: {
                                name: "Add Tags",
                            },
                        },
                    },
                    [SubModule.KEYWORD]: {
                        name: "Keywords",
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            bid: {
                                cellRenderer: PopOverBidCellRenderer,
                                minWidth: 150,
                                maxWidth: 160,
                                filterOptions: [
                                    "equals",
                                    "notEqual",
                                    "greaterThan",
                                    "lessThan",
                                    "greaterThanOrEqual",
                                    "lessThanOrEqual",
                                ],
                                maxBidLimitRecordTypes: [
                                    RecordType.KEYWORD,
                                    RecordType.TARGET,
                                ],
                                canBidInputBeDisabled: true,
                                isSlotChangeRequired: false,
                                bidType: "Exact",
                            },
                        },
                        actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                        columns: [
                            "keyword_name",
                            "isArchivedEntity",
                            "bid",
                            "state",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "program_type",
                            "campaign_name",
                            "sys_campaign_id",
                            "sys_ams_keyword_id",
                            "campaign_tags",
                            "tag_types",
                            "percent_utilisation",
                            "serving_status",
                            "record_type",
                            "targetingtype",
                            "match_type",
                        ],
                        features: {
                            [Features.PAUSE]: {
                                name: "Pause",
                                actions: [Actions.PAUSE],
                            },
                            [Features.ENABLE]: {
                                name: "Enable",
                                actions: [Actions.ENABLE],
                            },
                            [Features.BID]: {
                                name: "Update Bid",
                                actions: [
                                    Actions.BID_CHANGE_ABSOLUTE,
                                    Actions.INCREASE_BID_PERC,
                                    Actions.DECREASE_BID_PERC,
                                ],
                            },
                            [Features.RULE]: {
                                name: "Save Actions",
                                actions: [
                                    Actions.INCREASE_BID_PERC,
                                    Actions.DECREASE_BID_PERC,
                                ],
                            },
                        },
                    },
                    [SubModule.PRODUCT]: {
                        name: "Products",
                        actions: [],
                        columns: [
                            "sku_title",
                            "campaign_name",
                            "program_type",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "sys_campaign_id",
                            "asin_id",
                        ],
                        features: {},
                        gridParams: {
                            rowHeight: 65,
                        },
                    },
                },
            },
            [ParentModule.RULES]: {
                name: "Rules",
                isSubheaderVisible: false,
                // Arr of columns to show (keys:string to be used in column defs)
                columns: [
                    "rule_id",
                    "name",
                    "type",
                    "filter",
                    "module",
                    "cron_string",
                    "created_at",
                    "created_by",
                    "updated_at",
                    "updated_by",
                    "status",
                    "",
                ],
                features: {},
            },
            [ParentModule.KEYWORD_ANALYTICS]: {
                name: "Keyword Analytics",
                isSubheaderVisible: true,
                gridParams: {
                    isKeywordLinkRequired: true,
                    isAdgroupSelectionRequired: false,
                    isBidRequired: true,
                    isMatchTypeReviewRequired: false,
                    isNegativeAdditionAllowed: true,
                    isMatchTypeSelectionRequired: true,
                    columnsToHide: [
                        "page1OrganicSov",
                        "impression_share",
                        "total_impression",
                        "rank",
                        "is_exact",
                    ],
                    rowHeight: 65,
                    is_exact: {
                        headerName: "Is Exact",
                    },
                },
                actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                columns: [
                    "keyword_name",
                    "program_type",
                    "is_exact",
                    "impression_share_rank",
                    "impression_share",
                    "page1OverallSov",
                    "page1OrganicSov",
                    "total_impression",
                    "top_product",
                    "rank",
                    "impression",
                    "click",
                    "spend",
                    "orders",
                    "sale",
                    "cpc",
                    "cr",
                    "cpa",
                    "acos",
                    "roas",
                    "campaigns",
                ],
                subGrids: {
                    keywords: {
                        columns: [
                            "keyword_name",
                            "bid",
                            "state",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "match_type",
                            "record_type",
                            "program_type",
                            "campaign_name",
                            "sys_campaign_id",
                            "targetingtype",
                        ],
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            bid: {
                                cellRenderer: PopOverBidCellRenderer,
                                minWidth: 150,
                                maxWidth: 160,
                                maxBidLimitRecordTypes: [
                                    RecordType.KEYWORD,
                                    RecordType.TARGET,
                                ],
                                canBidInputBeDisabled: true,
                                isSlotChangeRequired: false,
                                bidType: "Exact",
                            },
                        },
                    },
                },
                features: {
                    [Features.BULK_TGT_ACTION]: {
                        name: "Bulk Target Actions",
                    },
                },
            },
            [ParentModule.PRODUCT_ANALYTICS]: {
                name: "Product Analytics",
                isSubheaderVisible: true,
                actions: [Actions.BID_CHANGE, Actions.STATUS_CHANGE],
                columns: [
                    "sku_title",
                    "asin",
                    "",
                    "isFocusedAsin",
                    "is_available",
                    // New metrics
                    "sov_overall_pct",
                    "sov_organic_pct",
                    "sov_paid_pct",
                    "ms_by_price_pct",
                    "ms_by_mrp_pct",
                    "click",
                    "spend",
                    "impression",
                    "orders",
                    "sale",
                    "cpc",
                    "cr",
                    "cpa",
                    "acos",
                    "roas",
                    // New averages near pricing/availability
                    // "price",
                    "avg_price",
                    // "availability_percent",
                    "weighted_availability",
                    "cpm",
                    "tag_types",
                    "sku_tags",
                ],
                gridParams: {
                    columnsToHide: ["asin", "is_available", "cpc", "cr", "cpa"],
                },
                subGrids: {
                    keywords: {
                        columns: [
                            "keyword_name",
                            "bid",
                            "state",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "match_type",
                            "record_type",
                            "program_type",
                            "campaign_name",
                            "cpm",
                            "targetingtype",
                        ],
                        gridParams: {
                            rowHeight: 65,
                            isBadgeRequired: true,
                            isTypeBadgeRequired: true,
                            bid: {
                                cellRenderer: PopOverBidCellRenderer,
                                minWidth: 150,
                                maxWidth: 160,
                                maxBidLimitRecordTypes: [
                                    RecordType.KEYWORD,
                                    RecordType.TARGET,
                                ],
                                canBidInputBeDisabled: true,
                                isSlotChangeRequired: false,
                                bidType: "Exact",
                            },
                        },
                    },
                },
                features: {
                    [Features.TAGS_ADDITION]: {
                        name: "Add Tags",
                    },
                },
            },
            [ParentModule.PRODUCT_DEEPDIVE]: {
                name: "Product Deepdive",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.COMMON_REPORTS]: {
                name: "Common Reports",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.REVIEW_ANALYTICS]: {
                name: "Review Analytics",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.REVSTACK]: {
                name: "Custom Reports",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.NAVIGATOR]: {
                name: "Navigator",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.CATEGORY_ANALYSIS]: {
                name: "Category Analysis",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.PRODUCT_ANALYSIS]: {
                name: "Product Analysis",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.CHANGE_LOG]: {
                name: "History",
                isSubheaderVisible: true,
                actions: [],
                columns: [
                    "log_date_iso",
                    "log_date",
                    "log_date",
                    "revert_action",
                    "module",
                    "change_type",
                    "record_name",
                    "record_type",
                    "attribute",
                    "old_value",
                    "new_value",
                    "source",
                    "nature",
                    "source_name",
                    "source_id",
                    "log_time",
                ],
                features: {
                    [Features.REVERT_ACTION]: {
                        name: "Revert Action",
                    },
                },
            },
            [ParentModule.REPORTS_BUILDER]: {
                name: "Reports Builder",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.SETTINGS]: {
                name: "Settings",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
                additionalFeatures: {
                    "buybox-add-sellers": false,
                },
            },
            [ParentModule.ONEDER]: {
                name: "1der ai",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
                additionalFeatures: {},
            },
            [ParentModule.CHATBOT]: {
                name: "Chatbot",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
                additionalFeatures: {},
            },
            [ParentModule.ADMIN_SETTINGS]: {
                name: "Admin Settings",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
        },
        dropdownOptions: {
            targetTypeOptions: [],
            matchTypeOptions: [
                { value: "exact", label: "Exact" },
                { value: "phrase", label: "Phrase" },
            ],
            negativeMatchTypeOptions: [
                { value: "exact", label: "Exact" },
                { value: "phrase", label: "Phrase" },
            ],
            programTypeOptions: [
                { value: "sp", label: "SP" },
                { value: "sb", label: "SB" },
            ],
            kaCampaignTypeOptions: [{ value: "manual", label: "Manual" }],
            chartFilterOptions: [
                { value: "sp", label: "SP" },
                { value: "sb", label: "SB" },
                { value: "all", label: "All" },
            ],
        },
    },
    [ChannelName.DEFAULT]: {
        options: {
            decimalValue: 2,
            recommendedBid: 10,
            isTargetCheckRequired: false,
            formatText: {
                program_type: {
                    sp: "SP",
                },
                record_type: {
                    keyword: "Keyword",
                    target: "Product",
                },
            },
            platformFeatures: [
                Features.DAY_PARTING,
                Features.REVSTACK,
                Features.CATEGORY_ANALYSIS,
                Features.PRODUCT_ANALYSIS,
                Features.NAVIGATOR,
            ],
        },
        modules: {
            [ParentModule.PERFORMANCE_OVERVIEW]: {
                name: "Performance Overview",
                isSubheaderVisible: true,
                subModules: {
                    [SubModule.OVERVIEW]: {
                        name: "Overview",
                        actions: [],
                        columns: [],
                        features: {},
                    },
                    [SubModule.PORTFOLIO]: {
                        name: "Portfolio",
                        actions: [],
                        columns: [
                            "portfolio_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "acos",
                        ],
                        features: {},
                    },
                    [SubModule.CAMPAIGN]: {
                        name: "Campaigns",
                        gridParams: {
                            budget: {
                                minWidth: 170,
                                maxWidth: 190,
                            },
                            program_type: {
                                minWidth: 80,
                                maxWidth: 80,
                            },
                            campaign_name: {
                                minWidth: 320,
                                maxWidth: 500,
                            },
                            isCpmChangeAllowed: false,
                            isBudgetReduceAllowed: true,
                            isDateChangeAllowed: false,
                            isDateReduceAllowed: true,
                            isArchivedCampaignsSelectionAllowed: false,
                        },
                        actions: [],
                        componentConfig: {
                            till_budget_ends: {
                                isVisible: false,
                            },
                            end_date: {
                                isVisible: false,
                            },
                            equally_divide_budget: {
                                isVisible: false,
                            },
                            cpm: {
                                isVisible: false,
                            },
                        },
                        // Arr of columns to show (keys:string to be used in column defs)
                        columns: [
                            "campaign_name",
                            "budget",
                            "record_type",
                            "portfolio_name",
                            "program_type",
                            "automation",
                            "state",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "top_product",
                            "percent_spend",
                            "sys_campaign_id",
                            "portfolio_id",
                        ],
                        features: {},
                    },
                    [SubModule.ADGROUP]: {
                        name: "AdGroups",
                        actions: [],
                        // Arr of columns to show (keys:string to be used in column defs)
                        columns: [
                            "ad_group_name",
                            "state",
                            "campaign_name",
                            "portfolio_name",
                            "program_type",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "top_product",
                            "percent_spend",
                            "sys_campaign_id",
                            "sys_adgroup_id",
                            "asin_id",
                            "portfolio_id",
                        ],
                        features: {},
                    },
                    [SubModule.KEYWORD]: {
                        name: "Keywords",
                        actions: [],
                        gridParams: {
                            bid: {
                                cellRenderer: UpdateBidOrBudgetCellRenderer,
                                minWidth: 170,
                                maxWidth: 190,
                                filterOptions: [
                                    "equals",
                                    "notEqual",
                                    "greaterThan",
                                    "lessThan",
                                    "greaterThanOrEqual",
                                    "lessThanOrEqual",
                                ],
                            },
                        },
                        // Arr of columns to show (keys:string to be used in column defs)
                        columns: [
                            "keyword_name",
                            "is_archived_entity",
                            "bid",
                            "ad_group_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "top_product",
                            "percent_spend",
                            "creation_date",
                            "match_type",
                            "record_type",
                            "kwd_class",
                            "program_type",
                            "campaign_name",
                            "portfolio_name",
                            "portfolio_id",
                            "sys_campaign_id",
                            "sys_adgroup_id",
                            "asin_id",
                            "sys_ams_keyword_id",
                        ],
                        features: {},
                    },
                    [SubModule.PRODUCT]: {
                        name: "Products",
                        actions: [],
                        // Arr of columns to show (keys:string to be used in column defs)
                        columns: [
                            "sku_title",
                            "ad_group_name",
                            "campaign_name",
                            "portfolio_name",
                            "program_type",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "top_product",
                            "percent_spend",
                            "portfolio_id",
                            "sys_campaign_id",
                            "sys_adgroup_id",
                            "asin_id",
                        ],
                        features: {},
                        gridParams: {
                            rowHeight: 65,
                        },
                    },
                },
            },
            [ParentModule.RULES]: {
                name: "Rules",
                isSubheaderVisible: false,
                // Arr of columns to show (keys:string to be used in column defs)
                columns: [
                    "rule_id",
                    "name",
                    "type",
                    "filter",
                    "module",
                    "cron_string",
                    "created_at",
                    "created_by",
                    "updated_at",
                    "updated_by",
                    "status",
                    "",
                ],
                features: {},
            },
            [ParentModule.KEYWORD_ANALYTICS]: {
                name: "Search Term Analytics",
                isSubheaderVisible: true,
                gridParams: {
                    isKeywordLinkRequired: true,
                    isAdgroupSelectionRequired: false,
                    isBidRequired: false,
                    isMatchTypeReviewRequired: false,
                    isNegativeAdditionAllowed: true,
                    isMatchTypeSelectionRequired: true,
                    columnsToHide: [],
                    bidColumn: "cpc",
                    keywordColumn: "keyword_name",
                    rowHeight: 65,
                },
                actions: [],
                columns: [
                    "keyword_name",
                    "program_type",
                    "kwd_class",
                    "is_exact",
                    "impression_share_rank",
                    "impression_share",
                    "page1OverallSov",
                    "page1OrganicSov",
                    "total_impression",
                    "top_product",
                    "rank",
                    "impression",
                    "click",
                    "spend",
                    "orders",
                    "sale",
                    "ctr",
                    "cpc",
                    "cr",
                    "cpa",
                    "acos",
                    "roas",
                ],
                subGrids: {
                    keywords: {
                        columns: [
                            "keyword_name",
                            "bid",
                            "state",
                            "kwd_class",
                            "ad_group_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "top_product",
                            "percent_spend",
                            "creation_date",
                            "match_type",
                            "record_type",
                            "program_type",
                            "campaign_name",
                            "sys_campaign_id",
                            "sys_adgroup_id",
                            "asin_id",
                        ],
                        gridParams: {
                            isBadgeRequired: false,
                            canChangeKeywordStatus: true,
                            bid: {
                                cellRenderer: UpdateBidOrBudgetCellRenderer,
                                minWidth: 150,
                                maxWidth: 170,
                            },
                        },
                    },
                    campaigns: {
                        columns: [
                            "customer_search_term",
                            "keyword_name",
                            "campaign_name",
                            "ad_group_name",
                            "match_type",
                            "spend",
                            "orders",
                            "sale",
                            "impression",
                            "click",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                        ],
                    },
                    addAsExact: {
                        columns: [
                            "keyword_name",
                            "cpc",
                            "campaign_name",
                            "ad_group_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "cr",
                            "cpa",
                            "acos",
                            "program_type",
                            "roas",
                            "match_type",
                        ],
                    },
                },
                features: {},
            },
            [ParentModule.PRODUCT_ANALYTICS]: {
                name: "Product Analytics",
                isSubheaderVisible: true,
                gridParams: {
                    columnsToHide: [
                        "asin",
                        "is_available",
                        "click",
                        "impression",
                        "orders",
                        "cpc",
                        "cr",
                        "cpa",
                        "acos",
                    ],
                },
                actions: [],
                columns: [
                    "sku_title",
                    "asin",
                    "",
                    "category",
                    "isFocusedAsin",
                    "is_available",
                    // New metrics
                    "sov_overall_pct",
                    "sov_organic_pct",
                    "sov_paid_pct",
                    "ms_by_price_pct",
                    "ms_by_mrp_pct",
                    "ag_group_nos",
                    // "availability_percent",
                    "bsr",
                    "click",
                    "spend",
                    "impression",
                    "orders",
                    "sale",
                    "ctr",
                    "cpc",
                    "cr",
                    "cpa",
                    "acos",
                    "tacos",
                    "roas",
                    // New averages near pricing/availability
                    // "price",
                    "avg_price",
                    "weighted_availability",
                    "bb_winner",
                    "rating",
                    "rating_count",
                    // "price",
                    "tag_types",
                    "sku_tags",
                ],
                subGrids: {
                    keywords: {
                        columns: [
                            "keyword_name",
                            "bid",
                            "state",
                            "kwd_class",
                            "percent_spend_in_adgroup",
                            "rank",
                            "ad_group_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                            "match_type",
                            "record_type",
                            "program_type",
                            "campaign_name",
                        ],
                        gridParams: {
                            isBadgeRequired: false,
                            bid: {
                                cellRenderer: UpdateBidOrBudgetCellRenderer,
                                minWidth: 150,
                                maxWidth: 170,
                            },
                        },
                    },
                    adgroups: {
                        columns: [
                            "campaign_name",
                            "ad_group_name",
                            "sku_id",
                            "state",
                            "percent_spend",
                            "click",
                            "spend",
                            "impression",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                        ],
                    },
                },
                features: {
                    [Features.TAGS_ADDITION]: {
                        name: "Add Tags",
                    },
                },
            },
            [ParentModule.PRODUCT_DEEPDIVE]: {
                name: "Product Deepdive",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.NEGATIVE_KEYWORDS]: {
                name: "Negative Keywords",
                isSubheaderVisible: true,
                gridParams: {
                    program_type: {
                        minWidth: 80,
                        maxWidth: 80,
                    },
                },
                actions: [],
                columns: [
                    "customer_search_term",
                    "",
                    "ad_group_name",
                    "adgroup_type",
                    "nature",
                    "program_type",
                    "campaign_name",
                    "impression",
                    "click",
                    "spend",
                    "orders",
                    "sale",
                    "ctr",
                    "cpc",
                    "cr",
                    "cpa",
                    "acos",
                    "roas",
                ],
                features: {},
            },
            [ParentModule.KEYWORD_RECOMMENDATIONS]: {
                name: "Keywords",
                isSubheaderVisible: true,
                subModules: {
                    [SubModule.KEYWORD_MANUAL]: {
                        name: "Keywords from Manual",
                        slug: "manual",
                        actions: [],
                        columns: [
                            "customer_search_term",
                            "cpc",
                            "ad_group_name",
                            "campaign_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cr",
                            "cpc",
                            "cpa",
                            "acos",
                            "roas",
                        ],
                        features: {},
                    },
                    [SubModule.KEYWORD_AUTO]: {
                        name: "Keywords from Auto",
                        slug: "auto",
                        actions: [],
                        columns: [
                            "search_term",
                            "bid",
                            "adgroup_name",
                            "campaign_name",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cr",
                            "cpc",
                            "cpa",
                            "acos",
                            "roas",
                        ],
                        features: {},
                    },
                    [SubModule.KEYWORD_REVINITI]: {
                        name: "Reviniti Recommedations",
                        slug: "reviniti",
                        actions: [],
                        columns: [
                            "search_term",
                            "bid",
                            "adgroup_name",
                            "campaign_name",
                            "targeting_class",
                            "targeting_type",
                            "match_type",
                            "program_type",
                            "",
                        ],
                        features: {},
                    },
                },
            },
            [ParentModule.CAMPAIGN_RECOMMENDATIONS]: {
                name: "Campaigns",
                isSubheaderVisible: true,
                subModules: {
                    [SubModule.CAMPAIGN_BUDGET]: {
                        name: "Budget Recommendations",
                        slug: "budget",
                        actions: [],
                        columns: [
                            "campaign_name",
                            "record_type",
                            "budget",
                            "percentSpentout",
                            "impression",
                            "click",
                            "spend",
                            "orders",
                            "sale",
                            "ctr",
                            "cpc",
                            "cr",
                            "cpa",
                            "acos",
                            "roas",
                        ],
                        features: {},
                    },
                    [SubModule.CAMPAIGN_PLACEMENT]: {
                        name: "Placement Recommendations",
                        slug: "placement",
                        actions: [],
                        columns: [
                            "campaign_name",
                            "top_of_search",
                            "product_page",
                            "rest_of_search",
                            "total",
                        ],
                        features: {},
                    },
                },
            },
            [ParentModule.REVIEW_ANALYTICS]: {
                name: "Review Analytics",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.REVSTACK]: {
                name: "Custom Reports",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.NAVIGATOR]: {
                name: "Navigator",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.CATEGORY_ANALYSIS]: {
                name: "Category Analysis",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [SubModule.PRODUCT_ANALYSIS]: {
                name: "Product Analysis",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.COMMON_REPORTS]: {
                name: "Common Reports",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.CHANGE_LOG]: {
                name: "History",
                isSubheaderVisible: false,
                actions: [],
                columns: [
                    "log_date_iso",
                    "log_date",
                    "log_date",
                    "module",
                    "change_type",
                    "record_name",
                    "record_type",
                    "attribute",
                    "old_value",
                    "new_value",
                    "source",
                    "nature",
                    "source_name",
                    "source_id",
                    "log_time",
                ],
                features: {},
            },
            [ParentModule.REPORTS_BUILDER]: {
                name: "Reports Builder",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
            [ParentModule.SETTINGS]: {
                name: "Settings",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {
                    // [Features.BULK_PRODUCT_MAPPING]: {
                    //     name: "Bulk Mapping",
                    // },
                    [Features.ADD_MAPPING]: {
                        name: "Add Mapping",
                    },
                    [Features.APPLY_1DS_MAPPING]: {
                        name: "Apply 1DS Mapping",
                    },
                },
                additionalFeatures: {
                    "buybox-add-sellers": true,
                },
            },
            [ParentModule.ONEDER]: {
                name: "1der ai",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
                additionalFeatures: {},
            },
            [ParentModule.CHATBOT]: {
                name: "Ask AI",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
                additionalFeatures: {},
            },
            [ParentModule.ADMIN_SETTINGS]: {
                name: "Admin Settings",
                isSubheaderVisible: false,
                actions: [],
                columns: [],
                features: {},
            },
        },
        dropdownOptions: {
            targetTypeOptions: [],
            matchTypeOptions: [{ value: "exact", label: "Exact" }],
            negativeMatchTypeOptions: [{ value: "exact", label: "Exact" }],
            programTypeOptions: [{ value: "default", label: "default" }],
            kaCampaignTypeOptions: [{ value: "default", label: "default" }],
            chartFilterOptions: [{ value: "all", label: "All" }],
        },
    },
};

export const spinnerHtml = `<div id="bulkActionSpinner" class="spinner-border" role="status" style="color: #9E9FA5;"></div>`;

export const accountOptions = [
    {
        value: "",
        label: "--- select account type ---",
    },
    {
        value: EAccountType.SELLER,
        label: "Seller",
    },
    {
        value: EAccountType.VENDOR,
        label: "Vendor",
    },
];

export const regionOptions = [
    {
        value: "",
        label: "--- Select Region ---",
    },
    {
        label: "North America (NA)",
        value: "NA",
        key: "North America (NA)",
    },
    {
        label: "Europe (EU)",
        value: "EU",
        key: "Europe (EU)",
    },
    {
        label: "Far East (FE)",
        value: "FE",
        key: "Far East (FE)",
    },
];

export const DISABLED_STATES = [
    "completed",
    "aborted",
    "archived",
    "stopped",
    "campaign_archived",
    "campaign_status_stopped",
    "campaign_status_booked",
    "non_editable",
    "ended",
    "ad_group_archived",
];

export const DISABLED_PROGRAM_TYPES = [ProgramType.Awareness];

export const DISABLED_PLACEMENTS = [
    PlacementTypeNames.ROSL,
    PlacementTypeNames.TOSLP,
];

export const DIVIDE_BUDGET_PROGRAM_TYPES = [ProgramType.PLA, ProgramType.SP];

export const MODULES_REQ_KEYWORDS = [
    SubModule.KEYWORD,
    ParentModule.PRODUCT_ANALYTICS,
    ParentModule.KEYWORD_ANALYTICS,
    SubModule.PLACEMENTS,
];

export const KEYWORD_MATCH_TYPES = [
    "broad",
    "exact",
    "phrase",
    "smart",
    "none",
];

export const DISABLED_MATCH_TYPES = [
    "exact",
    "targeting_expression_predefined",
];

//TODO: Add it to moduleConfig during maintenance (might have to create a separate function cause useGetModulesData hooke cant be used in a normal ts file)...
export const gridUpdateConfig: Partial<
    Record<Modules, IGridUpdateConfigParams>
> = {
    campaigns: {
        setGridState: setCampaignsData,
        paramToFilter: "sys_campaign_id",
        gridsToUpdateParams: {
            campaign_tags: [
                {
                    gridData: "keywordsData",
                    setGridState: setKeywordsData,
                },
                {
                    gridData: "placementsData",
                    setGridState: setPlacementsData,
                },
            ],
            campaign_name: [
                {
                    gridData: "adgroupsData",
                    setGridState: setAdgroupsData,
                },
                {
                    gridData: "keywordsData",
                    setGridState: setKeywordsData,
                },
                {
                    gridData: "placementsData",
                    setGridState: setPlacementsData,
                },
            ],
        },
    },
    keywords: {
        setGridState: setKeywordsData,
        paramToFilter: "sys_ams_keyword_id",
        gridsToUpdateParams: {},
    },
    placements: {
        setGridState: setPlacementsData,
        paramToFilter: "sys_ams_keyword_id",
        gridsToUpdateParams: {},
    },
    portfolios: {
        setGridState: setPortfoliosData,
        paramToFilter: "portfolio_id",
        gridsToUpdateParams: {
            portfolio_name: [
                {
                    gridData: "campaignsData",
                    setGridState: setCampaignsData,
                },
                {
                    gridData: "adgroupsData",
                    setGridState: setAdgroupsData,
                },
                {
                    gridData: "keywordsData",
                    setGridState: setKeywordsData,
                },
                {
                    gridData: "placementsData",
                    setGridState: setPlacementsData,
                },
            ],
        },
    },
    adgroups: {
        setGridState: setAdgroupsData,
        paramToFilter: "sys_adgroup_id",
        gridsToUpdateParams: {
            ad_group_name: [
                {
                    gridData: "keywordsData",
                    setGridState: setKeywordsData,
                },
                {
                    gridData: "placementsData",
                    setGridState: setPlacementsData,
                },
            ],
        },
    },
    budget: {
        setGridState: setBudgetRecommendationData,
        paramToFilter: "sys_campaign_id",
        gridsToUpdateParams: {},
    },
    placement: {
        setGridState: setPlacementRecommendationData,
        paramToFilter: "sys_campaign_id",
        gridsToUpdateParams: {},
    },
    "product-analytics": {
        setGridState: setProductAnalyticsData,
        paramToFilter: "asin",
        gridsToUpdateParams: {},
    },
    settings: {
        setGridState: setProductMappingData,
        paramToFilter: "sku",
        gridsToUpdateParams: {},
    },
    products: {
        setGridState: setProductsData,
        paramToFilter: "ad_id",
        gridsToUpdateParams: {},
    },
};

export const modulePayloadMapping: {
    [key: string]: Array<string>;
} = {
    portfolios: ["portfolios"],
    campaigns: ["campaigns"],
    adgroups: ["adgroups"],
    keywords: ["keywords"],
    placements: ["placements"],
    "keyword-analytics": ["keywords", "targets"],
    "product-analytics": ["keywords", "productads", "campaigns"],
    suggested: ["keywords"],
    existing: ["keywords"],
    manual: ["keywords"],
    reviniti: ["keywords"],
    budget: ["campaigns"],
    placement: ["campaigns"],
};

export const DEFAULT_CAMPAIGN_NAME_REGEX =
    /[^|$&(),.*:/;=?@+[\]~{}_0-9a-zA-Z -]|&|₹/g;

export const RULE_OPTION_CONFIG: Partial<Record<Actions, TRuleOptionConfig>> = {
    [Actions.MARK_NEGATIVE]: {
        value: "markNegative",
        text: "Add as Negative Exact",
    },
    [Actions.PAUSE]: {
        value: "pause",
        text: "Pause",
    },
    [Actions.PAUSE_KEYWORDS]: {
        value: "pauseKeywords",
        text: "Pause Keywords",
    },
    [Actions.PAUSE_ADS]: {
        value: "pauseAds",
        text: "Pause Ads",
    },
    [Actions.INCREASE_BID_PERC]: {
        value: "percentIncrease",
        text: "Increase Bid %",
    },
    [Actions.DECREASE_BID_PERC]: {
        value: "percentDecrease",
        text: "Decrease Bid %",
    },
    [Actions.MOVE_TO_SUGG_MIN]: {
        value: "moveToSuggestedMin",
        text: "Move to Suggested Min",
    },
    [Actions.MOVE_TO_SUGG_MAX]: {
        value: "moveToSuggestedMax",
        text: "Move to Suggested Max",
    },
    [Actions.MOVE_TO_SUGG_AVG]: {
        value: "moveToSuggestedAvg",
        text: "Move to Suggested Avg",
    },
};

export const RULE_MODULE_MAPPING: Partial<
    Record<SubModule | ParentModule, ParentModule>
> = {
    [SubModule.KEYWORD]: ParentModule.PERFORMANCE_OVERVIEW,
    [ParentModule.KEYWORD_ANALYTICS]: ParentModule.KEYWORD_ANALYTICS,
    [ParentModule.PRODUCT_ANALYTICS]: ParentModule.PRODUCT_ANALYTICS,
    [ParentModule.NEGATIVE_KEYWORDS]: ParentModule.NEGATIVE_KEYWORDS,
};

//endpoints that should not be validated if sent twice
export const SKIP_REQ_VALIDATION_ROUTES = [
    "validate",
    "list",
    "taxonomy",
    "review",
    "getRule",
];

export const TAGS_MODULE_MAPPING: TagsMap = {
    campaigns: {
        recordId: "sys_campaign_id",
        recordName: "campaign_name",
        isAddTagsAllowed: true,
        updateMethod: "PUT",
        updatePath: "campaigns/tags",
        updateKey: "sysCampaignId",
        colName: "campaign_tags",
        recordType: "campaign",
        selectedData: "campaignsSelectionData",
        gridApi: "campaignsGridApi",
        isExternalTagging: false,
        validRecordTypes: [
            GlobalTagRecordType.GLOBAL,
            GlobalTagRecordType.CAMPAIGN,
        ],
    },
    "product-analytics": {
        recordId: "asin",
        recordName: "sku_title",
        isAddTagsAllowed: true,
        updateMethod: "POST",
        updatePath: "analytics/products/tags",
        updateKey: "skuId",
        colName: "sku_tags",
        recordType: "product",
        selectedData: "productsSelectionData",
        isExternalTagging: false,
        validRecordTypes: [
            GlobalTagRecordType.GLOBAL,
            GlobalTagRecordType.PRODUCT,
        ],
    },
    keywords: {
        recordId: "sys_ams_keyword_id",
        recordName: "keyword_name",
        colName: "campaign_tags",
        isAddTagsAllowed: false,
        recordType: "campaign",
        isExternalTagging: false,
        validRecordTypes: [
            GlobalTagRecordType.GLOBAL,
            GlobalTagRecordType.CAMPAIGN,
        ],
    },
    placements: {
        recordId: "sys_ams_keyword_id",
        recordName: "keyword_name",
        colName: "campaign_tags",
        isAddTagsAllowed: false,
        recordType: "campaign",
        isExternalTagging: false,
        validRecordTypes: [
            GlobalTagRecordType.GLOBAL,
            GlobalTagRecordType.CAMPAIGN,
        ],
    },
    settings: {
        recordId: "sku",
        recordName: "title",
        isAddTagsAllowed: true,
        updateMethod: "POST",
        updatePath: "analytics/products/tags",
        updateKey: "skuId",
        colName: "sku_tags",
        recordType: "product",
        selectedData: "productMappingData",
        isExternalTagging: true,
        validRecordTypes: [GlobalTagRecordType.PRODUCT],
    },
};

export const DATE_BUDGET_UPDATE_CONFIG: TBudgetUpdate = {
    campaigns: {
        updateFunc: "updateCampaigns",
        updateRecordId: "sys_campaign_id",
        updateRecordKey: "campaignId",
        recordNameKey: "campaign_name",
        selectedData: "campaignsSelectionData",
    },
    adgroups: {
        updateFunc: "updateAdGroups",
        updateRecordId: "sys_adgroup_id",
        updateRecordKey: "adgroupId",
        recordNameKey: "ad_group_name",
        selectedData: "adgroupSelectionData",
    },
    keywords: {
        updateFunc: "updateKeywords",
        updateRecordId: "sys_ams_keyword_id",
        updateRecordKey: "keywordId",
        recordNameKey: "keyword_name",
    },
    placements: {
        updateFunc: "updatePlacements",
        updateRecordId: "sys_ams_keyword_id",
        updateRecordKey: "placementId",
        recordNameKey: "keyword_name",
    },
};

export const REMOVE_ENTITY_CONFIG: TRemoveEntity = {
    //neg keyword remove
    existing: {
        removeFunc: "updateNegativeKeywords",
        recordId: "sys_neg_kw_id",
        recordKey: "keywordId",
        recordNameKey: "",
        selectedData: "",
    },
    // sku remove
    "product-analytics": {
        removeFunc: "updateProductAds",
        recordId: "ad_id",
        recordKey: "productads",
        recordNameKey: "",
        selectedData: "",
    },
};

export const STATE_SUBSCRIBE_CONFIG: TSubscribeState = {
    dailyBudget: {
        key: "dailyBudgetUpdateResponse",
        message: "Daily budget config has been updated",
    },
    state: {
        key: "campaignStateActionResponse",
        message: "Campaign update request has been set",
    },
};

export const DATA_VALUE_MAPPING: Record<GoalDataLevels, string> = {
    "Campaign Tag": "campaignTagsList",
    "Product Tag": "productTagsList",
    Portfolio: "portfoliosList",
    Campaign: "campaignsList",
    "Ad Group": "adgroupsList",
    Keyword: "keywordsList",
    Product: "productsList",
    "Search Term": "searchTermsList",
    Account: "",
};

export const GOAL_STATUS_CONFIG: Record<GoalStatus, GoalStatusConfig> = {
    achieved: {
        text: "Achieved",
        color: "#14C38E",
    },
    unachieved: {
        text: "Not Achieved",
        color: "#FB3640",
    },
};

export const productTrendAnalysisChartOrder = [
    "Total Sales",
    "Sales",
    "Spends",
    "ROAS",
    "Availability",
    "Price Trend (LC)",
    "Organic Impressions",
    "Paid Impressions",
];

export const DEFAULT_TAG_TYPES: Record<string, string[]> = {
    campaign: ["Intent"],
    product: [],
    global: ["Category", "Subcategory", "Brand"],
};

export const MISC_PA_REPORT_WIDGET = {
    reviewsCount: 0,
    ratingsCount: 0,
    latestBsrRank: null as null | number,
    latestBsrCategory: null as null | string,
    msValue: null as null | string,
    paidVisibility: null as null | string,
    organicVisibility: null as null | string,
};

export const DEEPDIVE_METRICS = [
    "BSR",
    "Availability %",
    "Paid SOV",
    "Organic SOV",
    "Avg Price",
    "Avg Discount",
    "Price Range",
    "Discount Range",
    "Rating",
    "#Ratings",
];

export const REVIEWS_METRICS = [
    "Rating",
    "#Ratings",
    "#Reviews",
    "Avg Price",
    "BSR",
];

export const RATINGS_DEFAULT_DATA = {
    rating: 0,
    rating_count: 0,
    ratingDistribution: {
        "5 stars": 0,
        "4 stars": 0,
        "3 stars": 0,
        "2 stars": 0,
        "1 star": 0,
    },
};

export const ALL_OPTIONS = { value: "all", label: "All" };
export const UNSELECT_ALL_OPTIONS = { value: "**", label: "Unselect All" };
export const DEFAULT_REVIEW_OPTIONS: IFixedReviewOptions = {
    categories: [],
    subCategories: [],
    skus: [],
};

export const routePatterns = [
    `/${Platforms.PLANNING}/revstack/:id`,
    `/${Platforms.ACTIVATION}/common-reports/:id`,
    `/${ParentModule.ADMIN_SETTINGS}/:tab`,
    `/${ParentModule.SETTINGS}/:tab`,
];

export enum CommonPermissionsActions {
    CAN_DEEPDIVE = "can_deepdive",
    CAN_EXPORT = "can_export",
    CAN_MANAGE_ADMIN_SETTINGS = "can_manage_admin_settings",
    CAN_ACCESS_IOT = "can_access_iot",
    CAN_ACCESS_REVSCOUT = "can_access_revscout",
    CAN_UPDATE_SETTINGS = "can_update_settings",
    CAN_PERFORM_BULK_ACTIONS = "can_perform_bulk_actions",
}

export enum RulesActions {
    LIST_RULE = "list_rule",
    CREATE_RULE = "create_rule",
    UPDATE_RULE = "update_rule",
    READ_RULE = "read_rule",
    DELETE_RULE = "delete_rule",
}

export enum SearchTermAnalyticsActions {
    LIST_SEARCHTERM = "list_searchterm",
}

export enum ProductsActions {
    LIST_PRODUCTS = "list_products",
    READ_PRODUCT = "read_product",
    UPDATE_PRODUCT = "update_product",
}

export enum ReportsActions {
    VIEW_REPORT = "view_report",
    ADD_REPORT = "add_report",
    DELETE_REPORT = "delete_report",
    UPDATE_REPORT = "update_report",
    LIST_REPORTS = "list_reports",
}

export enum HistoryActions {
    CAN_VIEW_HISTORY = "can_view_history",
    VIEW_REQUEST_LOGS = "view_request_logs",
    REVERT_ACTION = "revert_action",
}

export enum AdgroupsActions {
    LIST_ADGROUP = "list_adgroup",
    READ_ADGROUP = "read_adgroup",
    UPDATE_ADGROUP = "update_adgroup",
}

export enum CampaignsActions {
    LIST_CAMPAIGN = "list_campaign",
    READ_CAMPAIGN = "read_campaign",
    UPDATE_CAMPAIGN = "update_campaign",
    CREATE_CAMPAIGN = "create_campaign",
}

export enum GoalsActions {
    LIST_GOALS = "list_goals",
    CREATE_GOAL = "create_goal",
    EDIT_GOAL = "edit_goal",
    DELETE_GOAL = "delete_goal",
}

export enum KeywordsActions {
    LIST_NEGATIVE = "list_negative",
    LIST_KEYWORD = "list_keyword",
    READ_KEYWORD = "read_keyword",
    UPDATE_KEYWORD = "update_keyword",
    CREATE_KEYWORD = "create_keyword",
    CREATE_NEGATIVE = "create_negative",
    DELETE_NEGATIVE = "delete_negative",
}

export enum PortfoliosActions {
    LIST_PORTFOLIOS = "list_Portfolios",
    UPDATE_PORTFOLIO = "update_portfolio",
}

export enum TagsActions {
    CREATE_TAG = "create_tag",
    LIST_TAGS = "list_tags",
    DELETE_TAG = "delete_tag",
    CAN_ASSIGN_TAGS = "can_assign_tags",
}

export enum TagTypesActions {
    CREATE_TAG_TYPE = "create_tag_type",
    LIST_TAG_TYPE = "list_tag_type",
    DELETE_TAG_TYPE = "delete_tag_type",
}

export enum UsersActions {
    DELETE_USER = "delete_user",
    CREATE_USER = "create_user",
    UPDATE_USER = "update_user",
    READ_USER = "read_user",
    LIST_USER = "list_user",
    RESET_PASSWORD = "reset_password",
    REACTIVATE_USER = "reactivate_user",
}

export enum RolesActions {
    READ_ROLE = "read_role",
    DELETE_ROLE = "delete_role",
    UPDATE_ROLE = "update_role",
    CREATE_ROLE = "create_role",
}

export enum PermissionsActions {
    LIST_PERMISSIONS = "list_permissions",
    UPDATE_PERMISSION = "update_permission",
}

export enum DayPartingActions {
    CAN_MANAGE_DAYPARTING = "can_manage_dayparting",
}

export enum BuyboxActions {
    CREATE_BUYBOX = "create_buybox",
    DELETE_BUYBOX = "delete_buybox",
}
export enum ClientActions {
    SHOW_ALL_CLIENTS = "show_all_clients",
    LIST_CLIENTS = "list_clients",
    CREATE_CLIENT = "create_client",
    UPDATE_CLIENT = "update_client",
    DELETE_CLIENT = "delete_client",
}
export enum AssetsActions {
    LIST_ASSETS = "list_assets",
    CREATE_ASSET = "create_asset",
}

export enum AlertActions {
    LIST_ALERTS = "list_alerts",
}
export enum ReviewActions {
    CAN_VIEW_REVIEW = "can_view_review",
}

export const cerbosCanListAndCommonPermissions = [
    {
        resource: {
            id: "common_permissions",
            kind: "common_permissions",
        },
        actions: [
            CommonPermissionsActions.CAN_DEEPDIVE,
            CommonPermissionsActions.CAN_EXPORT,
            CommonPermissionsActions.CAN_MANAGE_ADMIN_SETTINGS,
            CommonPermissionsActions.CAN_ACCESS_IOT,
            CommonPermissionsActions.CAN_ACCESS_REVSCOUT,
            CommonPermissionsActions.CAN_UPDATE_SETTINGS,
            CommonPermissionsActions.CAN_PERFORM_BULK_ACTIONS,
        ],
    },
    {
        resource: {
            id: "rules",
            kind: "rules",
        },
        actions: [RulesActions.LIST_RULE, RulesActions.CREATE_RULE],
    },
    {
        resource: {
            id: "search_term_analytics",
            kind: "search_term_analytics",
        },
        actions: [SearchTermAnalyticsActions.LIST_SEARCHTERM],
    },
    {
        resource: {
            id: "products",
            kind: "products",
        },
        actions: [ProductsActions.LIST_PRODUCTS],
    },
    {
        resource: {
            id: "reports",
            kind: "reports",
        },
        actions: [ReportsActions.VIEW_REPORT],
    },
    {
        resource: {
            id: "history",
            kind: "history",
        },
        actions: [
            HistoryActions.CAN_VIEW_HISTORY,
            HistoryActions.VIEW_REQUEST_LOGS,
        ],
    },
    {
        resource: {
            id: "adgroups",
            kind: "adgroups",
        },
        actions: [AdgroupsActions.LIST_ADGROUP],
    },
    {
        resource: {
            id: "campaigns",
            kind: "campaigns",
        },
        actions: [CampaignsActions.LIST_CAMPAIGN],
    },
    {
        resource: {
            id: "goals",
            kind: "goals",
        },
        actions: [
            GoalsActions.LIST_GOALS,
            GoalsActions.CREATE_GOAL,
            GoalsActions.EDIT_GOAL,
            GoalsActions.DELETE_GOAL,
        ],
    },
    {
        resource: {
            id: "keywords",
            kind: "keywords",
        },
        actions: [KeywordsActions.LIST_NEGATIVE, KeywordsActions.LIST_KEYWORD],
    },
    {
        resource: {
            id: "portfolios",
            kind: "portfolios",
        },
        actions: [PortfoliosActions.LIST_PORTFOLIOS],
    },
    {
        resource: {
            id: "alerts",
            kind: "alerts",
        },
        actions: [AlertActions.LIST_ALERTS],
    },
    {
        resource: {
            id: "reviews",
            kind: "reviews",
        },
        actions: [ReviewActions.CAN_VIEW_REVIEW],
    },
];

export const cerbosResourcesConfig: Record<string, TCerbosResources> = {
    [SubModule.PORTFOLIO]: [
        {
            resource: {
                id: "portfolios",
                kind: "portfolios",
            },
            actions: [PortfoliosActions.UPDATE_PORTFOLIO],
        },
    ],
    [SubModule.CAMPAIGN]: [
        {
            resource: {
                id: "campaigns",
                kind: "campaigns",
            },
            actions: [
                CampaignsActions.READ_CAMPAIGN,
                CampaignsActions.UPDATE_CAMPAIGN,
                CampaignsActions.CREATE_CAMPAIGN,
            ],
        },
        {
            resource: {
                id: "day_parting",
                kind: "day_parting",
            },
            actions: [DayPartingActions.CAN_MANAGE_DAYPARTING],
        },
        {
            resource: {
                id: "tags",
                kind: "tags",
            },
            actions: [
                TagsActions.CREATE_TAG,
                TagsActions.LIST_TAGS,
                TagsActions.DELETE_TAG,
                TagsActions.CAN_ASSIGN_TAGS,
            ],
        },
    ],
    [SubModule.ADGROUP]: [
        {
            resource: {
                id: "adgroups",
                kind: "adgroups",
            },
            actions: [
                AdgroupsActions.READ_ADGROUP,
                AdgroupsActions.UPDATE_ADGROUP,
            ],
        },
    ],
    [SubModule.KEYWORD]: [
        {
            resource: {
                id: "keywords",
                kind: "keywords",
            },
            actions: [
                KeywordsActions.READ_KEYWORD,
                KeywordsActions.UPDATE_KEYWORD,
                KeywordsActions.CREATE_KEYWORD,
            ],
        },
    ],
    [SubModule.PRODUCT]: [
        {
            resource: {
                id: "products",
                kind: "products",
            },
            actions: [
                ProductsActions.READ_PRODUCT,
                ProductsActions.UPDATE_PRODUCT,
            ],
        },
    ],
    [SubModule.SUGGESTED_NEG_KWS]: [
        {
            resource: {
                id: "keywords",
                kind: "keywords",
            },
            actions: [KeywordsActions.CREATE_NEGATIVE],
        },
    ],
    [SubModule.EXISTING_NEG_KWS]: [
        {
            resource: {
                id: "keywords",
                kind: "keywords",
            },
            actions: [KeywordsActions.DELETE_NEGATIVE],
        },
    ],
    [ParentModule.RULES]: [
        {
            resource: {
                id: "rules",
                kind: "rules",
            },
            actions: [
                RulesActions.UPDATE_RULE,
                RulesActions.READ_RULE,
                RulesActions.DELETE_RULE,
            ],
        },
    ],
    [ParentModule.KEYWORD_ANALYTICS]: [
        {
            resource: {
                id: "keywords",
                kind: "keywords",
            },
            actions: [
                KeywordsActions.CREATE_KEYWORD,
                KeywordsActions.UPDATE_KEYWORD,
            ],
        },
    ],
    [ParentModule.PRODUCT_ANALYTICS]: [
        {
            resource: {
                id: "campaigns",
                kind: "campaigns",
            },
            actions: [CampaignsActions.CREATE_CAMPAIGN],
        },
        {
            resource: {
                id: "products",
                kind: "products",
            },
            actions: [ProductsActions.UPDATE_PRODUCT],
        },
        {
            resource: {
                id: "tags",
                kind: "tags",
            },
            actions: [
                TagsActions.CREATE_TAG,
                TagsActions.LIST_TAGS,
                TagsActions.DELETE_TAG,
                TagsActions.CAN_ASSIGN_TAGS,
            ],
        },
        {
            resource: {
                id: "buybox",
                kind: "buybox",
            },
            actions: [BuyboxActions.CREATE_BUYBOX, BuyboxActions.DELETE_BUYBOX],
        },
        {
            resource: {
                id: "assets",
                kind: "assets",
            },
            actions: [AssetsActions.CREATE_ASSET, AssetsActions.LIST_ASSETS],
        },
    ],
    [ParentModule.CHANGE_LOG]: [
        {
            resource: {
                id: "history",
                kind: "history",
            },
            actions: [
                HistoryActions.CAN_VIEW_HISTORY,
                HistoryActions.REVERT_ACTION,
                HistoryActions.VIEW_REQUEST_LOGS,
            ],
        },
    ],
    [ParentModule.SETTINGS]: [
        {
            resource: {
                id: "tag_types",
                kind: "tag_types",
            },
            actions: [
                TagTypesActions.CREATE_TAG_TYPE,
                TagTypesActions.LIST_TAG_TYPE,
                TagTypesActions.DELETE_TAG_TYPE,
            ],
        },
        {
            resource: {
                id: "tags",
                kind: "tags",
            },
            actions: [
                TagsActions.CREATE_TAG,
                TagsActions.LIST_TAGS,
                TagsActions.DELETE_TAG,
                TagsActions.CAN_ASSIGN_TAGS,
            ],
        },
    ],
    [ParentModule.ADMIN_SETTINGS]: [
        {
            resource: {
                id: "users",
                kind: "users",
            },
            actions: [
                UsersActions.DELETE_USER,
                UsersActions.CREATE_USER,
                UsersActions.UPDATE_USER,
                UsersActions.READ_USER,
                UsersActions.LIST_USER,
                UsersActions.RESET_PASSWORD,
                UsersActions.REACTIVATE_USER,
            ],
        },
        {
            resource: {
                id: "roles",
                kind: "roles",
            },
            actions: [
                RolesActions.READ_ROLE,
                RolesActions.DELETE_ROLE,
                RolesActions.UPDATE_ROLE,
                RolesActions.CREATE_ROLE,
            ],
        },
        {
            resource: {
                id: "reports",
                kind: "reports",
            },
            actions: [
                ReportsActions.ADD_REPORT,
                ReportsActions.DELETE_REPORT,
                ReportsActions.UPDATE_REPORT,
                ReportsActions.LIST_REPORTS,
            ],
        },
        {
            resource: {
                id: "permissions",
                kind: "permissions",
            },
            actions: [
                PermissionsActions.LIST_PERMISSIONS,
                PermissionsActions.UPDATE_PERMISSION,
            ],
        },
        {
            resource: {
                id: "clients",
                kind: "clients",
            },
            actions: [
                ClientActions.SHOW_ALL_CLIENTS,
                ClientActions.LIST_CLIENTS,
                ClientActions.CREATE_CLIENT,
                ClientActions.UPDATE_CLIENT,
                ClientActions.DELETE_CLIENT,
            ],
        },
    ],
};

export const reviewDrawerTitleInitialState: TReviewDrawerTitleState = {
    "all-review": "All Reviews",
    "satisfaction-driver": "Satisfaction Driver",
    "cross-channel": "Cross Channel Rating and Review Trends",
};

export const CROSS_CHANNEL_WIDGET_METRICS = [
    {
        label: "Ratings",
        currentKey: "current_rating",
        previousKey: "previous_rating",
    },
    {
        label: "#Reviews",
        currentKey: "current_reviews",
        previousKey: "previous_reviews",
    },
    {
        label: "#Ratings",
        currentKey: "current_ratings",
        previousKey: "previous_ratings",
    },
    {
        label: "Overall NPS Score",
        currentKey: "current_nps",
        previousKey: "previous_nps",
    },
];

export const PRICING_CROSS_CHANNEL_WIDGET_METRICS = [
    { label: "Price", currentKey: "curr_price", previousKey: "prev_price" },
    { label: "RPI", currentKey: "curr_rpi", previousKey: "prev_rpi" },
    {
        label: "Discount",
        currentKey: "curr_discount",
        previousKey: "prev_discount",
    },
    {
        label: "%times on deal",
        currentKey: "curr_deal",
        previousKey: "prev_deal",
    },
];

export const AVAILABILITY_CROSS_CHANNEL_WIDGET_METRICS = [
    {
        label: "Availability%",
        currentKey: "curr_availability",
        previousKey: "prev_availability",
    },
    { label: "Buybox%", currentKey: "curr_buybox", previousKey: "prev_buybox" },
    {
        label: "MS%",
        currentKey: "curr_ms_percentage",
        previousKey: "prev_ms_percentage",
    },
    {
        label: "Current Availability",
        currentKey: "availability_status",
    },
];

export const VISBILITY_CROSS_CHANNEL_WIDGET_METRICS = [
    {
        label: "Overall SOV",
        currentKey: "cur_overall_sov",
        previousKey: "prev_overall_sov",
    },
];

export const KEYWORD_VISBILITY_CROSS_CHANNEL_WIDGET_METRICS = [
    {
        label: "Overall rank",
        currentKey: "cur_overall_rank",
        previousKey: "prev_overall_rank",
    },
    {
        label: "Organic rank",
        currentKey: "cur_organic_rank",
        previousKey: "prev_organic_rank",
    },
    {
        label: "Paid rank",
        currentKey: "cur_sponsored_rank",
        previousKey: "prev_sponsored_rank",
    },
];

export const NO_FILTER_MODULES = [
    ParentModule.RULES,
    ParentModule.SETTINGS,
    ParentModule.PRODUCT_DEEPDIVE,
    SubModule.REVIEW_SKU,
    SubModule.REVIEW_BRAND,
];

export const SKU_DEPENDENT_MODULES = [
    SubModule.REVIEW_SKU,
    ParentModule.PRODUCT_DEEPDIVE,
];

export const PA_MODULE_INITIAL_DATA: IDeepDiveModalConfig = {
    overview: null,
    funnel: null,
    reviews: null,
    digitalShelf: null,
    productTrendAnalysis: null,
    spendRoasData: null,
};

export const CA_MODULE_INITIAL_DATA: ICategoryOverviewConfig = {
    categoryInfoWidgetData: null,
};

export const PA_MODULE_LOADING_INTIAL_STATE: TPaModuleLoadingState = {
    [ProductDeepdiveModules.RATINGS_REVIEWS]: true,
    [ProductDeepdiveModules.OVERVIEW]: false,
    [ProductDeepdiveModules.PRICING]: true,
    [ProductDeepdiveModules.VISIBILITY]: true,
    [ProductDeepdiveModules.AVAILABILITY]: true,
};

export const usersFormRules: { [key: string]: Rule[] } = {
    fistName: [
        {
            required: true,
            message: "Please enter your first name",
        },
    ],
    lastName: [
        {
            required: true,
            message: "Please enter your last name",
        },
    ],
    email: [
        {
            required: true,
            message: "Please enter your email",
        },
        {
            type: "email",
            message: "Please enter a valid email",
        },
    ],
    phone: [
        {
            required: false,
            message: "Please enter your phone number",
        },
    ],
    timezone: [
        {
            required: true,
            message: "Please select your timezone",
        },
    ],
    platforms: [
        {
            required: true,
            message: "Platform is required",
        },
    ],
    role: [
        {
            required: true,
            message: "Role is required",
        },
    ],
    channels: [
        {
            required: false,
            message: "Channels are required",
        },
    ],
    sendWelcomeEmail: [
        {
            required: true,
            message: "Please select send welcome email",
        },
    ],
    expiration: [
        {
            required: false,
            message: "Please select expiration date",
        },
    ],
};

export enum BiddingTypeConstants {
    CONTEXTUAL_MULTIPLIER = "contextual_multiplier",
    PREMIUM_PLACEMENTS = "premium_placements",
    PRODUCT_LISTING_PAGE = "product_listing_page",
    HOME_PAGE = "home_page",
    TOP_OF_SEARCH = "top_of_search",
    PREMIUM_USERS = "premium_users",
}

export enum CerbosResources {
    COMMON_PERMISSIONS = "common_permissions",
    RULES = "rules",
    GOALS = "goals",
    TAGS = "tags",
    CAMPAIGNS = "campaigns",
    DAY_PARTING = "day_parting",
    KEYWORDS = "keywords",
    HISTORY = "history",
    PRODUCTS = "products",
    BUYBOX = "buybox",
    REPORTS = "reports",
    USERS = "users",
    ROLES = "roles",
    PERMISSIONS = "permissions",
    CLIENTS = "clients",
    PORTFOLIOS = "portfolios",
    TAG_TYPES = "tag_types",
    ADGROUPS = "adgroups",
    SEARCH_TERM_ANALYTICS = "search_term_analytics",
    ASSETS = "assets",
    ALERTS = "alerts",
    REVIEWS = "reviews",
}

export const cellRendererCerbosResourcesAndActions: TPermissionConfig = {
    [ParentModule.SETTINGS]: [
        {
            resource: CerbosResources.COMMON_PERMISSIONS,
            action: CommonPermissionsActions.CAN_UPDATE_SETTINGS,
            key: "isModuleEditDisabled",
        },
    ],
    [ParentModule.CHANGE_LOG]: [
        {
            resource: CerbosResources.HISTORY,
            action: HistoryActions.REVERT_ACTION,
            key: "isModuleEditDisabled",
        },
    ],
    [ParentModule.KEYWORD_ANALYTICS]: [
        {
            resource: CerbosResources.KEYWORDS,
            action: KeywordsActions.UPDATE_KEYWORD,
            key: "isModuleEditDisabled",
        },
    ],
    [ParentModule.PRODUCT_ANALYTICS]: [
        {
            resource: CerbosResources.COMMON_PERMISSIONS,
            action: CommonPermissionsActions.CAN_DEEPDIVE,
            key: "isModuleEditDisabled",
        },
    ],
    [ParentModule.ADMIN_SETTINGS]: [
        {
            resource: CerbosResources.REPORTS,
            action: ReportsActions.UPDATE_REPORT,
            key: "isModuleEditDisabled",
        },
    ],
    [SubModule.CAMPAIGN]: [
        {
            resource: CerbosResources.CAMPAIGNS,
            action: CampaignsActions.UPDATE_CAMPAIGN,
            key: "isModuleEditDisabled",
        },
    ],
    [SubModule.KEYWORD]: [
        {
            resource: CerbosResources.KEYWORDS,
            action: KeywordsActions.UPDATE_KEYWORD,
            key: "isModuleEditDisabled",
        },
    ],
    [SubModule.PORTFOLIO]: [
        {
            resource: CerbosResources.PORTFOLIOS,
            action: PortfoliosActions.UPDATE_PORTFOLIO,
            key: "isModuleEditDisabled",
        },
    ],
    [SubModule.ADGROUP]: [
        {
            resource: CerbosResources.ADGROUPS,
            action: AdgroupsActions.UPDATE_ADGROUP,
            key: "isModuleEditDisabled",
        },
    ],
    [SubModule.SUGGESTED_NEG_KWS]: [
        {
            resource: CerbosResources.KEYWORDS,
            action: KeywordsActions.CREATE_NEGATIVE,
            key: "isModuleEditDisabled",
        },
    ],
    [SubModule.EXISTING_NEG_KWS]: [
        {
            resource: CerbosResources.KEYWORDS,
            action: KeywordsActions.DELETE_NEGATIVE,
            key: "isModuleEditDisabled",
        },
    ],
    [SubModule.PRODUCT]: [
        {
            resource: CerbosResources.PRODUCTS,
            action: ProductsActions.UPDATE_PRODUCT,
            key: "isModuleEditDisabled",
        },
    ],
    [ParentModule.RULES]: [
        {
            resource: CerbosResources.RULES,
            action: RulesActions.UPDATE_RULE,
            key: "disableUpdateAction",
        },
        {
            resource: CerbosResources.RULES,
            action: RulesActions.DELETE_RULE,
            key: "disableDeleteAction",
        },
    ],
};

export enum BiddingType {
    auto = "autoBid",
    contextual_multiplier = "bidContextualMultiplier",
    premium_placements = "bidPremiumPlacements",
    product_listing_page = "bidProductListingPage",
    home_page = "bidHomePage",
    top_of_search = "bidTopOfSearch",
    premium_users = "bidPremiumUsers",
}

export const pricingFiltersOptions = [
    { label: "Rpi", value: "rpi" },
    { label: "Price", value: "price" },
];

export const PRICING_CHART_TOGGLE_FILTERS_INTIAL_STATE = {
    [PricingCharts.PRICE_SALES_PPU]: false,
    [PricingCharts.MS_RPI_PPU]: false,
    [PricingCharts.MS_RPI]: false,
    [PricingCharts.PRODUCT_COMPETITION_PPU]: false,
    [PricingCharts.PRICING_DEALS_PPU]: false,
};

export const PRICING_CHART_FILTERS_INTIAL_STATE = {
    [PricingCharts.MS_RPI]: "rpi",
    [PricingCharts.PRICE_DEALS]: "rpi",
    [PricingCharts.PRICE_DEALS_SKUS]: [] as string[],
    [PricingCharts.PRICE_DEALS_COMPETITION]: [] as string[],
    [PricingCharts.PRICE_DEALS_CHANNEL]: "",
    [PricingCharts.KPI_SLECTION]: AggregationField.MS_PERCENT,
};

export const VISIBILITY_CHART_FILTERS_INTIAL_STATE = {
    [PricingCharts.MS_RPI]: "rpi",
    [PricingCharts.PRICE_DEALS]: "organic_visibility",
    [PricingCharts.PRICE_DEALS_SKUS]: [] as string[],
    [PricingCharts.PRICE_DEALS_COMPETITION]: [] as string[],
    [PricingCharts.PRICE_DEALS_CHANNEL]: "",
};

export const VISIBILITY_KEYWORD_PERFORMANCE_CHART_FILTERS_INTIAL_STATE = {
    [PricingCharts.MS_RPI]: "rpi",
    [PricingCharts.PRICE_DEALS]: "organic_rank",
    [PricingCharts.PRICE_DEALS_SKUS]: [] as string[],
    [PricingCharts.PRICE_DEALS_COMPETITION]: [] as string[],
    [PricingCharts.PRICE_DEALS_CHANNEL]: "",
};

export const MY_PRODUCT_TRENDS_FILTER_MAP = {
    [MyProductTrendsTypes.RPI]: "curr_rpi",
    [MyProductTrendsTypes.DEALS]: "deal_percentage",
    [MyProductTrendsTypes.PRICE]: "price",
    [MyProductTrendsTypes.ORGANIC_RANK]: "organic_rank",
    [MyProductTrendsTypes.OVERALL_RANK]: "overall_rank",
    [MyProductTrendsTypes.PAID_RANK]: "sponsored_rank",
    [MyProductTrendsTypes.AVAILABILITY]: "availability",
    [MyProductTrendsTypes.BUYBOX]: "buybox_win",
    [MyProductTrendsTypes.ORGANIC_VISIBILITY]: "organic_visibility",
    [MyProductTrendsTypes.PAID_VISIBILITY]: "paid_visibility",
    [MyProductTrendsTypes.OVERALL_VISIBILITY]: "overall_visibility",
    [MyProductTrendsTypes.MS_PERCENT]: AggregationField.MS_PERCENT,
    [MyProductTrendsTypes.ROAS]: AggregationField.ROAS,
    [MyProductTrendsTypes.SALE]: AggregationField.SALE,
    [MyProductTrendsTypes.AD_SPEND]: AggregationField.SPEND,
    [MyProductTrendsTypes.TOTAL_SALES]: AggregationField.TOTAL_SALES,
};

export const AVAILABILITY_CHART_FILTERS_INTIAL_STATE = {
    [AvailabilityCharts.BB_WIN_IMPACT]: "price",
    [PricingCharts.PRICE_DEALS]: "availability",
    [PricingCharts.PRICE_DEALS_COMPETITION]: [] as string[],
    [AvailabilityCharts.BUYBOX]: "buybox",
};

export const PRICING_DEAL_TRENDS_AXIS_TITLE_MAP: Record<string, string> = {
    [MyProductTrendsTypes.RPI]: "RPI",
    [MyProductTrendsTypes.PRICE]: "Price",
    [MyProductTrendsTypes.DEALS]: "Deals %",
    [MyProductTrendsTypes.AVAILABILITY]: "Availability (%)",
    [MyProductTrendsTypes.BUYBOX]: "Buybox win (%)",
    [MyProductTrendsTypes.ORGANIC_VISIBILITY]: "Organic Visibility (%)",
    [MyProductTrendsTypes.PAID_VISIBILITY]: "Paid Visibility (%)",
    [MyProductTrendsTypes.OVERALL_VISIBILITY]: "Overall Visibility (%)",
    [MyProductTrendsTypes.AD_SPEND]: "Ad Spend",
    [MyProductTrendsTypes.ROAS]: "ROAS",
};

export const SUMMARY_WIDGET_UNITS = {
    change: [
        SummaryWidgetConfigKeys.PRICE_CHANGE,
        SummaryWidgetConfigKeys.RPI_CHANGE,
        SummaryWidgetConfigKeys.AVG_DEAL_CHANGE,
        SummaryWidgetConfigKeys.ORGANIC_VISIBILITY_VALUE,
        SummaryWidgetConfigKeys.PAID_VISIBILITY_VALUE,
        SummaryWidgetConfigKeys.AVAILABILITY_PERCENTAGE_CHANGE,
        SummaryWidgetConfigKeys.ESTIMATED_SALES_LOSS_CHANGE,
        SummaryWidgetConfigKeys.BUYBOX_PERCENTAGE_CHANGE,
    ],
    value: [
        SummaryWidgetConfigKeys.AVG_DEAL_CHANGE,
        SummaryWidgetConfigKeys.ORGANIC_VISIBILITY_CHANGE,
        SummaryWidgetConfigKeys.PAID_VISIBILITY_CHANGE,
        SummaryWidgetConfigKeys.AVAILABILITY_PERCENTAGE_VALUE,
        SummaryWidgetConfigKeys.BUYBOX_PERCENTAGE_VALUE,
    ],
};

export const permissionConfig: TPermissionConfig = {
    commonPermissions: [
        {
            resource: CerbosResources.COMMON_PERMISSIONS,
            action: CommonPermissionsActions.CAN_EXPORT,
            key: "canExport",
        },
        {
            resource: CerbosResources.COMMON_PERMISSIONS,
            action: CommonPermissionsActions.CAN_ACCESS_IOT,
            key: "hasIOTAccess",
        },
        {
            resource: CerbosResources.COMMON_PERMISSIONS,
            action: CommonPermissionsActions.CAN_ACCESS_REVSCOUT,
            key: "hasReportBuilderAccess",
        },
        {
            resource: CerbosResources.COMMON_PERMISSIONS,
            action: CommonPermissionsActions.CAN_MANAGE_ADMIN_SETTINGS,
            key: "canManageAdminSettings",
        },
        {
            resource: CerbosResources.HISTORY,
            action: HistoryActions.VIEW_REQUEST_LOGS,
            key: "canViewRequestLog",
        },
        {
            resource: CerbosResources.COMMON_PERMISSIONS,
            action: CommonPermissionsActions.CAN_PERFORM_BULK_ACTIONS,
            key: "canPerformBulkActions",
        },
        {
            resource: CerbosResources.USERS,
            action: UsersActions.REACTIVATE_USER,
            key: "canReactivateUser",
        },
    ],
    [ParentModule.SETTINGS]: [
        {
            resource: CerbosResources.COMMON_PERMISSIONS,
            action: CommonPermissionsActions.CAN_UPDATE_SETTINGS,
            key: "canUpdateSettings",
        },
        {
            resource: CerbosResources.TAGS,
            action: TagsActions.LIST_TAGS,
            key: "canListTags",
        },
        {
            resource: CerbosResources.TAGS,
            action: TagsActions.CREATE_TAG,
            key: "canCreateTag",
        },
        {
            resource: CerbosResources.TAGS,
            action: TagsActions.DELETE_TAG,
            key: "canDeleteTags",
        },
        {
            resource: CerbosResources.TAG_TYPES,
            action: TagTypesActions.LIST_TAG_TYPE,
            key: "canListTagType",
        },
        {
            resource: CerbosResources.TAG_TYPES,
            action: TagTypesActions.CREATE_TAG_TYPE,
            key: "canAddTagType",
        },
        {
            resource: CerbosResources.TAG_TYPES,
            action: TagTypesActions.DELETE_TAG_TYPE,
            key: "canDeleteTagType",
        },
        {
            resource: CerbosResources.TAGS,
            action: TagsActions.CAN_ASSIGN_TAGS,
            key: "canAssignTags",
        },
    ],
    [ParentModule.PRODUCT_ANALYTICS]: [
        {
            resource: CerbosResources.RULES,
            action: RulesActions.CREATE_RULE,
            key: "canCreateRule",
        },
        {
            resource: CerbosResources.CAMPAIGNS,
            action: CampaignsActions.CREATE_CAMPAIGN,
            key: "canCreateCampaign",
        },
        {
            resource: CerbosResources.PRODUCTS,
            action: ProductsActions.UPDATE_PRODUCT,
            key: "canUpdateInProductAnalytics",
        },
        {
            resource: CerbosResources.BUYBOX,
            action: BuyboxActions.CREATE_BUYBOX,
            key: "canCreateBuyBox",
        },
        {
            resource: CerbosResources.BUYBOX,
            action: BuyboxActions.DELETE_BUYBOX,
            key: "canDeleteBuyBox",
        },
        {
            resource: CerbosResources.TAGS,
            action: TagsActions.CAN_ASSIGN_TAGS,
            key: "canAssignTags",
        },
        {
            resource: CerbosResources.TAGS,
            action: TagsActions.DELETE_TAG,
            key: "canDeleteTags",
        },
        {
            resource: CerbosResources.KEYWORDS,
            action: KeywordsActions.LIST_KEYWORD,
            key: "canListKeywords",
        },
        {
            resource: CerbosResources.ASSETS,
            action: AssetsActions.LIST_ASSETS,
            key: "canListAssets",
        },
        {
            resource: CerbosResources.ASSETS,
            action: AssetsActions.CREATE_ASSET,
            key: "canCreateAssets",
        },
    ],
    [SubModule.OVERVIEW]: [
        {
            resource: CerbosResources.GOALS,
            action: GoalsActions.LIST_GOALS,
            key: "canListGoal",
        },
        {
            resource: CerbosResources.GOALS,
            action: GoalsActions.CREATE_GOAL,
            key: "canCreateGoal",
        },
        {
            resource: CerbosResources.GOALS,
            action: GoalsActions.EDIT_GOAL,
            key: "canEditGoal",
        },
        {
            resource: CerbosResources.GOALS,
            action: GoalsActions.DELETE_GOAL,
            key: "canDeleteGoal",
        },
        {
            resource: CerbosResources.ALERTS,
            action: AlertActions.LIST_ALERTS,
            key: "canListAlerts",
        },
    ],
    [SubModule.CAMPAIGN]: [
        {
            resource: CerbosResources.TAGS,
            action: TagsActions.CAN_ASSIGN_TAGS,
            key: "canAssignTags",
        },
        {
            resource: CerbosResources.CAMPAIGNS,
            action: CampaignsActions.CREATE_CAMPAIGN,
            key: "canCreateCampaign",
        },
        {
            resource: CerbosResources.CAMPAIGNS,
            action: CampaignsActions.UPDATE_CAMPAIGN,
            key: "canUpdateCampaign",
        },
        {
            resource: CerbosResources.DAY_PARTING,
            action: DayPartingActions.CAN_MANAGE_DAYPARTING,
            key: "canManageDayParting",
        },
        {
            resource: CerbosResources.TAGS,
            action: TagsActions.DELETE_TAG,
            key: "canDeleteTags",
        },
    ],
    [SubModule.KEYWORD]: [
        {
            resource: CerbosResources.KEYWORDS,
            action: KeywordsActions.CREATE_KEYWORD,
            key: "canCreateKeyword",
        },
        {
            resource: CerbosResources.KEYWORDS,
            action: KeywordsActions.UPDATE_KEYWORD,
            key: "canUpdateKeyword",
        },
        {
            resource: CerbosResources.RULES,
            action: RulesActions.CREATE_RULE,
            key: "canCreateRule",
        },
    ],
    [SubModule.SUGGESTED_NEG_KWS]: [
        {
            resource: CerbosResources.KEYWORDS,
            action: KeywordsActions.CREATE_NEGATIVE,
            key: "canCreateNegativeKeyword",
        },
        {
            resource: CerbosResources.RULES,
            action: RulesActions.CREATE_RULE,
            key: "canCreateRule",
        },
    ],
    [SubModule.EXISTING_NEG_KWS]: [
        {
            resource: CerbosResources.KEYWORDS,
            action: KeywordsActions.DELETE_NEGATIVE,
            key: "canDeleteNegativeKeyword",
        },
        {
            resource: CerbosResources.RULES,
            action: RulesActions.CREATE_RULE,
            key: "canCreateRule",
        },
    ],
    [ParentModule.CHANGE_LOG]: [
        {
            resource: CerbosResources.HISTORY,
            action: HistoryActions.REVERT_ACTION,
            key: "canRevertActionInHistory",
        },
    ],
    [ParentModule.KEYWORD_ANALYTICS]: [
        {
            resource: CerbosResources.KEYWORDS,
            action: KeywordsActions.CREATE_KEYWORD,
            key: "canCreateKeyword",
        },
        {
            resource: CerbosResources.RULES,
            action: RulesActions.CREATE_RULE,
            key: "canCreateRule",
        },
    ],
    [ParentModule.ADMIN_SETTINGS]: [
        {
            resource: CerbosResources.REPORTS,
            action: ReportsActions.ADD_REPORT,
            key: "canAddReport",
        },
        {
            resource: CerbosResources.REPORTS,
            action: ReportsActions.LIST_REPORTS,
            key: "canListReports",
        },
        {
            resource: CerbosResources.REPORTS,
            action: ReportsActions.UPDATE_REPORT,
            key: "canUpdateReport",
        },
        {
            resource: CerbosResources.REPORTS,
            action: ReportsActions.DELETE_REPORT,
            key: "canDeleteReport",
        },
        {
            resource: CerbosResources.USERS,
            action: UsersActions.LIST_USER,
            key: "canListUsers",
        },
        {
            resource: CerbosResources.ROLES,
            action: RolesActions.READ_ROLE,
            key: "canListUserRoles",
        },
        {
            resource: CerbosResources.PERMISSIONS,
            action: PermissionsActions.UPDATE_PERMISSION,
            key: "canUpdatePermissions",
        },
        {
            resource: CerbosResources.USERS,
            action: UsersActions.CREATE_USER,
            key: "canAddUser",
        },
        {
            resource: CerbosResources.USERS,
            action: UsersActions.RESET_PASSWORD,
            key: "canResetPassword",
        },
        {
            resource: CerbosResources.USERS,
            action: UsersActions.DELETE_USER,
            key: "canDeleteUser",
        },
        {
            resource: CerbosResources.PERMISSIONS,
            action: PermissionsActions.LIST_PERMISSIONS,
            key: "canListPermissions",
        },
        {
            resource: CerbosResources.CLIENTS,
            action: ClientActions.SHOW_ALL_CLIENTS,
            key: "canShowAllClients",
        },
        {
            resource: CerbosResources.CLIENTS,
            action: ClientActions.LIST_CLIENTS,
            key: "canListClients",
        },
        {
            resource: CerbosResources.CLIENTS,
            action: ClientActions.CREATE_CLIENT,
            key: "canCreateClient",
        },
        {
            resource: CerbosResources.CLIENTS,
            action: ClientActions.UPDATE_CLIENT,
            key: "canUpdateClient",
        },
        {
            resource: CerbosResources.CLIENTS,
            action: ClientActions.DELETE_CLIENT,
            key: "canDeleteClient",
        },
    ],
};

export const USERS_FORM_INITIAL_STATE: TUsersFormInitialState = {
    channelsLoading: null,
    formLoading: false,
    reactivateUserLoading: false,
    formSubmissionLoading: false,
};

export const reportUsersFormRules: { [key: string]: Rule[] } = {
    userName: [
        {
            required: true,
            message: "please enter user name",
        },
        {
            min: 3,
            message: "user name should be at least 3 characters",
        },
    ],
    password: [
        {
            required: true,
            message: "please enter password",
        },
        {
            min: 3,
            message: "password should be minimum 3 characters",
        },
    ],
};

export const UserDataInitialValues: User = {
    user_id: 0,
    uid: "",
    first_name: "",
    last_name: "",
    email: "",
    last_login: "",
    created_at: "",
    created_by: "",
    updated_at: "",
    updated_by: "",
    status: false,
    country: "",
    timezone: "",
    phone: "",
    default_client: 0,
    is_archived: false,
    is_super_admin: false,
    access: [
        {
            clients: [
                {
                    client_id: 0,
                    client_name: "",
                },
            ],
            role_id: 0,
            role_name: "",
            channels: [
                {
                    logo_url: "",
                    client_id: 0,
                    channel_id: 0,
                    name: "",
                },
            ],
            platforms: [] as string[],
            role_slug: "",
            privileges: [] as string[],
            is_internal: false,
        },
    ],
};

export const DISABLED_CHANNELS = [
    Channels.AMAZON_FRESH,
    Channels.DMART,
    Channels.JIO_MART,
];

export const deepDiveMetricsConfig = {
    overview: [
        { key: AggregationField.AVAILABILITY, label: "Availability (%)" },
        { key: AggregationField.PRICE, label: "Price Trend (LC)" },
        {
            key: AggregationField.ORG_SKU_WT_CRAWL_SV,
            label: "Organic Impressions",
        },
        {
            key: AggregationField.PAID_SKU_WT_CRAWL_SV,
            label: "Paid Impressions",
        },
    ],
    funnel: [
        { key: AggregationField.SALE, label: "Ad Sales" },
        { key: AggregationField.SPEND, label: "Spends" },
        { key: AggregationField.ROAS, label: "ROAS" },
    ],
    salesTrend: [
        { key: AggregationField.DAILY_TREND_SALE, label: "Total Sales" },
    ],
};

export const CustomGroupingFunction: CustomGroupingFunctionType = {
    MY_PRODUCT_PRICE_AND_DEAL: (
        item: Record<string, string>,
        groupKey: string
    ) => `${groupKey}_${item.sku}`,
    MY_PRODUCT_TRENDS_GRAPH_COMPETITION: (
        item: Record<string, string>,
        groupKey: string
    ) => `${groupKey}_${item.sku}`,
    MY_PRODUCT_TRENDS_GRAPH_CROSS_CHANNEL: (
        item: Record<string, string>,
        groupKey: string
    ) => `${groupKey}_${item.name}`,
    DATEWISE_TRENDS_OF_COMPETITIVE_PRODUCTS: (
        item: Record<string, string>,
        groupKey: string
    ) => `${groupKey}_${item.sku}`,
    CUSTOMER_SATISFACTION_AND_FEATURE: (
        item: Record<string, string>,
        groupKey: string
    ) => `${groupKey}_${item.theme}`,
    SALES_TREND: (item: Record<string, string>, groupKey: string) =>
        `${groupKey}_${item.sku_id}`,
    TOTAL_ROWS: (item: Record<string, string>, groupKey: string) =>
        `${groupKey}_${item.asin_id}`,
    ROWS: (item: Record<string, string>, groupKey: string) =>
        `${groupKey}_${item.asin_id}`,
    COMBINED_RESPONSE: (item: Record<string, string>, groupKey: string) =>
        `${groupKey}_${item.asin_id}`,
    MY_PRODUCT_TREND_COMPETITION_VISIBILITY: (
        item: Record<string, string>,
        groupKey: string
    ) => `${groupKey}_${item.sku}`,
    KEYWORD_PERFORMANCE_GRAPH_VISIBILITY: (
        item: Record<string, string>,
        groupKey: string
    ) => `${groupKey}_${item.keyword}_${item.channel}`,
    KEYWORD_PERFORMANCE_GRAPH_COMPETITION_VISIBILITY: (
        item: Record<string, string>,
        groupKey: string
    ) => `${groupKey}_${item.keyword}_${item.sku}_${item.channel}`,
    COMPETITION_DATA_VISIBILITY: (
        item: Record<string, string>,
        groupKey: string
    ) => `${groupKey}_${item.sku}`,
    CROSS_CHANNEL_AND_DIRECT_COMPETITOR_DATA_VISIBILITY: (
        item: Record<string, string>,
        groupKey: string
    ) => `${groupKey}_${item.sku}_${item.channel_id}`,
    CATEGORY_DATA: (
        item: Record<string, string>,
        groupKey: string
    ) => `${groupKey}_${item.sku}_${item.store_id}_${item.category}_${item.sub_category}_${item.city}_${item.pincode}_${item.region}
    `,
    CATEGORY_DATA_ECOMM: (
        item: Record<string, string>,
        groupKey: string
    ) => `${groupKey}_${item.brand}_${item.category}_${item.sub_category}_${item.sku}
    `,
};

export const DATE_COMPARISON_OPTIONS = {
    currentPeriod: {
        startDate: "",
        endDate: "",
    },
    previousPeriod: {
        startDate: "",
        endDate: "",
    },
    isCompareWithChecked: false,
};

export const funnelChartOptions = [
    { value: AggregationField.SPEND, label: "Ad Spends" },
    { value: AggregationField.CLICK, label: "Clicks" },
    { value: AggregationField.ORDERS, label: "Orders" },
    { value: AggregationField.SALE, label: "Ad Sales" },
    { value: AggregationField.ACOS, label: "ACoS" },
    { value: AggregationField.CPC, label: "CPC" },
    { value: AggregationField.AOV, label: "AoV" },
    { value: AggregationField.CR, label: "CR" },
    { value: AggregationField.CTR, label: "CTR" },
    { value: AggregationField.IMPRESSION, label: "Impressions" },
    { value: AggregationField.ROAS, label: "ROAS" },
];

export const GENERATE_MEDIA_PLAN_CONFIG: TGeneratePlanConfig = {
    Account: {
        fieldColumn: "client_name",
        gridHeight: 100,
    },
    Campaign: {
        fieldColumn: "campaign_name",
        gridHeight: 500,
    },
    Product: {
        fieldColumn: "sku_title",
    },
    ["Ad Type"]: {
        fieldColumn: "program_type",
    },
    Default: {
        fieldColumn: "tag",
        gridHeight: 500,
    },
};

export const MEDIA_PLAN_METRICES = [
    "spend",
    "ctr",
    "cpm",
    "cpc",
    "cr",
    "roas",
    "aov",
    "troas",
    "impression",
    "click",
    "orders",
    "sale",
];

/** Absolute metrices heirarchy as they appear in the array! */
export const ABSOLUTE_PLAN_METRICES = ["impression", "click", "orders", "sale"];

export const RATIO_PLAN_METRICES = [
    "ctr",
    "cpc",
    "cpm",
    "cr",
    "aov",
    "roas",
    "troas",
];

export const MONTH_OPTIONS: TMonthOption[] = [
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
];
export const pricingModuleRadioOptions = [
    { label: "RPI", value: MyProductTrendsTypes.RPI },
    { label: "Price", value: MyProductTrendsTypes.PRICE },
    { label: "%times on deal", value: MyProductTrendsTypes.DEALS },
];

export const visibilityModuleRadioOptions = [
    { label: "Organic Rank", value: MyProductTrendsTypes.ORGANIC_RANK },
    { label: "Paid Rank", value: MyProductTrendsTypes.PAID_RANK },
    { label: "Overall Rank", value: MyProductTrendsTypes.OVERALL_RANK },
];

export const availabilityModuleRadioOptions = [
    { label: "Availability", value: MyProductTrendsTypes.AVAILABILITY },
    { label: "Buybox", value: MyProductTrendsTypes.BUYBOX },
];

export const visibilityModuleMyProductTrendsRadioOptions = [
    {
        label: "Organic Visibility",
        value: MyProductTrendsTypes.ORGANIC_VISIBILITY,
    },
    { label: "Paid Visibility", value: MyProductTrendsTypes.PAID_VISIBILITY },
    {
        label: "Overall Visibility",
        value: MyProductTrendsTypes.OVERALL_VISIBILITY,
    },
];

export const visibilityFilterOptions = {
    [VisibilityFilterOptionKeys.SOV_TYPE]: [
        { label: "Overall", value: SOVTypeValues.OVERALL },
        { label: "Paid", value: SOVTypeValues.PAID },
        { label: "Organic", value: SOVTypeValues.ORGANIC },
    ],
    [VisibilityFilterOptionKeys.KEYWORD_TYPE]: [
        { label: "Branded", value: KeywordTypeValues.BRANDED },
        { label: "Generic", value: KeywordTypeValues.GENERIC },
        { label: "Competition", value: KeywordTypeValues.COMPETITION },
    ],
    [VisibilityFilterOptionKeys.AD_TYPE]: [
        { label: "SB", value: ProgramType.SB },
        { label: "SP", value: ProgramType.SP },
        { label: "SD", value: ProgramType.SD },
    ],
    [VisibilityFilterOptionKeys.PAGE_RANK]: [
        { label: "Top 5", value: PageRankValues.TOP_5 },
        { label: "Top 10", value: PageRankValues.TOP_10 },
        { label: "Page 1", value: PageRankValues.PAGE_1 },
    ],
};

export const DEFAULT_PAGE_SIZE = 10;
export const DEBOUNCE_DELAY = 500;
export const FIRST_PAGE = 1;

export const PRIORITIES = [
    { label: "High", value: "high" },
    { label: "Low", value: "low" },
    { label: "30D Historic", value: "30D-historic" },
];

export const DEFAULT_PAGE_SIZE_OPTIONS = ["5", "10", "20", "50"];

export const BB_FILTERS_OPTIONS = [
    {
        label: "Price",
        value: AggregationField.PRICE,
    },
    {
        label: "Availability",
        value: AggregationField.AVAILABILITY,
    },
];

export const BB_FILTERS_OPTIONS_AVAILABILITY_ECOMM = [
    {
        label: "Winners",
        value: AvailabilityDrawerRadioOptions.WINNERS,
    },
    {
        label: "Losers",
        value: AvailabilityDrawerRadioOptions.LOSERS,
    },
];

export const My_Brands_Trends_filter_options = [
    {
        label: "Availability",
        value: AggregationField.AVAILABILITY,
    },
    {
        label: "Buybox",
        value: AggregationField.BUYBOX,
    },
];

export const comparisonSelectOptions = [
    { label: "MS%", value: AggregationField.MS_PERCENT },
    { label: "ROAS", value: AggregationField.ROAS },
    { label: "Sales", value: AggregationField.SALE },
    { label: "AD Spends", value: AggregationField.SPEND },
    { label: "Total Sales", value: AggregationField.TOTAL_SALES },
];

export const comparisonSelectOptionsCategoryAvailabilityEcomm = [
    { label: "Availability%", value: AggregationField.AVAILABILITY },
    { label: "Buybox%", value: AggregationField.BUYBOX },
    { label: "MS%", value: AggregationField.MS_PERCENT },
    { label: "ROAS", value: AggregationField.ROAS },
    { label: "Sales", value: AggregationField.SALE },
    { label: "AD Spends", value: AggregationField.SPEND },
    { label: "Total Sales", value: AggregationField.TOTAL_SALES },
];

export enum DateTimeFormats {
    STANDARD = "DD MMM YYYY, HH:mm",
    DATE_ONLY = "DD MMM YYYY",
    UTC_SUFFIX = "- UTC",
    // Date Picker specific formats
    DATE_PICKER_DISPLAY = "DD MMM YYYY",
    DATE_PICKER_RANGE = "DD MMM",
    DATE_PICKER_DAY = "ddd",
    DATE_PICKER_DAY_FULL = "ddd - DD MMM YYYY",
    DATE_PICKER_INPUT = "MM/DD/YYYY",
    DATE_PICKER_TOOLTIP = "MMM DD, YYYY",
    DATE_PICKER_CALENDAR = "MMMM YYYY",
    DATE_PICKER_API = "YYYY-MM-DD",
}

export const msPercentOfftakeToggeleOptions = [
    {
        label: "MS%",
        value: CATEGORY_OVERVIEW_MS_OFFTAKE_RADIO.MS_PERCENT,
    },
    {
        label: "Offtake",
        value: CATEGORY_OVERVIEW_MS_OFFTAKE_RADIO.OFFTAKE,
    },
];

export const revenueUnitsToggleOptions = [
    {
        label: "Revenue",
        value: CATEGORY_OVERVIEW_SKU_FILTERS.REVENUE,
    },
    {
        label: "Units",
        value: CATEGORY_OVERVIEW_SKU_FILTERS.UNITS_SOLD,
    },
];

export const topNForSovOptions = [
    {
        label: "Top 5",
        value: "top10",
    },
    {
        label: "Top 10",
        value: "top5",
    },
    {
        label: "Two",
        value: "two",
    },
    {
        label: "Three",
        value: "three",
    },
];

export const CategoryOverviewMSTrendsOverTimeOptions = [
    {
        label: "MS%",
        value: CATEGORY_OVERVIEW_MS_TRENDS_OVERTIME_RADIO.MS_PERCENT,
    },
    {
        label: "ROAS",
        value: CATEGORY_OVERVIEW_MS_TRENDS_OVERTIME_RADIO.ROAS,
    },
    {
        label: "Sales",
        value: CATEGORY_OVERVIEW_MS_TRENDS_OVERTIME_RADIO.SALES,
    },
    {
        label: "Ad Spends",
        value: CATEGORY_OVERVIEW_MS_TRENDS_OVERTIME_RADIO.AD_SPENDS,
    },
    {
        label: "Total Sales",
        value: CATEGORY_OVERVIEW_MS_TRENDS_OVERTIME_RADIO.TOTAL_SALES,
    },
];

export const KPIOptions = [
    { label: "MS%", value: AggregationField.MS_PERCENT },
    { label: "ROAS", value: AggregationField.ROAS },
    { label: "Sales", value: AggregationField.SALE },
    { label: "AD Spends", value: AggregationField.SPEND },
    { label: "Total Sales", value: AggregationField.TOTAL_SALES },
];

export const kpiOptionsAvailabilityEcomm = [
    { label: "Availability%", value: AggregationField.AVAILABILITY },
    { label: "Buybox%", value: AggregationField.BUYBOX },
    { label: "MS%", value: AggregationField.MS_PERCENT },
    { label: "ROAS", value: AggregationField.ROAS },
    { label: "Sales", value: AggregationField.SALE },
    { label: "AD Spends", value: AggregationField.SPEND },
    { label: "Total Sales", value: AggregationField.TOTAL_SALES },
];

export const CATEGORY_OVERVIEW_CROSS_CHANNEL_COMPETITION_WIDGET_METRICS = [
    {
        label: "MS%",
        currentKey: "cur_ms_revenue",
        previousKey: "prev_ms_revenue",
    },
    {
        label: "Total Sales",
        currentKey: "cur_total_sales",
        previousKey: "prev_total_sales",
    },
    {
        label: "Sales",
        currentKey: "cur_sale",
        previousKey: "prev_sale",
    },
    {
        label: "Ad Spends",
        currentKey: "cur_spend",
        previousKey: "prev_spend",
    },
    {
        label: "Roas",
        currentKey: "cur_roas",
        previousKey: "prev_roas",
    },
];

export const CATEGORY_AVAILABILITY_ECOMM_COMPETITION_WIDGET_METRICS = [
    {
        label: "MS%",
        currentKey: "curr_ms_percent",
        previousKey: "prev_ms_percent",
    },
    {
        label: "Availability%",
        currentKey: "curr_availability",
        previousKey: "prev_availability",
    },
    {
        label: "Buybox%",
        currentKey: "curr_buybox",
        previousKey: "prev_buybox",
    },
];

export const CATEGORY_AVAILABILITY_ECOMM_COMPARISON_WIDGET_METRICS = [
    {
        label: "Availability%",
        currentKey: "curr_availability",
        previousKey: "prev_availability",
    },
    {
        label: "Buybox%",
        currentKey: "curr_buybox",
        previousKey: "prev_buybox",
    },
    {
        label: "Total Sales",
        currentKey: "curr_sale",
        previousKey: "prev_sale",
    },
    {
        label: "ROAS",
        currentKey: "curr_roas",
        previousKey: "prev_roas",
    },
];

export const KPIS_TRENDS_AXIS_TITLE_MAP: Record<string, string> = {
    [MyProductTrendsTypes.MS_PERCENT]: "MS%",
    [MyProductTrendsTypes.ROAS]: "ROAS",
    [MyProductTrendsTypes.SALE]: "Sale",
    [MyProductTrendsTypes.AD_SPEND]: "Ad Spend",
    [MyProductTrendsTypes.TOTAL_SALES]: "Total Sales",
};

export const CATEGORY_OVERVIEW_COMPETITION_WIDGET_METRICS = [
    {
        label: "MS%",
        currentKey: "cur_ms_revenue",
        previousKey: "prev_ms_revenue",
    },
    {
        label: "Total Sales",
        currentKey: "cur_sale",
        previousKey: "prev_sale",
    },
    {
        label: "Sales",
        currentKey: "cur_sale",
        previousKey: "prev_sale",
    },
    {
        label: "Ad Spends",
        currentKey: "cur_spend",
        previousKey: "cur_spend",
    },
    {
        label: "Roas",
        currentKey: "cur_roas",
        previousKey: "prev_roas",
    },
];

export const CATEGORY_OVERVIEW_CHART_FILTERS_INTIAL_STATE = {
    [PricingCharts.PRICE_DEALS_COMPETITION]: [] as string[],
    [PricingCharts.PRICE_DEALS_CHANNEL]: Channels.AMAZON,
    [PricingCharts.KPI_SLECTION]: AggregationField.MS_PERCENT,
    [AvailabilityCharts.BB_WIN_IMPACT]: "price",
};

export enum CategoryInsightsOverviewSections {
    FILTERS = "filters",
    CHARTS = "charts",
    SERP = "serp",
}

export const categoryAvailabilityRawData = [
    {
        sku: "345992",
        store_id: "29581",
        crawl_date: "2025-03-31",
        curr_availability: null,
        curr_inventory: null,
        curr_price: 209,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 209,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "new delhi",
        pincode: 110095,
        region: "Delhi-NCR",
    },
    {
        sku: "589096",
        store_id: "29581",
        crawl_date: "2025-03-31",
        curr_availability: null,
        curr_inventory: null,
        curr_price: 486,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 486,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "new delhi",
        pincode: 110095,
        region: "Delhi-NCR",
    },
    {
        sku: "589092",
        store_id: "29581",
        crawl_date: "2025-04-02",
        curr_availability: true,
        curr_inventory: 1,
        curr_price: 383,
        curr_upd: 1,
        curr_drr_upd: 1,
        curr_drr_revenue: 383,
        curr_drr_price: 383,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "new delhi",
        pincode: 110095,
        region: "Delhi-NCR",
    },
    {
        sku: "101247",
        store_id: "29581",
        crawl_date: "2025-04-02",
        curr_availability: true,
        curr_inventory: 3,
        curr_price: 442,
        curr_upd: 1,
        curr_drr_upd: 1,
        curr_drr_revenue: 437,
        curr_drr_price: 437,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "new delhi",
        pincode: 110095,
        region: "Delhi-NCR",
    },
    {
        sku: "885",
        store_id: "29581",
        crawl_date: "2025-03-31",
        curr_availability: null,
        curr_inventory: null,
        curr_price: 228,
        curr_upd: 3,
        curr_drr_upd: 3,
        curr_drr_revenue: 684,
        curr_drr_price: 228,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "new delhi",
        pincode: 110095,
        region: "Delhi-NCR",
    },
    {
        sku: "345992",
        store_id: "29581",
        crawl_date: "2025-04-02",
        curr_availability: false,
        curr_inventory: 0,
        curr_price: 209,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 209,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "new delhi",
        pincode: 110095,
        region: "Delhi-NCR",
    },
    {
        sku: "589092",
        store_id: "29581",
        crawl_date: "2025-03-31",
        curr_availability: null,
        curr_inventory: null,
        curr_price: 383,
        curr_upd: 1,
        curr_drr_upd: 1,
        curr_drr_revenue: 383,
        curr_drr_price: 383,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "new delhi",
        pincode: 110095,
        region: "Delhi-NCR",
    },
    {
        sku: "885",
        store_id: "29581",
        crawl_date: "2025-03-31",
        curr_availability: null,
        curr_inventory: null,
        curr_price: 228,
        curr_upd: 2,
        curr_drr_upd: 2,
        curr_drr_revenue: 456,
        curr_drr_price: 228,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "new delhi",
        pincode: 110095,
        region: "Delhi-NCR",
    },
    {
        sku: "101247",
        store_id: "29581",
        crawl_date: "2025-04-02",
        curr_availability: true,
        curr_inventory: 3,
        curr_price: 442,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 442,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "new delhi",
        pincode: 110095,
        region: "Delhi-NCR",
    },
    {
        sku: "101247",
        store_id: "29581",
        crawl_date: "2025-03-31",
        curr_availability: null,
        curr_inventory: null,
        curr_price: 437,
        curr_upd: 1,
        curr_drr_upd: 1,
        curr_drr_revenue: 437,
        curr_drr_price: 437,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "new delhi",
        pincode: 110095,
        region: "Delhi-NCR",
    },
    {
        sku: "589096",
        store_id: "29581",
        crawl_date: "2025-04-02",
        curr_availability: true,
        curr_inventory: 1,
        curr_price: 486,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 486,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "new delhi",
        pincode: 110095,
        region: "Delhi-NCR",
    },
    {
        sku: "349842",
        store_id: "29581",
        crawl_date: "2025-03-31",
        curr_availability: null,
        curr_inventory: null,
        curr_price: 228,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 228,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "new delhi",
        pincode: 110095,
        region: "Delhi-NCR",
    },
    {
        sku: "885",
        store_id: "29581",
        crawl_date: "2025-04-02",
        curr_availability: true,
        curr_inventory: 5,
        curr_price: 228,
        curr_upd: 3,
        curr_drr_upd: 3,
        curr_drr_revenue: 684,
        curr_drr_price: 228,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "new delhi",
        pincode: 110095,
        region: "Delhi-NCR",
    },
    {
        sku: "110137",
        store_id: "29581",
        crawl_date: "2025-04-02",
        curr_availability: true,
        curr_inventory: 1,
        curr_price: 440,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 440,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "new delhi",
        pincode: 110095,
        region: "Delhi-NCR",
    },
    {
        sku: "110137",
        store_id: "29581",
        crawl_date: "2025-04-02",
        curr_availability: true,
        curr_inventory: 1,
        curr_price: 440,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 440,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "new delhi",
        pincode: 110095,
        region: "Delhi-NCR",
    },
    {
        sku: "349842",
        store_id: "29581",
        crawl_date: "2025-03-31",
        curr_availability: null,
        curr_inventory: null,
        curr_price: 228,
        curr_upd: 1,
        curr_drr_upd: 1,
        curr_drr_revenue: 228,
        curr_drr_price: 228,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "new delhi",
        pincode: 110095,
        region: "Delhi-NCR",
    },
    {
        sku: "349842",
        store_id: "29581",
        crawl_date: "2025-04-02",
        curr_availability: true,
        curr_inventory: 2,
        curr_price: 228,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 228,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "new delhi",
        pincode: 110095,
        region: "Delhi-NCR",
    },
    {
        sku: "349842",
        store_id: "29581",
        crawl_date: "2025-04-02",
        curr_availability: true,
        curr_inventory: 2,
        curr_price: 228,
        curr_upd: 1,
        curr_drr_upd: 1,
        curr_drr_revenue: 228,
        curr_drr_price: 228,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "new delhi",
        pincode: 110095,
        region: "Delhi-NCR",
    },
    {
        sku: "101247",
        store_id: "29581",
        crawl_date: "2025-03-31",
        curr_availability: null,
        curr_inventory: null,
        curr_price: 442,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 442,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "new delhi",
        pincode: 110095,
        region: "Delhi-NCR",
    },
    {
        sku: "345992",
        store_id: "29581",
        crawl_date: "2025-04-02",
        curr_availability: false,
        curr_inventory: 0,
        curr_price: 209,
        curr_upd: 1,
        curr_drr_upd: 1,
        curr_drr_revenue: 209,
        curr_drr_price: 209,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "new delhi",
        pincode: 110095,
        region: "Delhi-NCR",
    },
    {
        sku: "885",
        store_id: "29581",
        crawl_date: "2025-04-02",
        curr_availability: true,
        curr_inventory: 5,
        curr_price: 228,
        curr_upd: 2,
        curr_drr_upd: 2,
        curr_drr_revenue: 456,
        curr_drr_price: 228,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "new delhi",
        pincode: 110095,
        region: "Delhi-NCR",
    },
    {
        sku: "110137",
        store_id: "29581",
        crawl_date: "2025-03-31",
        curr_availability: null,
        curr_inventory: null,
        curr_price: 440,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 440,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "new delhi",
        pincode: 110095,
        region: "Delhi-NCR",
    },
    {
        sku: "110137",
        store_id: "29581",
        crawl_date: "2025-03-31",
        curr_availability: null,
        curr_inventory: null,
        curr_price: 440,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 440,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "new delhi",
        pincode: 110095,
        region: "Delhi-NCR",
    },
    {
        sku: "345992",
        store_id: "29581",
        crawl_date: "2025-03-31",
        curr_availability: null,
        curr_inventory: null,
        curr_price: 209,
        curr_upd: 1,
        curr_drr_upd: 1,
        curr_drr_revenue: 209,
        curr_drr_price: 209,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "new delhi",
        pincode: 110095,
        region: "Delhi-NCR",
    },
    {
        sku: "364353",
        store_id: "29650",
        crawl_date: "2025-03-31",
        curr_availability: null,
        curr_inventory: null,
        curr_price: 510,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 510,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "gurugram",
        pincode: 122017,
        region: "Delhi-NCR",
    },
    {
        sku: "589092",
        store_id: "29650",
        crawl_date: "2025-04-02",
        curr_availability: true,
        curr_inventory: 1,
        curr_price: 383,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 383,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "gurugram",
        pincode: 122017,
        region: "Delhi-NCR",
    },
    {
        sku: "589092",
        store_id: "29650",
        crawl_date: "2025-04-02",
        curr_availability: true,
        curr_inventory: 1,
        curr_price: 383,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 383,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "gurugram",
        pincode: 122017,
        region: "Delhi-NCR",
    },
    {
        sku: "589092",
        store_id: "29650",
        crawl_date: "2025-03-31",
        curr_availability: null,
        curr_inventory: null,
        curr_price: 383,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 383,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "gurugram",
        pincode: 122017,
        region: "Delhi-NCR",
    },
    {
        sku: "589092",
        store_id: "29650",
        crawl_date: "2025-03-31",
        curr_availability: null,
        curr_inventory: null,
        curr_price: 383,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 383,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "gurugram",
        pincode: 122017,
        region: "Delhi-NCR",
    },
    {
        sku: "345992",
        store_id: "29650",
        crawl_date: "2025-03-31",
        curr_availability: null,
        curr_inventory: null,
        curr_price: 228,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 228,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "gurugram",
        pincode: 122017,
        region: "Delhi-NCR",
    },
    {
        sku: "345992",
        store_id: "29650",
        crawl_date: "2025-04-02",
        curr_availability: true,
        curr_inventory: 2,
        curr_price: 228,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 228,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "gurugram",
        pincode: 122017,
        region: "Delhi-NCR",
    },
    {
        sku: "345992",
        store_id: "29650",
        crawl_date: "2025-04-02",
        curr_availability: true,
        curr_inventory: 2,
        curr_price: 228,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 228,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "gurugram",
        pincode: 122017,
        region: "Delhi-NCR",
    },
    {
        sku: "597777",
        store_id: "29650",
        crawl_date: "2025-03-31",
        curr_availability: null,
        curr_inventory: null,
        curr_price: 627,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 627,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "gurugram",
        pincode: 122017,
        region: "Delhi-NCR",
    },
    {
        sku: "597777",
        store_id: "29650",
        crawl_date: "2025-03-31",
        curr_availability: null,
        curr_inventory: null,
        curr_price: 627,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 627,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "gurugram",
        pincode: 122017,
        region: "Delhi-NCR",
    },
    {
        sku: "364353",
        store_id: "29650",
        crawl_date: "2025-04-02",
        curr_availability: false,
        curr_inventory: 0,
        curr_price: 510,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 510,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "gurugram",
        pincode: 122017,
        region: "Delhi-NCR",
    },
    {
        sku: "364353",
        store_id: "29650",
        crawl_date: "2025-04-02",
        curr_availability: false,
        curr_inventory: 0,
        curr_price: 510,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 510,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "gurugram",
        pincode: 122017,
        region: "Delhi-NCR",
    },
    {
        sku: "597777",
        store_id: "29650",
        crawl_date: "2025-04-02",
        curr_availability: true,
        curr_inventory: 1,
        curr_price: 627,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 627,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "gurugram",
        pincode: 122017,
        region: "Delhi-NCR",
    },
    {
        sku: "597777",
        store_id: "29650",
        crawl_date: "2025-04-02",
        curr_availability: true,
        curr_inventory: 1,
        curr_price: 627,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 627,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "gurugram",
        pincode: 122017,
        region: "Delhi-NCR",
    },
    {
        sku: "364353",
        store_id: "29650",
        crawl_date: "2025-03-31",
        curr_availability: null,
        curr_inventory: null,
        curr_price: 510,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 510,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "gurugram",
        pincode: 122017,
        region: "Delhi-NCR",
    },
    {
        sku: "101247",
        store_id: "29650",
        crawl_date: "2025-04-02",
        curr_availability: true,
        curr_inventory: 3,
        curr_price: 476,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 476,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "gurugram",
        pincode: 122017,
        region: "Delhi-NCR",
    },
    {
        sku: "101247",
        store_id: "29650",
        crawl_date: "2025-03-31",
        curr_availability: null,
        curr_inventory: null,
        curr_price: 476,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 476,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "gurugram",
        pincode: 122017,
        region: "Delhi-NCR",
    },
    {
        sku: "101247",
        store_id: "29650",
        crawl_date: "2025-03-31",
        curr_availability: null,
        curr_inventory: null,
        curr_price: 476,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 476,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "gurugram",
        pincode: 122017,
        region: "Delhi-NCR",
    },
    {
        sku: "101247",
        store_id: "29650",
        crawl_date: "2025-04-02",
        curr_availability: true,
        curr_inventory: 3,
        curr_price: 476,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 476,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "gurugram",
        pincode: 122017,
        region: "Delhi-NCR",
    },
    {
        sku: "349842",
        store_id: "29650",
        crawl_date: "2025-04-02",
        curr_availability: true,
        curr_inventory: 5,
        curr_price: 228,
        curr_upd: 1,
        curr_drr_upd: 1,
        curr_drr_revenue: 228,
        curr_drr_price: 228,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "gurugram",
        pincode: 122017,
        region: "Delhi-NCR",
    },
    {
        sku: "885",
        store_id: "29650",
        crawl_date: "2025-04-02",
        curr_availability: true,
        curr_inventory: 11,
        curr_price: 228,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 228,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "gurugram",
        pincode: 122017,
        region: "Delhi-NCR",
    },
    {
        sku: "885",
        store_id: "29650",
        crawl_date: "2025-04-02",
        curr_availability: true,
        curr_inventory: 11,
        curr_price: 228,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 228,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "gurugram",
        pincode: 122017,
        region: "Delhi-NCR",
    },
    {
        sku: "885",
        store_id: "29650",
        crawl_date: "2025-03-31",
        curr_availability: null,
        curr_inventory: null,
        curr_price: 228,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 228,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "gurugram",
        pincode: 122017,
        region: "Delhi-NCR",
    },
    {
        sku: "885",
        store_id: "29650",
        crawl_date: "2025-03-31",
        curr_availability: null,
        curr_inventory: null,
        curr_price: 228,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 228,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "gurugram",
        pincode: 122017,
        region: "Delhi-NCR",
    },
    {
        sku: "589096",
        store_id: "29650",
        crawl_date: "2025-04-02",
        curr_availability: true,
        curr_inventory: 1,
        curr_price: 486,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 486,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "gurugram",
        pincode: 122017,
        region: "Delhi-NCR",
    },
    {
        sku: "589096",
        store_id: "29650",
        crawl_date: "2025-04-02",
        curr_availability: true,
        curr_inventory: 1,
        curr_price: 486,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 486,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "gurugram",
        pincode: 122017,
        region: "Delhi-NCR",
    },
    {
        sku: "589096",
        store_id: "29650",
        crawl_date: "2025-03-31",
        curr_availability: null,
        curr_inventory: null,
        curr_price: 486,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 486,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "gurugram",
        pincode: 122017,
        region: "Delhi-NCR",
    },
    {
        sku: "589096",
        store_id: "29650",
        crawl_date: "2025-03-31",
        curr_availability: null,
        curr_inventory: null,
        curr_price: 486,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 486,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "gurugram",
        pincode: 122017,
        region: "Delhi-NCR",
    },
    {
        sku: "110137",
        store_id: "29650",
        crawl_date: "2025-03-31",
        curr_availability: null,
        curr_inventory: null,
        curr_price: 480,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 480,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "gurugram",
        pincode: 122017,
        region: "Delhi-NCR",
    },
    {
        sku: "110137",
        store_id: "29650",
        crawl_date: "2025-03-31",
        curr_availability: null,
        curr_inventory: null,
        curr_price: 480,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 480,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "gurugram",
        pincode: 122017,
        region: "Delhi-NCR",
    },
    {
        sku: "110137",
        store_id: "29650",
        crawl_date: "2025-04-02",
        curr_availability: true,
        curr_inventory: 2,
        curr_price: 480,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 480,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "gurugram",
        pincode: 122017,
        region: "Delhi-NCR",
    },
    {
        sku: "110137",
        store_id: "29650",
        crawl_date: "2025-04-02",
        curr_availability: true,
        curr_inventory: 2,
        curr_price: 480,
        curr_upd: 0,
        curr_drr_upd: 0,
        curr_drr_revenue: 0,
        curr_drr_price: 480,
        brand: "tresemme",
        category: "hair care",
        sub_category: "shampoos",
        city: "gurugram",
        pincode: 122017,
        region: "Delhi-NCR",
    },
];
export const keysToMergeVisibilityModuleSectionData = [
    "KeywordVisibility",
    "mySovVsCompetitionData",
    "myProductTrendsCrossChannelExpandedTableData",
    "myProductTrendsCompetitionExpandedTableData",
    "keywordPerformanceGraphCrossChannel",
    "keyword_performance_graph_competition",
];

export enum productDeepDiveModuleSections {
    KEYWORD_VISIBILITY = "keyword_visibility",
}

export const keysToMergeCategoryOverviewModuleSectionData = [
    // Serp
    "summaryWidget",
    "subCategoryPerformance",
    "drainers",
    "comparisonTable",
    "comparisonGraph",
    "bestSellerSku",
];
export const MANAGE_REPORT_ACCESS_INITIAL_STATE: TManageReportAccessInitialState =
    {
        usersWithAccessLoading: false,
        removeUserAccessLoading: false,
        addOrChangeAccessLoading: false,
    };

export const REPORT_ACCESS_TYPE_DETAILS_NOTES = {
    [ReportsAccessTypes.GENERAL]: "Anyone can view this report",
    [ReportsAccessTypes.RESTRICTED]: "Control who can view this report",
};

export const TAG_MAPPING_STATS_DEFAULT_VALUE: ITagMappingStats = {
    top_line: 0,
    table_data_visibility: [],
    table_data_upd: [],
};

export enum FREQUENCY_SLICER_OPTIONS {
    DAILY_DATA = "dailyData",
    WEEKLY_DATA = "weeklyData",
    MONTHLY_DATA = "monthlyData",
}

export const valueDeltaDefault = { value: 0, delta: 0 };

export const CATEGORY_OVERVIEW_RADIO_FILTERS_INITIAL_STATE = {
    msTrendsOverTimeRadioFilter: CATEGORY_OVERVIEW_SKU_FILTERS.REVENUE,
    brandContributionRadioFilter: CATEGORY_OVERVIEW_SKU_FILTERS.REVENUE,
    subCategoriesContributionRadioFilter: CATEGORY_OVERVIEW_SKU_FILTERS.REVENUE,
    bestSellerSkuRadioFilter: CATEGORY_OVERVIEW_SKU_FILTERS.REVENUE,
    subCatPerformanceRadioFilter: CATEGORY_OVERVIEW_SKU_FILTERS.REVENUE,
    msOfftakeRadioFilter: CATEGORY_OVERVIEW_MS_OFFTAKE_RADIO.MS_PERCENT,
    revenueUnitsRadioFilter: CATEGORY_OVERVIEW_SKU_FILTERS.REVENUE,
    msTrendsOvertimeMetricsRadioFilter:
        CATEGORY_OVERVIEW_MS_TRENDS_OVERTIME_RADIO.MS_PERCENT,
};

export const CATEGORY_OVERVIEW_SHOW_HIDE_STATES_INITIAL_STATE = {
    showDrainers: false,
    showDrainersDeltaValues: true,
    showSubCatPerformanceDeltaValues: true,
    showMyBrandTrends: false,
};

export const catOverviewSubCatPerformancefieldsToFormat: (keyof IFormattedSubCategoryPerformance)[] =
    [
        CommonWidgetMetrics.AD_SALES,
        CommonWidgetMetrics.AD_SPENDS,
        CommonWidgetMetrics.ROAS,
        CommonWidgetMetrics.ORGANIC_SOV,
        CommonWidgetMetrics.PAID_SOV,
        CommonWidgetMetrics.SOV,
        CommonWidgetMetrics.RPI,
        CommonWidgetMetrics.RATING,
        CommonWidgetMetrics.REVIEWS,
        CommonWidgetMetrics.AVAILABILITY,
        CommonWidgetMetrics.BUYBOX,
        CommonWidgetMetrics.STOCK,
    ];

export const categoryOverSummaryCardGroupedMetrics = [
    {
        title: "Traffic",
        keys: [
            {
                label: "Organic SOV",
                key: CommonWidgetMetrics.ORGANIC_SOV,
                isPercent: true,
            },
            {
                label: "Paid SOV",
                key: CommonWidgetMetrics.PAID_SOV,
                isPercent: true,
            },
            // { label: "Search Volume", key: CommonWidgetMetrics.SEARCH_VOLUME, isPercent: false }, // optional
        ],
    },
    {
        title: "Conversion",
        keys: [
            {
                label: "Pricing (RPI)",
                key: CommonWidgetMetrics.PRICING_RPI,
                isPercent: true,
            },
            {
                label: "Rating",
                key: CommonWidgetMetrics.RATING,
                isPercent: false,
            },
            {
                label: "Reviews",
                key: CommonWidgetMetrics.REVIEW_COUNT,
                isPercent: false,
            },
        ],
    },
    {
        title: "Operations",
        keys: [
            {
                label: "Availability%",
                key: CommonWidgetMetrics.AVAILABILITY,
                isPercent: true,
            },
            {
                label: "Buybox",
                key: CommonWidgetMetrics.BUYBOX,
                isPercent: false,
            },
        ],
    },
] satisfies {
    title: string;
    keys: {
        label: string;
        key: keyof IFormattedCategoryOverviewSummaryWidgetData;
        isPercent?: boolean;
    }[];
}[];

export const emptyChartData = {
    chartDataSeries: {
        categories: [] as string[],
        series: { name: "", data: [] as number[] },
    },
};

export const categoryOverSummaryCardMetrics: {
    title: string;
    key: keyof IFormattedCategoryOverviewSummaryWidgetData;
}[] = [
    {
        title: "Ad Spends",
        key: CommonWidgetMetrics.SPENDS,
    },
    {
        title: "ROAS",
        key: CommonWidgetMetrics.ROAS,
    },
    {
        title: "Ad Sales",
        key: CommonWidgetMetrics.SALES,
    },
];

export enum CategoryOverviewBrandContributionMetricKey {
    UNITS_PER_DAY = "unit_per_day",
    REVENUE = "revenue",
}

export enum CategoryOverviewSubCategoryMetricKey {
    UNITS_PER_DAY = "upd",
    REVENUE = "revenue",
}

export const categoryOverviewDefaultChartPoints: {
    revenue: [number, number];
    units: [number, number];
} = {
    revenue: [0, 0],
    units: [0, 0],
};

export enum CategoryOverviewChartsKeys {
    BRAND_CONTRIBUTION = "brandContribution",
    MS_TRENDS_OVER_TIME = "msTrendsOverTime",
    SUB_CATEGORIES_CONTRIBUTION = "subCategoriesContribution",
    MARKET_SHARE_AND_SIZE = "marketShareAndSize",
    CROSS_CHANNEL_GRAPH = "crossChannelGraph",
    CROSS_CHANNEL_TABLE = "crossChannelTable",
    COMPETITION_GRAPH = "competitionGraph",
    COMPETITION_TABLE = "competitionTable",
}

export enum CategoryOverviewSerpKeys {
    SUMMARY_WIDGET = "summaryWidget",
    SUB_CATEGORY_PERFORMANCE = "subCategoryPerformance",
    DRAINERS = "drainers",
    COMPARISON_TABLE = "comparisonTable",
    COMPARISON_GRAPH = "comparisonGraph",
    BEST_SELLER_SKU = "bestSellerSku",
}

export const catOverviewCompareTablesMetricsToSum = [
    CommonWidgetMetrics.CUR_MS_UPD,
    CommonWidgetMetrics.CUR_MS_REVENUE,
    CommonWidgetMetrics.PREV_MS_UPD,
    CommonWidgetMetrics.PREV_MS_REVENUE,
    CommonWidgetMetrics.CUR_SALE,
    CommonWidgetMetrics.CUR_SPEND,
    CommonWidgetMetrics.CUR_ROAS,
    CommonWidgetMetrics.CUR_TOTAL_SALES,
    CommonWidgetMetrics.PREV_TOTAL_SALES,
    CommonWidgetMetrics.PREV_SALE,
    CommonWidgetMetrics.PREV_SPEND,
    CommonWidgetMetrics.PREV_ROAS,
] as const;

export const catOverviewComparisonTableMetricsToSum = [
    CommonWidgetMetrics.CUR_UPD_MS,
    CommonWidgetMetrics.CUR_MS_REVENUE,
    CommonWidgetMetrics.PREV_UPD_MS,
    CommonWidgetMetrics.PREV_MS_REVENUE,
    CommonWidgetMetrics.CUR_SALE,
    CommonWidgetMetrics.CUR_SPEND,
    CommonWidgetMetrics.CUR_ROAS,
    CommonWidgetMetrics.CUR_TOTAL_SALES,
    CommonWidgetMetrics.PREV_TOTAL_SALES,
    CommonWidgetMetrics.PREV_SALE,
    CommonWidgetMetrics.PREV_SPEND,
    CommonWidgetMetrics.PREV_ROAS,
] as const;

export const oosProductsColDefs = [
    {
        headerName: "Product Title",
        field: "productTitle",
        pinned: "left",
        cellStyle: { fontWeight: "bold" },
    },
    { headerName: "Sub Category", field: "subCategory" },
    {
        headerName: "Availability%",
        field: "availability",
        cellStyle: { textAlign: "right" },
    },
    { headerName: "Listing Status", field: "listingStatus" },
    {
        headerName: "Estimated Sales Loss",
        field: "estimatedSalesLoss",
        cellStyle: { textAlign: "right" },
    },
    {
        headerName: "Unavailable on Dark Stores",
        field: "unavailableOnDarkStores",
        cellStyle: { textAlign: "right" },
    },
];

export const topProductsWithEstimatedLossColDefs = [
    {
        headerName: "Title",
        field: "productTitle",
        pinned: "left",
        cellStyle: { fontWeight: "bold" },
    },
    { headerName: "Sub Category", field: "subCategory" },
    {
        headerName: "Availability%",
        field: "availability",
        cellStyle: { textAlign: "right" },
    },
];

export const oosDarkStoresRegionColDefs = [
    {
        headerName: "Dark Store",
        field: "darkStore",
        pinned: "left",
        cellStyle: { fontWeight: "bold" },
    },
    { headerName: "Region", field: "region" },
    {
        headerName: "# Products",
        field: "totalProducts",
        cellStyle: { textAlign: "right" },
    },
    {
        headerName: "# Products < 30% Availability",
        field: "lowAvailabilityProducts",
        cellStyle: { textAlign: "right" },
    },
    {
        headerName: "Estimated Sales Loss",
        field: "estimatedSalesLoss",
        cellStyle: { textAlign: "right" },
    },
];

export const topSubcatEstimatedLossColDefs = [
    {
        headerName: "Sub Category",
        field: "subCategory",
        pinned: "left",
        cellStyle: { fontWeight: "bold" },
    },
    {
        headerName: "Availability (%)",
        field: "availability",
        cellStyle: { textAlign: "right" },
    },
    { headerName: "Listing Status", field: "listingStatus" },
    {
        headerName: "Estimated Sales Loss",
        field: "estimatedSalesLoss",
        cellStyle: { textAlign: "right" },
    },
    {
        headerName: "#Total Products",
        field: "totalProducts",
        cellStyle: { textAlign: "right" },
    },
    {
        headerName: "OOS Products",
        field: "oosProducts",
        cellStyle: { textAlign: "right" },
    },
    {
        headerName: "Products with availability < 30%",
        field: "lowAvailabilityProducts",
        cellStyle: { textAlign: "right" },
    },
];

export const topSubcatEstimatedLossEcommColDefs = [
    {
        headerName: "Sub Category",
        field: "subCategory",
        pinned: "left",
        cellStyle: { fontWeight: "bold" },
    },
    {
        headerName: "Availability (%)",
        field: "availability",
        cellStyle: { textAlign: "right" },
    },
    {
        headerName: "Estimated Sales Loss",
        field: "estimatedSalesLoss",
        cellStyle: { textAlign: "right" },
    },
    {
        headerName: "#Total Products",
        field: "totalProducts",
        cellStyle: { textAlign: "right" },
    },
    {
        headerName: "OOS Products",
        field: "oosProducts",
        cellStyle: { textAlign: "right" },
    },
];

export const topProductsWithLossEcommColDefs = [
    {
        headerName: "Title",
        field: "title",
    },
    {
        headerName: "Sub Category",
        field: "subCategory",
    },
    {
        headerName: "Latest SR",
        field: "latestSr",
    },
    {
        headerName: "Current Availability",
        field: "availabilityStatus",
        cellStyle: (params: any) => ({
            color: params.value === "Available" ? "green" : "red",
        }),
    },
    {
        headerName: "Estimated Sales Loss",
        field: "estimatedSalesLoss",
        valueFormatter: (params: any) =>
            params.value != null ? `${params.value.toFixed(2)}%` : "-",
    },
    {
        headerName: "Latest BBwin",
        field: "latestBBwin",
    },
];

export const productsPincodeLevelAvailabilityColDefs = [
    {
        headerName: "Product",
        field: "product",
        pinned: "left",
        cellStyle: { fontWeight: "bold" },
    },
    { headerName: "Sub Cat", field: "subCategory" },
    { headerName: "Region", field: "region" },
    { headerName: "ASP", field: "asp", cellStyle: { textAlign: "right" } },
    {
        headerName: "Availability%",
        field: "availability",
        cellStyle: { textAlign: "right" },
    },
    { headerName: "Listing Status", field: "listingStatus" },
    {
        headerName: "Avg. Stock in Hand",
        field: "avgStockInHand",
        cellStyle: { textAlign: "right" },
    },
    {
        headerName: "Unavailable on Dark Stores",
        field: "unavailableOnDarkStores",
        cellStyle: { textAlign: "right" },
    },
    {
        headerName: "Estimated Sales Loss",
        field: "estimatedSalesLoss",
        cellStyle: { textAlign: "right" },
    },
];

export const pincodeLevelInsightsColDefs = [
    {
        headerName: "Pincode",
        field: "pincode",
        pinned: "left",
        cellStyle: { fontWeight: "bold" },
    },
    { headerName: "Dark Store", field: "darkStore" },
    {
        headerName: "Availability%",
        field: "availability",
        cellStyle: { textAlign: "right" },
    },
    { headerName: "Listing Status", field: "listingStatus" },
    {
        headerName: "#Products",
        field: "totalProducts",
        cellStyle: { textAlign: "right" },
    },
    {
        headerName: "#Unavailable Products",
        field: "unavailableProducts",
        cellStyle: { textAlign: "right" },
    },
    {
        headerName: "Estimated Sales Loss",
        field: "estimatedSalesLoss",
        cellStyle: { textAlign: "right" },
    },
];

export const subcategoriesMaxSalesLossColDefs = [
    {
        headerName: "Sub Category",
        field: "subCategory",
        pinned: "left",
        cellStyle: { fontWeight: "bold" },
    },
    { headerName: "Pincode", field: "pincode" },
    { headerName: "ASP", field: "asp", cellStyle: { textAlign: "right" } },
    {
        headerName: "Latest Availability",
        field: "latestAvailability",
        cellStyle: { textAlign: "right" },
    },
    {
        headerName: "Availability%",
        field: "availabilityPercentage",
        cellStyle: { textAlign: "right" },
    },
    {
        headerName: "#Dark Stores",
        field: "darkStores",
        cellStyle: { textAlign: "right" },
    },
    { headerName: "Listing Status", field: "listingStatus" },
    {
        headerName: "#Products",
        field: "totalProducts",
        cellStyle: { textAlign: "right" },
    },
    {
        headerName: "Unavailable Products",
        field: "unavailableProducts",
        cellStyle: { textAlign: "right" },
    },
    {
        headerName: "Estimated Sales Loss",
        field: "estimatedSalesLoss",
        cellStyle: { textAlign: "right" },
    },
];

export const productsMaxSalesLossColDefs = [
    {
        headerName: "Product Title",
        field: "productTitle",
        pinned: "left",
        cellStyle: { fontWeight: "bold" },
    },
    { headerName: "Sub Category", field: "subCategory" },
    {
        headerName: "Availability (%)",
        field: "availability",
        cellStyle: { textAlign: "right" },
    },
    { headerName: "Listing Status", field: "listingStatus" },
    {
        headerName: "Estimated Sales Loss",
        field: "estimatedSalesLoss",
        cellStyle: { textAlign: "right" },
    },
    {
        headerName: "Unavailable on Dark Stores",
        field: "unavailableOnDarkStores",
        cellStyle: { textAlign: "right" },
    },
];

export const buyboxWinnersAndLosersAvailabilityColDefs = [
    {
        headerName: "Product",
        field: "product",
        cellStyle: { color: "#1a73e8", cursor: "pointer" },
    },
    {
        headerName: "Sub categories",
        field: "subCategory",
    },
    {
        headerName: "Seller",
        field: "seller",
        cellStyle: { color: "#1a73e8", cursor: "pointer" },
        // hide: true,
    },
    {
        headerName: "Seller Type",
        field: "sellerType",
        hide: true,
    },
    {
        headerName: "Seller Rating & Reviews",
        field: "sellerRating",
        hide: true,
    },
    {
        headerName: "Discounted Price",
        field: "discountedPrice",
    },
    {
        headerName: "M.R.P",
        field: "mrp",
        cellStyle: { textDecoration: "line-through", color: "#888" },
    },
];

export const channelTypeMapping = {
    ecomm: [
        Channels.FLIPKART,
        Channels.AMAZON,
        Channels.MYNTRA,
        Channels.PURPLLE,
        Channels.NOON,
        Channels.TAKEALOT,
        Channels.TALABAT,
        Channels.WALMART,
        Channels.HEPSIBURADA,
    ],
    qcomm: [
        Channels.BLINKIT,
        Channels.ZEPTO,
        Channels.INSTAMART,
        Channels.FLIPKART_GROCERY,
        Channels.NOON_MINUTES,
    ],
};

export const comparisonMetricsAvailability = [
    AggregationField.MS_PERCENT,
    AggregationField.ROAS,
    AggregationField.SALE,
    AggregationField.SPEND,
    AggregationField.TOTAL_SALES,
    AggregationField.AVAILABILITY,
    AggregationField.BUYBOX,
];

export const CATEGORY_OVERVIEW_DRAINERS_CURRENCY_METRICS = [
    CommonWidgetMetrics.AD_SALES,
    CommonWidgetMetrics.AD_SPENDS,
];

export const CATEGORY_OVERVIEW_DRAINERS_PERCENTAGE_METRICS = [
    CommonWidgetMetrics.MS,
    CommonWidgetMetrics.TOTAL_SALES,
    CommonWidgetMetrics.CTR,
    CommonWidgetMetrics.CTC,
];

export enum CERBOS_PERMISSIONS_COMP_TYPES {
    CELL_RENDERER = "cellRenderer",
}

export const placementMappings = [
    {
        key: "topOfSearch",
        placement: "placementTop" as PlacementKey,
    },
    {
        key: "productPage",
        placement: "placementProductPage" as PlacementKey,
    },
    {
        key: "restOfSearch",
        placement: "placementRest" as PlacementKey,
    },
];

export const placementConfigForUpdateGridData = [
    {
        key: "restOfSearch",
        placementKey: "placement_rest_placement",
    },
    {
        key: "productPage",
        placementKey: "placement_product_page_placement",
    },
    { key: "topOfSearch", placementKey: "placement_top_placement" },
];

export enum CAMPAIGN_STATES {
    ENABLED = "enabled",
    ARCHIVED = "archived",
}

export enum TaggingActionTypes {
    ADD = "add",
    UPDATE = "update",
    REMOVE = "remove",
    INSERT = "insert",
}
export const REVSTACK_CHART_COLORS = {
    primary: {
        700: "#0078AA",
        600: "#00A2E6",
        500: "#00B4FF",
        400: "#1ABCFF",
        300: "#4DCBFF",
        200: "#80DAFF",
    },
    secondary: {
        900: "#00374D",
        700: "#0078AA",
        600: "#00A2E6",
        500: "#00B4FF",
        400: "#1ABCFF",
        300: "#4DCBFF",
        200: "#80DAFF",
    },
    gold: {
        700: "#A7850B",
        600: "#D7AB0F",
        500: "#EFBE10",
        400: "#F0C428",
        300: "#F4D35E",
        200: "#F7DE88",
    },
    coral: {
        700: "#A32E10",
        600: "#D13B14",
        500: "#E94116",
        400: "#EB542E",
        300: "#EE6E4D",
        200: "#F4A08B",
    },
    lightGrayBlue: {
        700: "#39597A",
        600: "#49739C",
        500: "#5180AE",
        400: "#638CB6",
        300: "#85A6C6",
        200: "#A8BFD6",
    },
    deepNavy1: {
        700: "#3D4D76",
        600: "#4E6397",
        500: "#576EA8",
        400: "#687CB1",
        300: "#8999C2",
        200: "#ABB6D4",
    },
    mintGreen: {
        700: "#1C9689",
        600: "#24C1B1",
        500: "#28D7C4",
        400: "#3EDBCA",
        300: "#69E3D6",
        200: "#7FE7DC",
    },
    plumPurple: {
        700: "#604E65",
        600: "#735D78",
        500: "#8A6F90",
        400: "#957E9B",
        300: "#AD9AB1",
        200: "#C4B7C7",
    },
};

export const REVSTACK_THEME_COLORS = {
    primary: {
        ...REVSTACK_CHART_COLORS.primary,
        800: "#005A80",
        100: "#B3E9FF",
        50: "#E5F8FF",
    },
    secondary: {
        ...REVSTACK_CHART_COLORS.secondary,
        800: "#005A80",
        100: "#B3EAFF",
        50: "#E5F8FF",
    },
    gold: {
        ...REVSTACK_CHART_COLORS.gold,
        800: "#775F08",
        100: "#FAEBB7",
        50: "#FDF8E7",
    },
    coral: {
        ...REVSTACK_CHART_COLORS.coral,
        800: "#74210B",
        100: "#F8C6B9",
        50: "#FDECE8",
    },
    lightGrayBlue: {
        ...REVSTACK_CHART_COLORS.lightGrayBlue,
        800: "#294057",
        100: "#CBD9E7",
        50: "#F0F4F8",
    },
    deepNavy1: {
        ...REVSTACK_CHART_COLORS.deepNavy1,
        800: "#2E3A59",
        100: "#CDD3E5",
        50: "#EEF0F6",
    },
    mintGreen: {
        ...REVSTACK_CHART_COLORS.mintGreen,
        800: "#146B62",
        100: "#BFF3ED",
        50: "#EAFBF9",
    },
    plumPurple: {
        950: "#1C161D",
        900: "#29212B",
        800: "#453848",
        100: "#DCD4DE",
        50: "#F3F1F4",
        ...REVSTACK_CHART_COLORS.plumPurple,
    },
    warning: {
        500: "#F79009",
        100: "#FFFAEB",
    },
};

export const REVSTACK_TRENDS_CHART_COLORS = [
    REVSTACK_CHART_COLORS.primary[500],
    REVSTACK_CHART_COLORS.gold[500],
    REVSTACK_CHART_COLORS.coral[500],
    REVSTACK_CHART_COLORS.lightGrayBlue[500],
    REVSTACK_CHART_COLORS.deepNavy1[500],
    REVSTACK_CHART_COLORS.mintGreen[500],
    REVSTACK_CHART_COLORS.plumPurple[500],
    REVSTACK_CHART_COLORS.primary[600],
    REVSTACK_CHART_COLORS.secondary[600],
    REVSTACK_CHART_COLORS.gold[600],
    REVSTACK_CHART_COLORS.coral[600],
    REVSTACK_CHART_COLORS.lightGrayBlue[600],
    REVSTACK_CHART_COLORS.deepNavy1[600],
    REVSTACK_CHART_COLORS.mintGreen[600],
    REVSTACK_CHART_COLORS.plumPurple[600],
    REVSTACK_CHART_COLORS.primary[700],
    REVSTACK_CHART_COLORS.secondary[700],
    REVSTACK_CHART_COLORS.gold[700],
    REVSTACK_CHART_COLORS.coral[700],
    REVSTACK_CHART_COLORS.lightGrayBlue[700],
    REVSTACK_CHART_COLORS.deepNavy1[700],
    REVSTACK_CHART_COLORS.mintGreen[700],
    REVSTACK_CHART_COLORS.plumPurple[700],
];

export const REVSTACK_TRENDS_LIGHT_CHART_COLORS = [
    REVSTACK_CHART_COLORS.primary[700],
    REVSTACK_CHART_COLORS.gold[500],
    REVSTACK_CHART_COLORS.deepNavy1[500],
    REVSTACK_CHART_COLORS.secondary[500],
    REVSTACK_CHART_COLORS.coral[500],
    REVSTACK_CHART_COLORS.mintGreen[200],
    REVSTACK_CHART_COLORS.primary[300],
    REVSTACK_CHART_COLORS.gold[300],
    REVSTACK_CHART_COLORS.coral[300],
    REVSTACK_CHART_COLORS.lightGrayBlue[300],
    REVSTACK_CHART_COLORS.mintGreen[300],
    REVSTACK_CHART_COLORS.plumPurple[300],
];

export const CUST_DATE_PICKER_INPUT_VALIDATION_INITIAL_STATE: IDatePickerInputValidationInitialState =
    {
        primaryStart: { invalid: false, message: "" },
        primaryEnd: { invalid: false, message: "" },
        compareStart: { invalid: false, message: "" },
        compareEnd: { invalid: false, message: "" },
    };

export enum CUSTOM_DATE_PICKER_INPUT_FIELDS {
    PrimaryStart = "primaryStart",
    PrimaryEnd = "primaryEnd",
    CompareStart = "compareStart",
    CompareEnd = "compareEnd",
}

export const CustomDatePickerInputInitialState: TCustomDatePickerInputState = {
    [CUSTOM_DATE_PICKER_INPUT_FIELDS.PrimaryStart]: "",
    [CUSTOM_DATE_PICKER_INPUT_FIELDS.PrimaryEnd]: "",
    [CUSTOM_DATE_PICKER_INPUT_FIELDS.CompareStart]: "",
    [CUSTOM_DATE_PICKER_INPUT_FIELDS.CompareEnd]: "",
};

export enum DATE_PICKER_PRESET_PERIODS {
    YESTERDAY = "yesterday",
    LAST_7_DAYS = "l7d",
    MONTH_TO_DATE = "mtd",
    LAST_MONTH = "lm",
    LAST_3_MONTHS = "l3m",
    YEAR_TO_DATE = "ytd",
    LAST_12_MONTHS = "last12m",
    CUSTOM = "custom",
    LAST_30_DAYS = "l30d",
    LAST_60_DAYS = "l60d",
    LAST_90_DAYS = "l90d",
    LAST_180_DAYS = "l180d",
    LAST_365_DAYS = "l365d",
    PREVIOUS_PERIOD = "previous-period",
}

export enum DATE_PICKER_FOCUSED_INPUT {
    PRIMARY = "primary",
    COMPARE = "compare",
}

export const PIE_CHART_COLORS = [
    REVSTACK_THEME_COLORS.primary[700],
    REVSTACK_THEME_COLORS.secondary[900],
    REVSTACK_THEME_COLORS.gold[500],
    REVSTACK_THEME_COLORS.primary[500],
    REVSTACK_THEME_COLORS.mintGreen[100],
    REVSTACK_THEME_COLORS.lightGrayBlue[100],
];

export const CatAnalyticsSubCatDrillDownConfig: Partial<TSubCatDrillDownConfig> =
    {
        [CA_DD_MODULES.OVERVIEW]: {
            entity: "Overview",
            filters: {
                activeSubTab:
                    CatAnalyticsSubCatDrillDownContentViews.OVERVIEW_VIEW,
                search: "",
                hideCompetition: false,
                chartType:
                    CatAnalyticsSubCatDrillDownContentViews.OVERVIEW_VIEW,
                selectedYAxisMetrics: CommonWidgetMetrics.MARKET_SHARE,
                selectedYAxisOppositeMetrics:
                    CommonWidgetMetrics.OVERALL_IMPRESSIONS,
            },
        },
        [CA_DD_MODULES.BRAND_ANALYTICS]: {
            entity: "Brands",
            filters: {
                activeSubTab:
                    CatAnalyticsSubCatDrillDownContentViews.TABLE_VIEW,
                search: "",
                hideCompetition: false,
                chartType: CatAnalyticsSubCatDrillDownContentViews.TABLE_VIEW,
            },
        },
        [CA_DD_MODULES.SKU_ANALYTICS]: {
            entity: "Products",
            filters: {
                activeSubTab:
                    CatAnalyticsSubCatDrillDownContentViews.TABLE_VIEW,
                search: "",
                hideCompetition: false,
                chartType: CatAnalyticsSubCatDrillDownContentViews.TABLE_VIEW,
            },
        },
        [CA_DD_MODULES.CROSS_REGION]: {
            entity: "Regions",
            filters: {
                activeSubTab:
                    CatAnalyticsSubCatDrillDownContentViews.TABLE_VIEW,
                search: "",
                hideCompetition: false,
                chartType: CatAnalyticsSubCatDrillDownContentViews.TABLE_VIEW,
            },
        },
        [CA_DD_MODULES.CROSS_CHANNEL]: {
            entity: "Channels",
            filters: {
                activeSubTab:
                    CatAnalyticsSubCatDrillDownContentViews.TABLE_VIEW,
                search: "",
                hideCompetition: false,
                chartType: CatAnalyticsSubCatDrillDownContentViews.TABLE_VIEW,
                selectedChannels: [
                    "Instamart",
                    "Blinkit",
                    "Zepto",
                    "BigBasket",
                ],
            },
        },
        [CA_DD_MODULES.AI_INSIGHTS]: {
            entity: "ai Insights",
            filters: {},
        },
    };

export const COMPONENT_FILTER_QUERY_CONFIG: any = {
    subcategory: {
        sectionKey: RevstackCASectionIds.SP,
        sectionWidgetKey: "scp-table",
        filterKey: RevstackFilterKey.SUBCATEGORY,
    },
    regional: {
        sectionKey: RevstackCASectionIds.RWP,
        sectionWidgetKey: "rwp-table",
        filterKey: RevstackFilterKey.REGION,
    },
    ma_category: {
        sectionKey: RevstackCASectionIds.COMV,
        sectionWidgetKey: "cwp",
        filterKey: RevstackFilterKey.CATEGORY,
    },
};

export const WIDGET_QUERY_CONFIG = {
    [WidgetType.W0]: [
        {
            type: CubeQueryType.DIMENSION_PIVOT_CONSOLIDATION,
            isDimensioned: true,
        },
    ],
    [WidgetType.W1]: [
        { type: CubeQueryType.VALUE_DELTA },
        { type: CubeQueryType.SERIES },
        //!ToDo: Review all cases where W1 exists - this requires queryDimesnions to be present
        { type: CubeQueryType.DIMENSION_VALUE_DELTA, isDimensioned: true },
    ],
    [WidgetType.W2]: [
        { type: CubeQueryType.VALUE_DELTA },
        { type: CubeQueryType.DIMENSION_VALUE_DELTA, isDimensioned: true },
    ],
    [WidgetType.W3]: [
        { type: CubeQueryType.DIMENSION_TIME_SERIES, isDimensioned: true },
    ],
    [WidgetType.W4]: [
        { type: CubeQueryType.DIMENSION_BAR_CHART, isDimensioned: true },
    ],
    [WidgetType.W5]: [
        { type: CubeQueryType.DIMENSION_PIE_CHART, isDimensioned: true },
    ],
    [WidgetType.W6]: [
        { type: CubeQueryType.MULTI_MEASURE_VALUE_DELTA, isDimensioned: true },
    ],
    [WidgetType.W7]: [
        {
            type: CubeQueryType.DIMENSION_PIVOT_CONSOLIDATION,
            isDimensioned: true,
        },
    ],
    [WidgetType.W8]: [
        {
            type: CubeQueryType.DIMENSION_PIVOT_CONSOLIDATION,
            isDimensioned: true,
        },
    ],
    [WidgetType.W9]: [
        {
            type: CubeQueryType.DIMENSION_PIVOT_CONSOLIDATION,
            isDimensioned: true,
        },
        { type: CubeQueryType.VALUE_DELTA },
    ],
};

export const granularityOptions = [
    { label: "Daily", value: RevstackFrequencySlicerOptions.Daily },
    { label: "Weekly", value: RevstackFrequencySlicerOptions.Weekly },
    { label: "Monthly", value: RevstackFrequencySlicerOptions.Monthly },
];

export const REVSTACK_MEASURES: Record<string, Measure> = {
    [CommonWidgetMetrics.MARKET_SHARE]: {
        value: CommonWidgetMetrics.MARKET_SHARE,
        label: "Market Share",
        unit: "%",
        changeUnit: "%",
        isMeasureDependent: true,
        dependencies: ["metricType", "priceType"],
        group: DDColGroupKeys.OVERALL_PERFORMANCE,
        isActive: true,
        description:
            "Estimated sales contribution of the selected brand(s) within the selected categories and subcategories during the chosen time period.",
    },
    [CommonWidgetMetrics.CATEGORY_SHARE]: {
        value: CommonWidgetMetrics.CATEGORY_SHARE,
        label: "Category Share",
        unit: "%",
        changeUnit: "%",
        isMeasureDependent: false,
        group: DDColGroupKeys.OVERALL_PERFORMANCE,
        isActive: true,
        description:
            "The percentage share of total revenue that a specific subcategory contributes within the selected subcategories during the chosen time period.",
    },
    [CommonWidgetMetrics.KEYWORD_TYPE]: {
        value: CommonWidgetMetrics.KEYWORD_TYPE,
        label: "Keyword Type",
        unit: "",
        changeUnit: "",
        isMeasureDependent: false,
        isCategorical: true,
        group: DDColGroupKeys.TRAFFIC,
        isActive: true,
        description: "",
    },
    [CommonWidgetMetrics.SEARCH_INDEX]: {
        value: CommonWidgetMetrics.SEARCH_INDEX,
        label: "Indexed Search Volume",
        unit: "%",
        changeUnit: "",
        isMeasureDependent: false,
        group: DDColGroupKeys.TRAFFIC,
        isActive: true,
        description: "",
    },
    [CommonWidgetMetrics.PAID_IMPRESSION]: {
        value: CommonWidgetMetrics.PAID_IMPRESSION,
        label: "Paid Impressions",
        unit: "",
        changeUnit: "",
        isMeasureDependent: false,
        group: DDColGroupKeys.TRAFFIC,
        isActive: true,
        description: "",
    },
    [CommonWidgetMetrics.OVERALL_SOV]: {
        value: CommonWidgetMetrics.OVERALL_SOV,
        label: "Overall SOV",
        unit: "%",
        changeUnit: "%",
        isMeasureDependent: false,
        group: DDColGroupKeys.OVERALL_PERFORMANCE,
        isActive: true,
        description:
            "Combined share of total impressions from both sponsored and organic listings that the selected brand(s) received in top search results within the selected categories and subcategories during the chosen time period.",
    },
    [CommonWidgetMetrics.WEIGHTED_AVAILABILITY]: {
        value: CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
        label: "Wt. Availability",
        unit: "%",
        changeUnit: "%",
        isMeasureDependent: false,
        group: DDColGroupKeys.OVERALL_PERFORMANCE,
        isActive: true,
        description:
            "Average availability of the selected brand(s)' SKUs within the selected categories and subcategories, weighted by their recent Daily Run Rate (DRR).",
    },
    [CommonWidgetMetrics.ORGANIC_SOV]: {
        value: CommonWidgetMetrics.ORGANIC_SOV,
        label: "Organic SOV",
        unit: "%",
        changeUnit: "%",
        isMeasureDependent: false,
        group: DDColGroupKeys.TRAFFIC,
        isActive: true,
        description:
            "Share of total impressions from organic (non-sponsored) listings that the selected brand(s) received in top search results within the selected categories and subcategories during the chosen time period.",
    },
    [CommonWidgetMetrics.PAID_SOV]: {
        value: CommonWidgetMetrics.PAID_SOV,
        label: "Paid SOV",
        unit: "%",
        changeUnit: "%",
        isMeasureDependent: false,
        group: DDColGroupKeys.TRAFFIC,
        isActive: true,
        description:
            "Share of total impressions from sponsored listings that the selected brand(s) received in top search results within the selected categories and subcategories during the chosen time period.",
    },
    [CommonWidgetMetrics.KEYWORD_PAID_SOV]: {
        value: CommonWidgetMetrics.KEYWORD_PAID_SOV,
        label: "Paid SOV",
        unit: "%",
        changeUnit: "%",
        isMeasureDependent: false,
        group: DDColGroupKeys.TRAFFIC,
        isActive: true,
        description:
            "Share of total impressions from sponsored listings that the selected brand(s) received in top search results within the selected categories and subcategories during the chosen time period.",
    },
    [CommonWidgetMetrics.KEYWORD_OVERALL_SOV]: {
        value: CommonWidgetMetrics.KEYWORD_OVERALL_SOV,
        label: "Overall SOV",
        unit: "%",
        changeUnit: "%",
        isMeasureDependent: false,
        group: DDColGroupKeys.TRAFFIC,
        isActive: true,
        description:
            "Share of total impressions from sponsored listings that the selected brand(s) received in top search results within the selected categories and subcategories during the chosen time period.",
    },
    [CommonWidgetMetrics.KEYWORD_ORGANIC_SOV]: {
        value: CommonWidgetMetrics.KEYWORD_ORGANIC_SOV,
        label: "Organic SOV",
        unit: "%",
        changeUnit: "%",
        isMeasureDependent: false,
        group: DDColGroupKeys.TRAFFIC,
        isActive: true,
        description:
            "Share of total impressions from sponsored listings that the selected brand(s) received in top search results within the selected categories and subcategories during the chosen time period.",
    },
    [CommonWidgetMetrics.BRANDED_SEARCH]: {
        value: CommonWidgetMetrics.BRANDED_SEARCH,
        label: "Branded Search",
        unit: "%",
        changeUnit: "%",
        isMeasureDependent: false,
        group: DDColGroupKeys.TRAFFIC,
        isActive: true,
        description:
            "The percentage of total search volume that came from branded keywords for the selected brand(s) within the selected categories and subcategories during the chosen time period.",
    },
    [CommonWidgetMetrics.RELATIVE_PRICE_INDEX]: {
        value: CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
        label: "RPI",
        unit: "",
        changeUnit: "",
        isMeasureDependent: false,
        group: DDColGroupKeys.CONVERSION,
        isActive: true,
        description:
            "A score comparing the average selling price of the selected brand(s) to the market average within the selected categories and subcategories.",
        invertChangeSign: true,
        decimal: 2,
    },
    [CommonWidgetMetrics.AVERAGE_RATING]: {
        value: CommonWidgetMetrics.AVERAGE_RATING,
        label: "Rating",
        unit: "",
        changeUnit: "",
        isMeasureDependent: false,
        group: DDColGroupKeys.CONVERSION,
        isActive: true,
        description:
            "Average customer rating for the selected brand(s)' SKUs within the selected categories and subcategories during the chosen time period, weighted by the number of ratings received.",
    },
    [CommonWidgetMetrics.STOCK_IN_HAND]: {
        value: CommonWidgetMetrics.STOCK_IN_HAND,
        label: "Stock in Hand",
        unit: " Day(s)",
        changeUnit: "%",
        isMeasureDependent: false,
        group: DDColGroupKeys.OPERATIONS,
        isActive: true,
        description:
            "An estimate of how many days the current inventory of the selected brand(s)' SKUs within the selected categories and subcategories will last, based on recent sales rate (DRR) during the chosen time period.",
    },
    [CommonWidgetMetrics.FILL_RATE]: {
        value: CommonWidgetMetrics.FILL_RATE,
        label: "Fill Rate",
        unit: "%",
        changeUnit: "%",
        isMeasureDependent: false,
        group: DDColGroupKeys.OPERATIONS,
        isActive: true,
        description:
            "The percentage of purchase orders successfully fulfilled by the brand that were raised by the channel within the selected categories and subcategories during the chosen time period.",
    },
    [CommonWidgetMetrics.LISTING_STATUS]: {
        value: CommonWidgetMetrics.LISTING_STATUS,
        label: "Listing Status",
        unit: "%",
        changeUnit: "%",
        isMeasureDependent: false,
        group: DDColGroupKeys.OPERATIONS,
        isActive: true,
        description:
            "The percentage of stores where the selected brand(s)' SKUs are listed, relative to the total number of stores where the selected categories and subcategories are listed.",
    },
    [CommonWidgetMetrics.CONTENT_SCORE]: {
        value: CommonWidgetMetrics.CONTENT_SCORE,
        label: "Content Score",
        unit: "",
        changeUnit: "",
        isMeasureDependent: false,
        group: DDColGroupKeys.CONVERSION,
        isActive: true,
        description:
            "Sales-weighted average content quality score for the selected brand(s)' SKU listings within the selected categories and subcategories during the chosen time period, reflecting how well they are optimized to drive sales.",
    },
    product_market_share: {
        value: "product_market_share",
        label: "Product MS%",
        unit: "%",
        changeUnit: "%",
        isMeasureDependent: false,
        group: DDColGroupKeys.OVERALL_PERFORMANCE,
        isActive: true,
    },
    [CommonWidgetMetrics.AVERAGE_PRICE]: {
        value: CommonWidgetMetrics.AVERAGE_PRICE,
        label: "Avg. Price",
        unit: "",
        changeUnit: "",
        isMeasureDependent: false,
        group: DDColGroupKeys.CONVERSION,
        isActive: true,
        description:
            "Average selling price of the selected brand(s)' SKUs within the selected categories and subcategories during the chosen time period.",
    },
    [CommonWidgetMetrics.WEIGHTED_DISCOUNT]: {
        value: CommonWidgetMetrics.WEIGHTED_DISCOUNT,
        label: "Wt. Discount",
        unit: "%",
        changeUnit: "%",
        isMeasureDependent: false,
        group: DDColGroupKeys.CONVERSION,
        isActive: true,
        description:
            "Average discount percentage applied to the selected brand(s)' SKUs within the selected categories and subcategories during the chosen time period.",
    },
    [CommonWidgetMetrics.INDEXED_SEARCH_VOLUME]: {
        value: CommonWidgetMetrics.INDEXED_SEARCH_VOLUME,
        label: "Indexed Search Volume",
        unit: "%",
        decimal: 0,
        changeUnit: "",
        isMeasureDependent: false,
        group: DDColGroupKeys.TRAFFIC,
        isActive: true,
        description: "",
    },
};

export const REVSTACK_DRILL_DOWN_PERIOD_OPTIONS: PERIOD_OPTIONS[] = [
    {
        label: "30 Days",
        value: DATE_PICKER_PRESET_PERIODS.LAST_30_DAYS,
    },
    {
        label: "60 Days",
        value: DATE_PICKER_PRESET_PERIODS.LAST_60_DAYS,
    },
    {
        label: "90 Days",
        value: DATE_PICKER_PRESET_PERIODS.LAST_90_DAYS,
    },
    {
        label: "180 Days",
        value: DATE_PICKER_PRESET_PERIODS.LAST_180_DAYS,
    },
    {
        label: "365 Days",
        value: DATE_PICKER_PRESET_PERIODS.LAST_365_DAYS,
    },
];

export const CUBE_PERCENT_MEASURES = [
    CommonWidgetMetrics.MARKET_SHARE,
    CommonWidgetMetrics.OVERALL_SOV,
    CommonWidgetMetrics.ORGANIC_SOV,
    CommonWidgetMetrics.PAID_SOV,
    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
    CommonWidgetMetrics.LISTING_STATUS,
    CommonWidgetMetrics.INDEXED_SEARCH_VOLUME,
];

export const RevstackFiltersInitialSearchState: TRevstackFiltersSearchState = {
    categories: "",
    subcategories: "",
    brands: "",
};

export const REVSTACK_COUNTRY_DATE_FORMATS = {
    IN: "M/D/YY",
};

export const REVSTACK_W7_ALL_CATEGORIES_KEYWORDS = [
    "all subcategories",
    "pan india",
];

export const REVSTACK_W7_TABLE_NO_DATA_MESSAGES = {
    LEADERS: {
        ALL_CATEGORIES: "All subcategories dropping pace",
        SPECIFIC_CATEGORY: "All products dropping pace",
    },
    LAGGARDS: {
        ALL_CATEGORIES: "All subcategories keeping pace",
        SPECIFIC_CATEGORY: "All products keeping pace",
        NAVIGATOR_PO_CARDS_LAGGARDS: "Performance across channels keeping pace",
    },
};

export const REVSTACK_TOOLTIP_PREFIXES_TO_REMOVE = ["keyword_"] as const;

//!ToDo: How can we make this dynamic to cater to future channel requirements?
export const REVSTACK_CHANNEL_HEIRARCHY = [
    Channels.BLINKIT,
    Channels.ZEPTO,
    Channels.INSTAMART,
    Channels.AMAZON,
    Channels.HEPSIBURADA,
];

export const REVSTACK_CAPITALIZED_METRICS = ["sov"];

export enum UserRoles {
    ADMIN = "1ds_admin",
    USER = "1ds_user",
    CUSTOMER_ADMIN = "customer_admin",
}

export enum WidgetQueryType {
    EXPANDED = "expanded",
    CONSOLIDATED = "consolidated",
    EXP_CONSOLIDATED = "expanded_consolidated",
}

export enum DimenionType {
    DIMENSION = "dimensions",
    UNIVERSE_DIMENSION = "universeDimensions",
}
export const REVSTACK_MULTI_CHANNEL_MODULES = [SubModule.NAVIGATOR];

export const REVSTACK_PERIOD_TO_DAYS_MAP: Record<
    DATE_PICKER_PRESET_PERIODS,
    number
> = {
    [DATE_PICKER_PRESET_PERIODS.YESTERDAY]: 1,
    [DATE_PICKER_PRESET_PERIODS.LAST_7_DAYS]: 7,
    [DATE_PICKER_PRESET_PERIODS.LAST_30_DAYS]: 30,
    [DATE_PICKER_PRESET_PERIODS.LAST_60_DAYS]: 60,
    [DATE_PICKER_PRESET_PERIODS.LAST_90_DAYS]: 90,
    [DATE_PICKER_PRESET_PERIODS.LAST_180_DAYS]: 180,
    [DATE_PICKER_PRESET_PERIODS.MONTH_TO_DATE]: 30,
    [DATE_PICKER_PRESET_PERIODS.LAST_MONTH]: 30,
    [DATE_PICKER_PRESET_PERIODS.LAST_3_MONTHS]: 90,
    [DATE_PICKER_PRESET_PERIODS.YEAR_TO_DATE]: 365,
    [DATE_PICKER_PRESET_PERIODS.LAST_12_MONTHS]: 365,
    [DATE_PICKER_PRESET_PERIODS.CUSTOM]: 7,
    [DATE_PICKER_PRESET_PERIODS.PREVIOUS_PERIOD]: 7,
    [DATE_PICKER_PRESET_PERIODS.LAST_365_DAYS]: 365,
} as const;

export const REVSTACK_GRANULARITY_TO_PERIOD_MAP: Record<
    RevstackFrequencySlicerOptions,
    DATE_PICKER_PRESET_PERIODS
> = {
    [RevstackFrequencySlicerOptions.Daily]:
        DATE_PICKER_PRESET_PERIODS.LAST_30_DAYS,
    [RevstackFrequencySlicerOptions.Weekly]:
        DATE_PICKER_PRESET_PERIODS.LAST_60_DAYS,
    [RevstackFrequencySlicerOptions.Monthly]:
        DATE_PICKER_PRESET_PERIODS.LAST_90_DAYS,
} as const;

export const REVSTACK_GRANULARITY_DIVISORS: Record<
    RevstackFrequencySlicerOptions,
    number
> = {
    [RevstackFrequencySlicerOptions.Daily]: 1,
    [RevstackFrequencySlicerOptions.Weekly]: 7,
    [RevstackFrequencySlicerOptions.Monthly]: 30,
} as const;

export const DEFAULT_DASHBOARD_THEME: any = {
    token: {
        colorPrimary: "#0078aa",
        colorInfo: "#0078aa",
        colorTextBase: "#252b37",
        sizeStep: 4,
        sizeUnit: 4,
        borderRadius: 8,
        borderRadiusLG: 12,
        borderRadiusSM: 4,
        boxShadow: "\n     0 2px 4px 2px rgba(0, 0, 0, 0.03)",
        wireframe: true,
        colorSuccess: "#039855",
        colorWarning: "#fdb022",
        colorError: "#d92d20",
    },
    components: {
        Typography: {
            fontFamilyCode: "'Manrope',Helvetica,Arial,sans-serif",
        },
        Layout: {
            bodyBg: "rgb(241,245,250)",
            headerPadding: "0 48px",
            zeroTriggerHeight: 36,
            zeroTriggerWidth: 36,
            footerPadding: "24px 48px",
        },
        Dropdown: {
            paddingBlock: 4,
            boxShadowPopoverArrow: "2px 1px 4px rgba(0, 0, 0, 0.05)",
            boxShadowSecondary: "0 2px 4px 2px rgba(0, 0, 0, 0.06)",
        },
        Form: {
            controlHeight: 36,
        },
        Input: {
            activeBorderColor: "rgb(0,120,170)",
            hoverBorderColor: "rgb(29,142,184)",
            colorBorder: "rgb(203,217,231)",
            controlHeight: 36,
        },
        Mentions: {
            fontSizeIcon: 14,
            activeBorderColor: "rgb(0,120,170)",
            hoverBorderColor: "rgb(124,212,253)",
            controlHeight: 36,
        },
        Radio: {
            controlHeight: 36,
            controlHeightLG: 44,
            fontFamily: '"Manrope", Arial,sans-serif;',
            colorBorder: "rgb(0,120,170)",
            colorPrimaryHover: "rgb(0,88,133)",
            colorText: "rgb(0,120,170)",
        },
        Select: {
            colorWarning: "rgb(254,200,75)",
            optionHeight: 32,
            multipleItemHeightLG: 32,
            optionPadding: "5px 12px",
            controlHeight: 36,
            singleItemHeightLG: 44,
            controlHeightLG: 44,
            hoverBorderColor: "rgb(179,233,255)",
            activeBorderColor: "rgb(0,120,170)",
            optionSelectedBg: "rgb(240,244,248)",
            colorBorder: "rgb(203,217,231)",
        },
        Slider: {
            handleActiveColor: "rgb(0,120,170)",
            dotActiveBorderColor: "rgb(0,120,170)",
            trackBg: "rgb(0,120,170)",
            handleColor: "rgb(0,120,170)",
            trackHoverBg: "rgb(29,142,184)",
            colorPrimaryBorderHover: "rgb(29,142,184)",
            controlHeight: 36,
            controlHeightLG: 44,
            fontFamily: '"Manrope", Arial, sans-serif;',
        },
        Transfer: {
            colorBorder: "rgb(203,217,231)",
            controlItemBgActive: "rgb(241,245,250)",
            colorWarning: "rgb(254,200,75)",
            controlItemBgActiveHover: "rgb(240,252,255)",
            controlItemBgHover: "rgb(241,245,250)",
        },
        TreeSelect: {
            titleHeight: 28,
            indentSize: 24,
            controlItemBgHover: "rgb(241,245,250)",
            fontFamily: '"Manrope", Arial, sans-serif;',
        },
        Badge: {
            dotSize: 8,
        },
        Card: {
            colorBorderSecondary: "rgb(203,217,231)",
            bodyPaddingSM: 12,
            headerFontSize: 20,
            headerFontSizeSM: 14,
            borderRadiusLG: 16,
            fontFamily: '"Manrope", Arial, sans-serif;',
        },
        Collapse: {
            colorBorder: "#CBD9E7",
            headerBg: "#F0F4F8",
            colorText: "#2D2D2D",
            colorTextHeading: "#2D2D2D",
            borderRadiusLG: 16,
            padding: "0",
            headerPadding: "12px 16px",
            contentPadding: "20px 16px",
            expandIconColor: "#2D2D2D",
            contentBg: "#FFFFFF",
        },
        Descriptions: {
            labelBg: "rgb(241,245,250)",
            labelColor: "rgb(37,43,55)",
            fontWeightStrong: 700,
            borderRadiusLG: 16,
            colorSplit: "rgb(203,217,231)",
            titleColor: "rgb(37,43,55)",
            colorText: "rgb(37,43,55)",
            contentColor: "rgb(37,43,55)",
        },
        List: {
            colorText: "rgb(37,43,55)",
            controlHeight: 36,
            controlHeightLG: 44,
            borderRadiusLG: 16,
        },
        Button: {
            defaultActiveBorderColor: "rgb(0,120,170)",
            defaultActiveColor: "rgb(0,120,170)",
            defaultHoverBorderColor: "rgb(0,120,170)",
            defaultHoverColor: "rgb(0,120,170)",
            groupBorderColor: "rgb(0,120,170)",
            colorBorder: "rgb(203,217,231)",
            primaryShadow: "0 0px 0 rgba(5,145,255,0.1)",
            borderRadiusLG: 16,
            cyan1: "#0086C9",
        },
        Table: {
            borderColor: "rgb(203,217,231)",
            headerBg: "rgb(241,245,250)",
            headerColor: "rgb(164,181,200)",
            headerBorderRadius: 0,
            fontWeightStrong: 400,
            fontSize: 14,
            padding: 16,
            fontFamily: '"Manrope", Arial,sans-serif;',
            rowSelectedBg: "#F0F4F8",
            rowSelectedHoverBg: "#F0F4F8",
        },
        Tabs: {
            fontFamily: '"Manrope", Arial, sans-serif;',
            borderRadius: 16,
            horizontalItemGutter: 48,
            cardGutter: 4,
            margin: 24,
            lineWidthBold: 3,
            fontSize: 16,
            controlHeight: 36,
            horizontalMargin: "0 0 24px 0",
            cardPadding: "8px 16px",
            colorBorder: "rgb(203,217,231)",
            colorBorderSecondary: "rgb(203,217,231)",
        },
        Tag: {
            fontFamily: '"Manrope",Arial, sans-serif;',
        },
        Modal: {
            titleFontSize: 20,
            controlHeight: 36,
            margin: 12,
            padding: 12,
            borderRadiusLG: 16,
            borderRadiusSM: 8,
            colorSplit: "rgb(203,217,231)",
        },
        Cascader: {
            controlHeight: 36,
        },
    },
};

export const CAMPAIGN_CREATION_DEFAULT_OPTIONS = {
    category: {
        label: "All Categories",
        value: CommonWidgetMetrics.All,
    },
    subcategory: {
        label: "All Sub-categories",
        value: CommonWidgetMetrics.All,
    },
};

export const htmlLoader = ` <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; width: 218px; min-height: 150px; border-radius: 12px;">
                        <div style="width: 30px; height: 30px; border: 3px solid #2B2841; border-top-color: #B4B4B3; border-radius: 50%; animation: spin 0.8s linear infinite;"></div>
                    </div>
                    <style>
                        @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                        }
                    </style>`;

export const BCC_CAMPAIGN_TYPE_MAPPING_SHORT: Record<string, string> = {
    "Product Booster": "PB",
    "Recommendation Ads": "RA",
};

export enum REVSTACK_RELATIVE_FILTERS {
    SKUS = "skus",
    REGIONAL = "regional",
}

export const CAMPAIGN_SYNC_COLOR_MAP = {
    [CAMPAIGN_SYNC_STATE.SYNCED]: "#4CAF50",
    [CAMPAIGN_SYNC_STATE.PENDING]: "#FFC107",
    [CAMPAIGN_SYNC_STATE.FAILED]: "#F44336",
};

// BCC City Selection Constants
export const BCC_CUSTOM_ZONE_GROUPS = [
    { name: "North + Central", zones: ["North India", "Central India"] },
    { name: "South + West", zones: ["South India", "West India"] },
    { name: "East", zones: ["East India"] },
] as const;

export const BCC_LEVEL_DISPLAY_NAMES: Record<string, string> = {
    city: "Cities",
    region: "Regions",
    tier: "Tiers",
    custom: "Custom",
};

export const BCC_SELECTION_TAG_COLORS: Record<string, string> = {
    cohort: "#0078aa",
    default: "default",
};

export const BCC_DEFAULT_FALLBACK = "Other";

export const SELECTION_LEVELS = [
    BCCSelectionLevel.CITY,
    BCCSelectionLevel.REGION,
    BCCSelectionLevel.TIER,
    BCCSelectionLevel.CUSTOM,
] as const;

export const COHORT_LEVEL_OPTIONS = [
    { label: "Cities", value: BCCSelectionLevel.CITY },
    { label: "Regions", value: BCCSelectionLevel.REGION },
    { label: "Tiers", value: BCCSelectionLevel.TIER },
    { label: "Custom", value: BCCSelectionLevel.CUSTOM },
];

// Mode labels for toggle buttons
export const CITY_SELECTION_MODE_LABELS: Record<BCCSelectionMode, string> = {
    [BCCSelectionMode.INDIVIDUAL]: "Individual",
    [BCCSelectionMode.COHORT]: "Custom Cohort",
};

export const BCC_TIME_SLOTS = ["MORNING", "AFTERNOON", "EVENING", "NIGHT"];

export const BCC_KEYWORD_TYPE_ABBREVIATIONS: Record<string, string> = {
    [KeywordTypeValues.GENERIC]: "Gen",
    [KeywordTypeValues.BRANDED]: "Brand",
    [KeywordTypeValues.COMPETITION]: "Comp",
};

// @Vaheed @Asif For PEI SubModule has to be added.
export const ALLOWED_MODULES_FOR_HASH_FILTERS = [
    SubModule.NAVIGATOR,
    SubModule.CATEGORY_ANALYSIS,
    SubModule.PRODUCT_ANALYSIS,
];

export const STATUS_COLORS: Record<MappingSyncStatus, string> = {
    [MappingSyncStatus.Success]: "#2AC769",
    [MappingSyncStatus.Syncing]: "#3B82F6",
    [MappingSyncStatus.Error]: "#F04438",
};

export const STATUS_LABELS: Record<MappingSyncStatus, string> = {
    [MappingSyncStatus.Success]: "Success",
    [MappingSyncStatus.Syncing]: "In progress",
    [MappingSyncStatus.Error]: "Failed",
};
